<template>
	<div class="wr-page" :class="[{ 'wr-pdf': pdfMode, 'wr-anim': anim, 'wr-wk': webkitPrint }, 'wr-t-' + themeId]">
		<!-- 표지 도판용 듀오톤. 실제 매물 사진은 전선·주차 차량·제각각인 하늘이 섞여 있어
		     컬러로 나란히 놓으면 표지가 지저분해진다(지도도 마찬가지다). 밝기만 남기고
		     잉크 두 색 사이로 다시 칠해 도판처럼 묶는다. 속장은 컬러 그대로 간다. -->
		<svg class="wr-duo-def" aria-hidden="true" focusable="false">
			<filter id="wr-duotone" color-interpolation-filters="sRGB">
				<feColorMatrix type="matrix"
					values="0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0 0 0 1 0" />
				<feComponentTransfer>
					<feFuncR type="table" :tableValues="duoVals(theme.duo, 0)" />
					<feFuncG type="table" :tableValues="duoVals(theme.duo, 1)" />
					<feFuncB type="table" :tableValues="duoVals(theme.duo, 2)" />
				</feComponentTransfer>
			</filter>
			<!-- 항공사진용. 어두운 쪽을 덜 눌러 세피아로 남긴다 — 위 필터로 누르면 도시가
			     검은 덩어리가 되어 건물 윤곽이 사라진다(실측). 반대로 덜 누르면(예전 값
			     0.360~0.976) 27mm 짜리 작은 판에서 회색 안개가 되어 무엇이 찍혔는지
			     읽히지 않는다(실측 4종 비교, 2026-09-16). 지금 값은 크림 지면과 같은
			     계열을 지키면서 블록과 도로가 구분되는 지점이다. -->
			<filter id="wr-duotone-soft" color-interpolation-filters="sRGB">
				<feColorMatrix type="matrix"
					values="0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0 0 0 1 0" />
				<feComponentTransfer>
					<feFuncR type="table" :tableValues="duoVals(theme.duoSoft, 0)" />
					<feFuncG type="table" :tableValues="duoVals(theme.duoSoft, 1)" />
					<feFuncB type="table" :tableValues="duoVals(theme.duoSoft, 2)" />
				</feComponentTransfer>
			</filter>
		</svg>
		<div v-if="loading" class="wr-state">리포트를 불러오는 중입니다…</div>

		<!-- 개발중 게이트(2026-09-08). 오류가 아니라 안내다 — '리포트를 열 수 없습니다' 로
		     묶으면 고객이 링크가 잘못된 줄 안다. -->
		<div v-else-if="devBlocked" class="wr-state wr-state--dev">
			<div class="wr-state__title">개발중입니다!</div>
			<p>위클리 리포트는 현재 준비 중입니다.<br />준비가 끝나면 담당자가 다시 안내드립니다.</p>
		</div>

		<div v-else-if="errorMsg" class="wr-state wr-state--err">
			<div class="wr-state__title">리포트를 열 수 없습니다</div>
			<p>{{ errorMsg }}</p>
		</div>

		<template v-else>
			<!-- 워터마크(2026-10-26 대표 요청: 웹에도 크게, PDF 는 모든 페이지에 같게). 고정 위치라
			     웹에서는 화면에 늘 떠 있고, 인쇄에서는 브라우저가 쪽마다 다시 찍는다. 흐름 낱장처럼
			     한 장이 여러 쪽으로 흘러도 빠지는 쪽이 없다. 흐름 밖이라 조판 측정에도 닿지 않는다. -->
			<div class="wr-wm" aria-hidden="true"><span>WEONUS</span></div>
			<!-- 인쇄 경고는 고정 요소 하나로 찍는다. tfoot 은 중간 쪽의 자리를 비우지만 마지막 조각에서는
			     본문 바로 뒤에 붙으므로, 문장까지 거기에 맡기면 마지막 쪽 하단이 비게 된다. -->
			<p class="wr-print-legal">{{ legalNotice }}</p>

			<!-- 어드민만 통과한 미리보기라는 사실을 알려 준다. 이 화면은 지금 고객에게 열리지
			     않으므로, 링크를 보내 놓고 열렸다고 오해하지 않게 한다. 미리보기 인쇄에도 이 상태를 남긴다. -->
			<div class="wr-devbar" v-if="devPreview && !pdfMode">
				개발중 상태입니다. 어드민만 볼 수 있는 미리보기이고, 고객이 이 링크를 열면 “개발중입니다!” 안내가 보입니다. 열람 기록도 남지 않습니다.
			</div>
			<!-- 담당자 미리보기(로그인한 직원). 이 열람과 PDF 저장은 고객 통계에 남지 않는다는 것을 알려 준다. -->
			<div class="wr-devbar" v-else-if="staffPreview && !pdfMode">
				담당자 미리보기입니다. 고객이 보는 화면과 같고, 이 화면을 보는 시간과 PDF 저장은 고객 열람으로 남지 않습니다.
			</div>

			<!-- ── 웹 전용 머리띠 ────────────────────────────────────────────
			     표지를 지나면 나타난다. 물건이 다섯 건을 넘어가면 지금 몇 번을 보고 있는지,
			     다른 번호로 어떻게 건너뛰는지가 화면에서 사라진다. 인쇄에는 없다(꼬리말이 그
			     역할을 한다). 위치만 고정하고 내용층을 가리지 않는다. -->
			<div class="wr-bar" v-if="!pdfMode && items.length" :class="{ 'is-on': barOn }">
				<div class="wr-bar__in">
					<span class="wr-mark wr-mark--dark wr-bar__mark"></span>
					<span class="wr-bar__title">{{ report.title }}</span>
					<nav class="wr-bar__nav" aria-label="수록 물건 바로가기">
						<button
							v-for="(it, i) in items"
							:key="'nav' + it.building_uid"
							type="button"
							class="wr-bar__no"
							:class="{ 'is-on': activeLot === i + 1 }"
							:aria-current="activeLot === i + 1 ? 'true' : null"
							@click="goLot(i + 1)">{{ pad(i + 1) }}</button>
					</nav>
					<button type="button" class="wr-bar__pdf" @click="downloadPdf" :disabled="pdfBusy">
						{{ pdfBusy ? '준비 중' : 'PDF' }}
					</button>
				</div>
			</div>

			<!-- ── 표지 ───────────────────────────────────────────────────────
			     표지는 테마마다 **골격이 통째로 다르다**(2026-09-16 사용자 지시 "각 버전들 다
			     구현해서 사용자가 디자인 고를수있게끔"). 색만 갈아 끼우면 같은 문서가 일곱 벌
			     나올 뿐이라는 것은 이미 확인했다.

			     바뀌지 않는 것은 **적히는 사실**이다 — 상호와 캡션(head), 기간·받는 분·제목·
			     요약(low), 상호·주소·고지(band). 일곱 골격이 그 세 덩어리를 서로 다른 자리에
			     앉힐 뿐이고, 어느 지면을 골라도 고객이 받는 정보는 같다. -->
			<!-- 웹에서는 표지의 상호 띠를 접는다. 경고만은 첫 화면에서 읽히도록 띠 밖에 둔다. -->
			<p class="wr-legal wr-cover-legal" v-if="!pdfMode">{{ legalNotice }}</p>
			<section class="wr-sheet wr-sheet--cover" :class="['wr-cover--' + coverKind, titleLenClass]" :style="coverVars">
				<!-- ① 모노그램(도록). 흰 PNG 를 마스크로 떠서 칠한다. 원본을 그대로 쓰면
				     어포스트로피가 버건디라 크림 지면에서 그 획만 떠 보인다. -->
				<span class="wr-cover__mark" v-if="coverKind === 'mark'" aria-hidden="true"></span>

				<!-- ② 도판(프리미엄 다크). 대표 물건 사진 한 장을 금선으로 두른다.
				     먹지에서는 사진이 유일한 빛이므로 컬러 그대로 간다(듀오톤으로 누르면
				     지면 전체가 한 덩어리가 되어 아무것도 안 보인다). -->
				<div class="wr-cover__plate" v-else-if="coverKind === 'plate'">
					<img v-if="coverPhoto" :key="coverPhoto" :src="coverPhoto" alt="" @error="onPhotoError" />
					<span v-else class="wr-cover__plateoff">WEONUS</span>
				</div>

				<!-- ③ 항공사진 전면. 이번 주 물건이 모두 담기는 판을 지면 전체에 깔고,
				     번호를 **실제 위치**에 얹는다. 좌표가 없는 옛 리포트에서는 판이 오지
				     않으므로(coverMapSrc 가 빈 값) 활자만 남은 표지가 된다. -->
				<!-- 판을 못 받으면(좌표가 담기기 전에 만든 리포트, 좌표 없는 매물, 지도 서버 실패)
				     대표 사진을 어둡게 눌러 판으로 쓴다. 검은 판만 남기면 화면 한 장이 통째로 빈
				     먹지가 된다(운영 실측 2026-09-17: 9/7 에 만든 리포트가 그렇게 보였다). -->
				<div class="wr-cover__aerial" v-else-if="coverKind === 'aerial'" :class="{ 'is-photo': !coverMapSrc && !!coverPhoto }">
					<img v-if="coverMapSrc" :src="coverMapSrc" alt="" @error="coverMapFailed = true" />
					<img v-else-if="coverPhoto" :key="coverPhoto" :src="coverPhoto" alt="" @error="onPhotoError" />
					<span class="wr-cover__scrim" aria-hidden="true"></span>
					<span
						v-for="m in coverMarks"
						:key="'cm' + m.no"
						class="wr-cover__pin"
						:style="pinStyle(m)">{{ pad(m.no) }}</span>
				</div>

				<!-- ④ 사진 격자(카탈로그 모자이크). 재단선까지 꽉 채운다.
				     한 칸이라도 비면 격자가 아니라 사고로 보이므로, 사진이 모자라면
				     그 칸을 상호 칸으로 메운다(mosaicCells 가 그렇게 만든다). -->
				<div class="wr-cover__mosaic" v-else-if="coverKind === 'mosaic'">
					<span
						v-for="c in mosaicCells"
						:key="'mo' + c.key"
						class="wr-cover__cell"
						:class="[c.tone ? 'is-' + c.tone : '', { 'is-blank': !c.url }]">
						<img v-if="c.url" :src="c.url" alt="" @error="mosaicFailed[c.key] = true" />
					</span>
				</div>

				<!-- ⑤ 색면(딥그린). 그림 없이 색만으로 지면을 가른다. -->
				<span class="wr-cover__field" v-else-if="coverKind === 'split'" aria-hidden="true"></span>

				<!-- ⑥ 도판 한 장(갤러리). 여백이 주인공이라 사진을 지면 한가운데 띄운다.
				     사진을 못 받으면 자리를 접는다. 빈 상자를 그대로 두면 지면의 2/3 가
				     아무것도 없는 칸으로 남는다(실측 202mm). -->
				<div class="wr-cover__single" v-else-if="coverKind === 'gallery'" :class="{ 'is-off': !coverPhoto }">
					<img v-if="coverPhoto" :key="coverPhoto" :src="coverPhoto" alt="" @error="onPhotoError" />
				</div>

				<!-- ⑦ 그리드(스위스). 사진은 가로 띠로 지면을 가로지르고, 위아래를
				     라틴 라벨 행과 수치 행이 잡는다. -->
				<div class="wr-cover__band2" v-else-if="coverKind === 'grid'" :class="{ 'is-off': !coverPhoto }">
					<img v-if="coverPhoto" :key="coverPhoto" :src="coverPhoto" alt="" @error="onPhotoError" />
				</div>

				<div class="wr-cover__head">
					<span class="wr-mark wr-mark--dark"></span>
					<span class="wr-caption">위클리 리포트</span>
					<!-- 머리를 라틴 라벨 행으로 쓰는 두 골격(시안 05·07). 스위스 그리드는 네 칸을
					     세로 구분선으로 나눠 격자를 눈에 보이게 만들고, 갤러리는 같은 사실을
					     괘선 없이 좌·중·우 세 자리에 흩어 놓는다. 어느 칸을 쓰는지는 CSS 가
					     고른다(다른 골격에서는 이 행 자체를 그리지 않는다). -->
					<template v-if="coverKind === 'grid' || coverKind === 'gallery'">
						<span class="wr-cover__railcap">WEEKLY PROPERTY BRIEF</span>
						<span class="wr-cover__railkey" v-if="report.period_key">{{ report.period_key }}</span>
						<span class="wr-cover__raildate" v-if="period">{{ period }}</span>
					</template>
				</div>

				<div class="wr-cover__gap"></div>

				<!-- 활자 덩어리. **시안 대부분은 제목이 먼저 서고 사실 한 줄이 그 아래 붙는다**
				     (기간 · 받는 분 · 수록 건수 · 합계). 기간과 받는 분을 제목 위에 따로 얹으면
				     읽는 눈이 작은 글 두 줄을 지나야 제목에 닿는다. 도록(catalog)만 예외로
				     예전 순서를 지킨다 — 그 지면은 모노그램이 주인공이고 활자가 아래 한 귀퉁이에
				     모이는 구성이라, 짧은 줄부터 쌓아 올리는 편이 맞다. -->
				<div class="wr-cover__low">
					<span class="wr-cover__rule" aria-hidden="true"></span>
					<span class="wr-period" v-if="period">{{ period }}</span>
					<span class="wr-to" v-if="addressee">{{ addressee }}</span>
					<h1 class="wr-title">{{ report.title }}</h1>
					<p class="wr-cover__meta1" v-if="coverFacts">{{ coverFacts }}</p>
					<p class="wr-cover__sum" v-if="coverSummary">{{ coverSummary }}</p>

					<!-- 스위스 그리드의 키-값 표(시안 05). 제목 오른쪽에 붙어 같은 사실을
					     라벨과 함께 세운다. 다른 골격은 제목 아래 한 줄이 그 일을 한다. -->
					<dl class="wr-cover__meta" v-if="coverKind === 'grid'">
						<div><dt>수신</dt><dd>{{ report.client_label || '–' }}</dd></div>
						<div><dt>수록 물건</dt><dd>{{ items.length }}건</dd></div>
						<div><dt>합계 매매가</dt><dd>{{ coverTotal || '–' }}</dd></div>
						<div><dt>지역</dt><dd>{{ mapDongs.replace(' 일대', '') || '–' }}</dd></div>
					</dl>
				</div>

				<!-- 표지 물건 목록. 지면이 그림으로 차 있는 골격(모노그램·갤러리)에는 두지
				     않는다 — 거기서는 목록이 다음 장(수록 물건)의 일이다. 목록을 두는
				     골격에서도 **행마다 괘선을 긋지 않는다**. 다섯 줄에 밑줄을 다섯 번 그으면
				     인쇄 조판이 아니라 웹 표가 된다. -->
				<!-- 웹에서는 이 목록이 바로가기다. 아래 '이번 주 수록 물건' 색인을 그대로 두면 같은
				     네 줄이 표지 바로 밑에 한 번 더 서서 같은 것이 두 번 읽힌다(운영 실측 2026-09-17,
				     휴대폰에서 특히). 그래서 표지에 목록이 있는 골격은 웹 색인을 접고 이 행이 그 일을 한다. -->
				<ol class="wr-cover__lots" v-if="coverLots.length">
					<li
						v-for="l in coverLots"
						:key="'cl' + l.no"
						:class="{ 'is-link': !pdfMode }"
						:tabindex="pdfMode ? null : 0"
						:role="pdfMode ? null : 'link'"
						@click="!pdfMode && goLot(l.no)"
						@keydown.enter.prevent="!pdfMode && goLot(l.no)">
						<span class="wr-cover__lotno">{{ pad(l.no) }}</span>
						<span class="wr-cover__lotname">{{ l.name }}</span>
						<span class="wr-cover__lotarea" v-if="l.area">{{ l.area }}</span>
						<span class="wr-cover__lotaddr" v-if="l.addr">{{ l.addr }}</span>
						<span class="wr-cover__lotdong" v-if="l.dong">{{ l.dong }}</span>
						<span class="wr-cover__lotprice" v-if="l.price">{{ l.price }}</span>
					</li>
				</ol>

				<div class="wr-cover__band">
					<p class="wr-cover__tally" v-if="coverTally">{{ coverTally }}</p>
					<p class="wr-cover__dongs" v-if="coverTally && mapDongs">{{ mapDongs.replace(' 일대', '') }}</p>
					<div class="wr-cover__corp">
						<strong>위온어스부동산중개법인</strong>
						<span>서울특별시 강남구 학동로25길 11, 5층(논현동, 씨플레이스)</span>
						<span class="wr-cover__agent" v-if="report.agent_name">담당 {{ report.agent_name }}</span>
					</div>
					<p class="wr-cover__note">{{ disclaimer }}</p>
					<p class="wr-legal">{{ legalNotice }}</p>
				</div>

				<!-- 종이 결. 잉크가 종이에 눌린 것처럼 만든다. 먹지 계열 테마에서는 곱하기가
				     지면을 더 어둡게만 만들어 결이 보이지 않으므로 테마가 끈다(--wr-tex: 0). -->
				<span class="wr-cover__tex" aria-hidden="true"></span>
				<!-- WebKit 인쇄 전용 워터마크(아래 `.wr-wk` 인쇄 규칙). 고정 워터마크가 쪽마다 되풀이되지 않는 엔진이다. -->
				<div class="wr-wm wr-wm--card" v-if="!pdfMode" aria-hidden="true"><span>WEONUS</span></div>
			</section>

			<!-- ── 이번 주 수록 물건 ──────────────────────────────────────────
			     표지에서 내려온 목록. 웹에서는 그대로 바로가기가 되고, 인쇄에서는
			     속장 첫 장 머리에 얹힌다(낱장을 따로 쓰면 다섯 건짜리 리포트에서
			     한 장이 목록만으로 채워진다). -->
			<section class="wr-index" v-if="items.length && !pdfMode && !coverLots.length" :class="{ 'wr-index--three': items.length >= 13 }">
				<h2 class="wr-index__head">이번 주 수록 물건</h2>
				<ol class="wr-index__list">
					<li v-for="(it, i) in items" :key="it.building_uid">
						<a
							class="wr-index__link"
							:href="'#wr-lot-' + (i + 1)"
							@click.prevent="goLot(i + 1)">
							<span class="wr-index__no">{{ pad(i + 1) }}</span>
							<span class="wr-index__name">{{ it.snapshot.building_name || '매물' }}</span>
							<span class="wr-index__area">{{ areaShort(it) }}</span>
							<span class="wr-index__price" v-if="priceText(it)">{{ priceText(it) }}</span>
						</a>
					</li>
				</ol>
			</section>

			<!-- ── 속장 ───────────────────────────────────────────────────────
			     인쇄는 낱장마다 끊고, 웹에서는 sheets 가 한 덩어리로 흐른다. -->
			<section
				v-for="(sh, si) in sheets"
				:key="'sheet' + si"
				class="wr-sheet wr-sheet--body"
				:class="{ 'wr-sheet--measure': measuring, 'wr-sheet--flow': sh.flow }">
				<!-- 흐름 낱장(인쇄)만 실제 표로 감싸 꼬리말을 <tfoot> 에 둔다. 한 건이 A4 보다 길어 여러 쪽으로
				     흐르면 낱장 끝의 꼬리말은 마지막 쪽에만 찍히는데, Chromium 은 표가 쪽을 넘을 때 <tfoot> 을
				     쪽마다 다시 찍는다(2026-10-26 실측. CSS 의 table-footer-group 은 반복되지 않는다). 그래서
				     민·형사 경고의 공간을 흐름 낱장의 모든 쪽에 남긴다. 문장은 고정 꼬리말이 하단에 찍는다.
				     일반 낱장과 측정 중에는 같은 자리를 display: contents 인 div 로 그려 레이아웃이 바뀌지 않는다. -->
				<component :is="flowTab(sh) ? 'table' : 'div'" class="wr-flowtab">
				<tfoot v-if="flowTab(sh)"><tr><td>
					<div class="wr-foot-line">
						<p class="wr-foot-line__legal">{{ legalNotice }}</p>
						<span>위온어스부동산중개법인</span>
					</div>
				</td></tr></tfoot>
				<component :is="flowTab(sh) ? 'tbody' : 'div'" class="wr-flowtab__part">
				<component :is="flowTab(sh) ? 'tr' : 'div'" class="wr-flowtab__part">
				<component :is="flowTab(sh) ? 'td' : 'div'" class="wr-flowtab__part">
				<!-- 낱장 머리. 도록에서는 그 면에 실린 로트 번호가 온다 — 매 장 같은 문서명을
				     반복하는 것보다 어디를 펴 든 지금 몇 번을 보고 있는지가 쓸모 있다. -->
				<div class="wr-run" v-if="pdfMode">
					<span class="wr-caption wr-caption--dark">{{ runLabel(sh) }}</span>
					<span class="wr-run__line"></span>
					<span class="wr-mark wr-mark--dark"></span>
				</div>

				<div class="wr-sheet__inner" :style="innerStyle(sh)">
					<!-- 수록 물건 색인. 인쇄에서는 표지에서 내려와 첫 낱장 머리에 앉는다.
					     낱장을 따로 쓰면 다섯 건짜리 리포트에서 한 장이 목록만으로 채워진다
					     (실측: 4쪽이 5쪽이 되고 2쪽의 4/5 가 빈 종이였다). -->
					<section class="wr-index" v-if="sh.index" :class="{ 'wr-index--three': items.length >= 13 }">
						<h2 class="wr-index__head">이번 주 수록 물건</h2>
						<ol class="wr-index__list">
							<li v-for="(it, i) in items" :key="it.building_uid">
								<span class="wr-index__link">
									<span class="wr-index__no">{{ pad(i + 1) }}</span>
									<span class="wr-index__name">{{ it.snapshot.building_name || '매물' }}</span>
									<span class="wr-index__area">{{ areaShort(it) }}</span>
									<span class="wr-index__price" v-if="priceText(it)">{{ priceText(it) }}</span>
								</span>
							</li>
						</ol>
					</section>

					<div class="wr-brief" v-if="sh.brief">
						<div class="wr-brief__label">이번 주 브리핑</div>
						<p class="wr-brief__body">{{ report.brief }}</p>
						<p class="wr-legal wr-brief__legal" v-if="!pdfMode" aria-hidden="true">{{ legalNotice }}</p>
					</div>

					<!-- 물건 로트. 테두리도 배경도 그림자도 없다(DESIGN.md Components).
					     왼쪽 띠는 현장 사진과 **그 자리의 항공사진**이고 지면 왼쪽 재단선까지 나간다.
					     오른쪽 단이 번호·이름·금액·제원·소견을 받는다. 지면이 위아래가 아니라
					     좌우로 갈려야 웹 스크롤이 아니라 인쇄 조판으로 읽힌다. -->
					<article
						v-for="row in sh.rows"
						:key="row.it.building_uid"
						:id="'wr-lot-' + row.no"
						class="wr-lot"
						:style="pdfMode && row.h ? { height: row.h } : null"
						:data-building-uid="row.it.building_uid">
						<!-- 사진 단. 인쇄에서는 display:contents 로 사라지고 .wr-lot__photo 가
						     예전처럼 로트의 직계 자식이 된다(낱장 높이 계산이 그 구조를 잰다). -->
						<div class="wr-lot__media">
							<div class="wr-lot__photo">
								<!-- 원본이 사라진 사진이 실제로 있다(R2 유실). 그때 서버는 200 에 빈 본문을
								     주므로 브라우저는 '깨진 이미지 + 대체문구' 를 그린다. 고객 문서에서는
								     그것보다 빈 칸이 낫다. 실패하면 자리 표시로 바꾼다. -->
								<img
									v-if="mainPhoto(row.it)"
									:key="mainPhoto(row.it)"
									:src="mainPhoto(row.it)"
									:alt="(row.it.snapshot.building_name || '매물') + ' 대표 사진'"
									@error="onPhotoError"
									@click="onPhoto(row.it)" />
								<div v-else class="wr-lot__nophoto">WEONUS</div>
								<!-- 사진이 더 있다는 사실은 화면에 단서가 있어야 존재한다. -->
								<button
									type="button"
									class="wr-lot__more"
									v-if="!pdfMode && photoCount(row.it) > 1"
									@click="openPhotos(row.it, shownIdx(row.it))">사진 {{ photoCount(row.it) }}장</button>
							</div>

							<!-- 사진 격자(mosaic) 지면만: 대표 사진 아래 작은 사진 두 장이 격자를 잇는다.
							     표지의 3x3 격자와 같은 언어다. 1·2번을 집지 않고 목록을 균등 간격으로 끊어
							     같은 장면의 다른 크기 벌을 건너뛴다(표지 격자와 같은 이유). -->
							<div class="wr-lot__grid" v-if="lotGrid && gridPhotos(row.it).length">
								<component
									:is="pdfMode ? 'div' : 'button'"
									v-for="g in gridPhotos(row.it)"
									:key="'g' + g.id"
									:type="pdfMode ? null : 'button'"
									class="wr-lot__cell"
									:aria-label="pdfMode ? null : `사진 ${g.idx + 1}번 크게 보기`"
									@click="pdfMode ? null : openPhotos(row.it, g.idx)">
									<img :src="g.url" alt="" @error="gridFailed[g.id] = true" />
								</component>
							</div>
							<!-- 갤러리 도판 지면만: 도판 아래 한 줄 캡션. 전시 도록의 플레이트가 그렇듯
							     그림은 여백 안에 앉고 그 밑에 작은 글자가 붙는다. -->
							<div class="wr-lot__photocap" v-if="lotCaption">
								<span>{{ spotCaption(row.it) }}</span>
								<span>현장 사진</span>
							</div>

							<ul class="wr-lot__thumbs" v-if="!pdfMode && thumbs(row.it).length">
								<li v-for="t in thumbs(row.it)" :key="'th' + t.idx">
									<button
										type="button"
										class="wr-thumb"
										:aria-label="`사진 ${t.idx + 1}번 크게 보기`"
										@click="openPhotos(row.it, t.idx)">
										<img :src="t.url" alt="" loading="lazy" @error="thumbFailed[t.id] = true" />
										<span class="wr-thumb__more" v-if="t.more">+{{ t.more }}</span>
									</button>
								</li>
							</ul>
							<!-- WebKit 인쇄 전용 경고(위치도를 못 받은 카드). 아래 `.wr-wk` 인쇄 규칙이 두 골격 모두 사진을 같은 만큼 줄인다. -->
							<p class="wr-legal wr-lot__legal" v-if="!pdfMode && !spotSrc(row.no)" aria-hidden="true">{{ legalNotice }}</p>
						</div>

						<div class="wr-lot__body">
							<!-- 번호는 사진 위 배지가 아니라 글 단의 머리다. 도록에서 로트 번호는
							     그 물건의 이름 앞에 서는 좌표지, 사진에 붙이는 딱지가 아니다. -->
							<!-- 번호는 로트의 이름표다. 도록에서는 물건명·금액과 같은 층위로 크게 서고,
							     태그는 그 옆에 기준선을 맞춰 작게 붙는다(시안 04). 작게 두면 지면에서
							     "몇 번 물건인가" 를 찾는 데 눈이 한 번 더 든다. -->
							<!-- 번호·이름·주소·금액은 한 덩어리(`wr-lot__top`)다. 색면 분할 지면이 이
							     덩어리에만 색을 입힌다 — 다른 지면에서는 보이지 않는 감싸개다. -->
							<div class="wr-lot__top">
							<div class="wr-lot__head">
								<span class="wr-lot__no">{{ pad(row.no) }}</span>
								<span class="wr-lot__tags" v-if="row.it.tags.length">
									<template v-for="(t, i) in row.it.tags" :key="t">
										<span v-if="i" class="wr-sep">·</span>{{ t }}
									</template>
								</span>
							</div>

							<h2 class="wr-lot__name">{{ row.it.snapshot.building_name || '매물' }}</h2>

							<div class="wr-lot__addr" v-if="addrOf(row.it)">{{ addrOf(row.it) }}</div>

							<div class="wr-lot__figure" v-if="priceText(row.it) || rateText(row.it)">
								<span class="wr-lot__price" v-if="priceText(row.it)">{{ priceText(row.it) }}</span>
								<span class="wr-lot__rate" v-if="rateText(row.it)">{{ rateText(row.it) }}</span>
							</div>
							</div>

							<!-- 제원. 라벨과 값을 짝지어 두 단으로 편다. 항목이 없어도 줄표로 자리를
							     지키므로 같은 항목이 매물마다 같은 칸에 온다 — 다섯 건을 나란히 놓고
							     비교할 수 있다(DESIGN.md Typography, 2026-09-14 사용자 선택). 가운뎃점으로
							     한 줄에 흘리면 그 비교가 불가능해진다. 배경도 테두리도 없다. -->
							<dl class="wr-spec" v-if="specRows(row.it).length">
								<div
									v-for="(r, ri) in specRows(row.it)"
									:key="ri"
									class="wr-spec__row"
									:class="{ 'wr-spec__row--wide': r.wide }">
									<dt>{{ r.k }}</dt>
									<dd>{{ r.v }}</dd>
								</div>
							</dl>

							<ul class="wr-lot__features" v-if="hasFeatures(row.it)">
								<li v-for="(line, i) in row.it.snapshot.feature_lines" :key="i">{{ line }}</li>
							</ul>

							<p class="wr-lot__note" v-if="row.it.comment">{{ row.it.comment }}</p>
						</div>

						<!-- 그 자리의 위치도(라벨 있는 일반 지도). **두 단 아래 카드 전폭의 띠**다.
						     글 단 바닥의 110mm 띠로 두었을 때 "네이버 지도 너무 작은데" (2026-09-17
						     사용자 지적). 전폭이면 사진 단과 글 단의 아래끝은 그대로 맞고(사진이
						     글 높이에 맞춰 잘린다) 지도만 그 밑에서 지면 폭을 다 쓴다. 사진 밑 한
						     단에 두는 것과 다르다 — 그렇게 두면 글이 짧은 물건에서 오른쪽이 통째로
						     빈다(실측 04·05번). 건물이 판 한가운데 온다(서버가 그 좌표를 중심으로
						     받는다). 마커는 이미지에 굽지 않고 화면이 번호 핀으로 얹는다(지면마다
						     강조색이 다르고, 인쇄에서도 벡터라 선명하다). -->
						<figure class="wr-lot__spot" v-if="spotSrc(row.no)">
							<div class="wr-lot__spotimg">
								<img :src="spotSrc(row.no)" alt="" @error="spotFailed[row.no] = true" />
								<!-- 번호 핀. 꼭짓점이 판 한가운데(=건물)에 선다. 지도가 라벨이 있는 일반 지도로
								     바뀌면서(2026-09-17) 십자는 상호 아이콘 사이에 묻혔다 — 핀은 흰 테두리와
								     그림자로 지도 위의 어떤 아이콘과도 구별되고, 번호가 위 물건과 잇는다. -->
								<!-- 항공 지면은 표지의 번호 배지(원)를 그대로 쓴다. 배지 한가운데가 건물이고,
								     바깥의 가는 고리가 표지의 조준선을 잇는다. -->
								<svg v-if="pinKind === 'badge'" class="wr-lot__pin wr-lot__pin--badge" viewBox="0 0 44 44" aria-hidden="true">
									<circle class="wr-lot__ring" cx="22" cy="22" r="20" />
									<circle class="wr-lot__pinbody" cx="22" cy="22" r="13" />
									<text class="wr-lot__pinno" x="22" y="26.5" text-anchor="middle">{{ pad(row.no) }}</text>
								</svg>
								<svg v-else class="wr-lot__pin" viewBox="0 0 34 44" aria-hidden="true">
									<ellipse class="wr-lot__pinhalo" cx="17" cy="41" rx="9" ry="3" />
									<path class="wr-lot__pinbody" d="M17 42 C17 42 3.5 26 3.5 15.5 A13.5 13.5 0 1 1 30.5 15.5 C30.5 26 17 42 17 42 Z" />
									<text class="wr-lot__pinno" x="17" y="20" text-anchor="middle">{{ pad(row.no) }}</text>
								</svg>
							</div>
							<figcaption class="wr-lot__spotcap">
								<span>{{ spotCaption(row.it) }}</span>
								<span class="wr-lot__spotsrc">네이버 지도</span>
							</figcaption>
						</figure>
						<!-- WebKit 인쇄 전용 경고와 워터마크(아래 `.wr-wk` 인쇄 규칙). 카드가 한 쪽에 들면 그 쪽에 하나씩 남는다. -->
						<template v-if="!pdfMode">
							<p class="wr-legal wr-lot__legal" v-if="spotSrc(row.no)" aria-hidden="true">{{ legalNotice }}</p>
							<div class="wr-wm wr-wm--card" aria-hidden="true"><span>WEONUS</span></div>
						</template>
					</article>

				</div>
				</component>
				</component>
				</component>
				</component>

				<!-- 측정 중에도 그린다. 감추면 그만큼 가용 높이가 넓게 측정돼(실측 12mm)
				     실제로는 안 들어가는 카드를 한 장에 밀어 넣게 된다. -->
				<div class="wr-foot-line" v-if="pdfMode && !flowTab(sh)">
					<p class="wr-foot-line__legal">{{ legalNotice }}</p>
					<span>위온어스부동산중개법인</span>
					<span class="wr-foot-line__no" v-if="!measuring && canNumber">{{ si + 2 }} / {{ sheets.length + 2 }}</span>
				</div>
			</section>

			<!-- 뒤표지(인쇄 전용). 마지막 쪽에 민·형사 경고를 크게 둔다(2026-10-26 대표 요청: 첫 쪽·끝 쪽·
			     모든 쪽 하단). 마지막 속장 안에 넣으면 그 장의 가용 높이가 줄어 조판을 다시 재야 하므로
			     따로 한 장을 쓴다. 표지처럼 쪽번호는 붙이지 않는다(DESIGN.md Layout). -->
			<section class="wr-sheet wr-sheet--back" v-if="pdfMode && !measuring">
				<span class="wr-mark wr-mark--dark"></span>
				<div class="wr-back__gap"></div>
				<div class="wr-back__legal">
					<strong>자료 이용 제한 및 법적 책임 안내</strong>
					<p>{{ legalNotice }}</p>
				</div>
				<div class="wr-back__corp">
					<strong>위온어스부동산중개법인</strong>
					<span>서울특별시 강남구 학동로25길 11, 5층(논현동, 씨플레이스)</span>
					<span v-if="report.agent_name">담당 {{ report.agent_name }}</span>
				</div>
				<p class="wr-back__note">{{ disclaimer }}</p>
			</section>

			<!-- 웹에서만 보이는 마무리 -->
			<footer class="wr-foot" v-if="!pdfMode">
				<div class="wr-foot__actions">
					<button type="button" class="wr-btn" @click="downloadPdf" :disabled="pdfBusy">
						{{ pdfBusy ? 'PDF 준비 중…' : 'PDF로 저장' }}
					</button>
				</div>
				<div class="wr-foot__corp">
					<strong>위온어스부동산중개법인</strong>
					<span>서울특별시 강남구 학동로25길 11, 5층(논현동, 씨플레이스)</span>
					<span v-if="report.agent_name">담당 {{ report.agent_name }}</span>
				</div>
				<p class="wr-foot__note">{{ disclaimer }}</p>
				<p class="wr-legal">{{ legalNotice }}</p>
				<div class="wr-wm wr-wm--card" aria-hidden="true"><span>WEONUS</span></div>
			</footer>

			<!-- ── 사진 크게 보기(웹 전용) ────────────────────────────────────
			     예전에는 대표 사진을 새 탭으로 열었다. 카톡 인앱 브라우저에서는 그 탭이
			     리포트를 덮어 버려 고객이 돌아오지 못하고, 나머지 열아홉 장은 볼 방법이
			     아예 없었다. 같은 화면 위에서 넘겨 본다. -->
			<div
				class="wr-lb"
				v-if="lb.open"
				role="dialog"
				aria-modal="true"
				:aria-label="lb.name + ' 사진'"
				@click.self="closeLb">
				<div class="wr-lb__head" @click.self="closeLb">
					<span class="wr-lb__name">{{ lb.name }}</span>
					<button type="button" class="wr-lb__x" @click="closeLb" aria-label="닫기">
						<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
					</button>
				</div>

				<div class="wr-lb__stage" @click.self="closeLb">
					<img v-if="!lbFailed" :src="lbUrl" :alt="lb.name + ' 사진 ' + (lb.idx + 1)" @error="onLbError" />
					<p v-else class="wr-lb__gone">이 사진은 불러오지 못했습니다.</p>
				</div>

				<div class="wr-lb__foot">
					<button type="button" class="wr-lb__nav" @click="stepLb(-1)" aria-label="이전 사진">
						<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
					</button>
					<span class="wr-lb__count">{{ pad(lb.idx + 1) }} <i>/</i> {{ pad(lb.ids.length) }}</span>
					<button type="button" class="wr-lb__nav" @click="stepLb(1)" aria-label="다음 사진">
						<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
					</button>
				</div>
			</div>

			<!-- 서버 PDF 렌더러가 이 표시를 기다린다. 자료가 다 그려진 뒤에만 나타나야 한다. -->
			<div id="report-ready" v-if="ready" style="width: 0; height: 0; overflow: hidden"></div>
		</template>
	</div>
</template>

<script>
import axios from 'axios';
import { formatMoneyMuch, normalizeAddr } from '@/utils/format';
import { REPORT_THEMES, DEFAULT_THEME, themeOf } from '@/utils/reportThemes';

// ── 체류 측정 상수 ──────────────────────────────────────────────────────────
const TICK_MS = 1000;          // 1초마다 "지금 보고 있는가" 를 판정한다
const IDLE_MS = 60000;         // 마지막 조작 후 이 시간이 지나면 보고 있지 않다고 본다
const FLUSH_MS = 15000;        // 모아 둔 시간을 서버로 보내는 주기
const VISIBLE_RATIO = 0.5;     // 카드가 이만큼 보이면 "보고 있다" 로 센다

// ── 인쇄 낱장 짜기 ──────────────────────────────────────────────────────────
// A4 한 장에 카드를 몇 장 앉힐지는 **재 보고** 정한다. 글자 수로 어림하면 두 방향으로
// 다 틀린다. 넉넉히 잡으면 지면이 반쯤 비고, 빠듯하게 잡으면 고객 문서에서 문장이
// 소리 없이 잘린다. 그래서 먼저 낱장 하나에 전부 흘려 실제 높이를 재고(measuring),
// 그 높이로 낱장을 나눈 다음에야 #report-ready 를 올린다.
const MAX_CARDS_PER_SHEET = 2; // 자리가 남아도 셋은 앉히지 않는다(카드가 우표만 해진다)
/* 한 건이 지면보다 커도 잘라내지 않고 이만큼까지 줄여 담는다. 0.8 이었는데, 위치도가 전폭 56mm
   띠가 되면서 특징 12줄 + 코멘트 300자 물건이 0.79 로 아슬하게 못 들어가 흐름 낱장으로 빠졌고,
   그 결과 지도 하나만 실린 낱장이 생겼다(실측 2026-09-17). 0.74 면 그 물건이 한 장에 들었는데,
   속장이 테마를 따르면서(색면 분할의 머리 색면 11mm, 스위스의 제원 괘선 행) 같은 물건이 스위스
   0.732·색면 0.715 를 요구해 다시 흐름 낱장으로 빠졌다(실측). 0.7 이면 일곱 지면 전부 든다.
   잰 높이는 한 단 폭 기준이라 넓어진 지면에서는 글이 덜 접혀 실제로는 이보다 덜 줄어든 것처럼
   보인다(드문 경로다). */
const MIN_FIT = 0.7;

/**
 * 세션 키. 운영이 http(비보안 컨텍스트)라 crypto.randomUUID 가 **없다**.
 * 폴백 없이 쓰면 운영에서만 조용히 터진다(사내 다른 기능에서 겪은 유형).
 */
function makeSessionKey() {
	try {
		if (window.crypto && typeof window.crypto.randomUUID === 'function') {
			return window.crypto.randomUUID().replace(/-/g, '');
		}
		if (window.crypto && window.crypto.getRandomValues) {
			const a = new Uint8Array(16);
			window.crypto.getRandomValues(a);
			return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
		}
	} catch (e) { /* 아래 폴백 */ }
	return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`.slice(0, 32);
}

export default {
	name: 'PublicReport',
	data() {
		return {
			loading: true,
			errorMsg: '',
			devBlocked: false,     // 개발중 게이트에 막혔다(고객이 보는 화면)
			devPreview: false,     // 게이트를 통과한 어드민 미리보기다 → 열람 추적을 걸지 않는다
			staffPreview: false,   // 로그인한 직원의 미리보기다(게이트와 무관) → 열람 추적을 걸지 않는다
			ready: false,
			report: {},
			items: [],
			pdfBusy: false,
			failedPhotoIds: {},    // 사진 id -> 못 불러왔다(R2 유실). 대표·표지 사진이 다음 후보로 넘어간다.
			spotUrl: {},           // 매물 번호 → 그 자리의 위치도 주소(인증이 필요하면 blob)
			gridFailed: {},        // 사진 격자(mosaic)에서 원본이 유실돼 실패한 사진 id
			spotFailed: {},        // 그 위치도를 못 받은 매물 번호

			// ── 조판 테마 ──────────────────────────────────────────────────
			// 지면은 **리포트 설정에서 고른다**(ReportEdit). 이 화면은 고객 문서이므로 고르는
			// 자리를 두지 않고, 리포트에 저장된 값을 그대로 그린다. 주소의 ?theme= 는 편집
			// 화면의 미리보기와 서버 PDF 렌더러만 쓴다.
			themeSaved: DEFAULT_THEME,
			coverMapUrl: '',       // 표지 항공사진(묶음). 항공 표지에서만 받는다
			coverMapMeta: null,    // { w, h, level, marks: [{no,x,y}] }
			coverMapFailed: false,
			/** 표지 항공사진 판의 실제 크기(px). 번호를 cover 로 얹는 계산에 쓴다. */
			coverBox: { w: 0, h: 0 },
			coverPhotoId: 0,       // pickCoverPhoto 가 고른 사진. 0 이면 첫 물건의 대표 사진.
			alive: true,           // 화면을 떠난 뒤 늦게 온 응답이 추적·타이머를 새로 걸지 않게(Codex 리뷰)
			lastTickAt: 0,         // 체류를 실제 경과시간으로 세기 위한 직전 tick 시각
			mosaicFailed: {},
			measuring: false,      // 인쇄용 높이 측정 중(낱장을 아직 나누지 않았다)
			groups: null,          // 측정으로 확정된 낱장 구성
			disclaimer: '본 자료는 수신인 참고용으로 제공되며, 기재된 내용은 조사 시점의 자료를 기준으로 '
				+ '작성되어 실제와 다를 수 있습니다. 거래 전 반드시 담당자에게 확인해 주시기 바랍니다.',
			// 민·형사 경고. 매물 자료(PrintPage 의 pageFooterLine1)와 같은 문장이다 — 대표가 "기존 매물
			// 자료에 쓰는 경고 문구" 를 지정했다. 빨간색으로 표지·속장 꼬리말·뒤표지·웹 끝에 싣는다.
			legalNotice: '본 자료에는 공개가 제한되거나 매도인의 비밀정보가 포함될 수 있습니다. 수신인이 불필요하게 '
				+ '수령한 경우 즉시 파기하여야 하며, WEONUS 및 매도인의 사전 서면 동의 없이 본 자료의 전부 또는 '
				+ '일부를 복제, 배포, 전송, 인용, 변경 또는 제3자에게 제공할 수 없습니다. 위반 시 관련 법령에 '
				+ '따른 민·형사상 책임이 발생할 수 있습니다.',

			// ── 웹 전용 상태(인쇄에서는 하나도 쓰이지 않는다) ──────────────
			anim: false,           // 등장 전환을 켜도 되는가(IntersectionObserver 가 있고, 감속 설정이 아님)
			reduceMotion: false,   // prefers-reduced-motion
			barOn: false,          // 표지를 지나 머리띠가 보이는 상태인가
			activeLot: 0,          // 지금 읽고 있는 물건 번호(머리띠에서 굵게 표시)
			thumbFailed: {},       // imgId -> 작은 사진을 못 불러왔다(R2 유실)
			lb: { open: false, ids: [], idx: 0, name: '', uid: 0 },   // 사진 크게 보기
			lbFailed: false,

			sessionKey: makeSessionKey(),
			lastActiveAt: Date.now(),
			pendingActiveMs: 0,
			pendingItems: {},      // buildingUid -> { visibleMs, photoCnt }
			cardEls: [],           // 체류를 셀 카드 요소(추적 시작 때 한 번 걷는다)
			tickTimer: null,
			flushTimer: null,
			robotsMeta: null,   // 색인 차단 메타 태그(created 에서 붙이고 unmount 에서 뗀다)
		};
	},
	computed: {
		token() {
			return String(this.$route.params.token || '');
		},
		/** 지금 크게 보고 있는 사진 주소. 고객 화면이라 원본이 아니라 적당히 줄여 받는다. */
		lbUrl() {
			const id = this.lb.ids[this.lb.idx];
			return id ? `${this.apiBase}/users/item/image?id=${id}&w=1600&q=84` : '';
		},
		/** 서버 PDF 렌더 중이면 추적을 끄고 인쇄용으로 그린다. */
		pdfMode() {
			return String(this.$route.query.pdf || '') === '1';
		},
		/**
		 * 이 브라우저의 인쇄가 WebKit(맥 사파리, 아이폰의 모든 브라우저)으로 찍히는가. WebKit 은 고정 요소를
		 * 쪽마다 되풀이하지 않고 문서 끝에 한 번 찍는다. 표 머리·꼬리 행과 @page 여백 상자도 되풀이하지
		 * 않는다(실측 2026-10-29, WebKitGTK 2.52: 7쪽 중 마지막 쪽에만 경고와 워터마크).
		 * 기능으로는 가려낼 수 없어 엔진 이름으로 가른다. 아이폰의 크롬(CriOS)·엣지(EdgiOS)도 WebKit 이라
		 * 맞게 잡히고, 데스크톱 크롬·엣지·삼성 인터넷과 서버 PDF(HeadlessChrome)는 `Chrome/` 이 있어 빠진다.
		 */
		webkitPrint() {
			if (this.pdfMode || typeof navigator === 'undefined') return false;
			const ua = navigator.userAgent || '';
			return /AppleWebKit\//.test(ua) && !/(Chrome|Chromium)\//.test(ua);
		},
		apiBase() {
			return process.env.VUE_APP_HOST_BACK || '';
		},
		/**
		 * 지금 그릴 테마. 주소의 ?theme= 가 최우선(담당자 미리보기 · 서버 PDF 미리보기),
		 * 그다음이 화면에서 고른 값, 마지막이 리포트에 저장된 값이다.
		 * 주소를 우선하는 이유: 서버 PDF 렌더러는 화면 상태를 물려받을 수 없고 주소만 받는다.
		 */
		themeId() {
			const q = String(this.$route.query.theme || '').trim().toLowerCase();
			if (q && REPORT_THEMES.some((t) => t.id === q)) return q;
			return this.themeSaved || DEFAULT_THEME;
		},
		theme() {
			return themeOf(this.themeId);
		},
		/** 표지 골격. 테마가 정한다. */
		coverKind() {
			return this.theme.cover;
		},
		/* 속장 조판의 테마별 갈래. 골격은 일곱 지면이 같고(왼쪽 사진, 오른쪽 글 + 위치도),
		   지면마다 그 골격 위에 얹는 것이 다르다. 여기 세 값이 템플릿에 들어가는 전부이고
		   나머지는 `.wr-t-*` 스코프의 CSS 다. */
		lotGrid() { return this.themeId === 'mosaic'; },       // 대표 사진 아래 작은 사진 격자
		lotCaption() { return this.themeId === 'gallery'; },   // 도판 아래 캡션 한 줄
		pinKind() { return this.themeId === 'aerial' ? 'badge' : 'pin'; },
		/**
		 * 표지에 쓸 대표 사진 한 장. 기본은 첫 물건의 대표 사진이고, `pickCoverPhoto` 가 더 나은
		 * 후보를 찾으면 그것으로 바꾼다(운영 실측 2026-09-17: 첫 물건의 사진이 전부 450px 짜리
		 * 세로 사진이라 표지 도판이 두 배로 늘어나 뭉개졌는데, 두 번째 물건에는 1200px 가로
		 * 사진이 있었다). 표지 사진은 물건 번호를 달지 않으므로 어느 물건의 사진이어도 된다.
		 */
		/** 지금 표지에 쓸 사진 id. 고른 사진이 실패하면 첫 물건부터 쓸 수 있는 대표 사진으로 넘어간다. */
		coverPhotoNow() {
			if (this.coverPhotoId && !this.failedPhotoIds[this.coverPhotoId]) return this.coverPhotoId;
			for (const it of this.items) {
				const id = this.mainPhotoId(it);
				if (id) return id;
			}
			return null;
		},
		coverPhoto() {
			const id = this.coverPhotoNow;
			return id ? this.imgUrl(id) : '';
		},
		coverMapSrc() {
			return this.coverMapFailed ? '' : this.coverMapUrl;
		},
		/** 표지 항공사진 위의 번호. 판을 못 받았으면 얹지 않는다. */
		coverMarks() {
			if (!this.coverMapSrc || !this.coverMapMeta) return [];
			return this.coverMapMeta.marks || [];
		},
		/**
		 * 표지 사진 격자. 아홉 칸을 **물건을 돌아가며** 채운다 — 한 물건의 사진을 앞에서부터
		 * 아홉 장 뽑으면 같은 건물만 아홉 번 나온다(사진 목록은 같은 장면이 크기만 다르게
		 * 여러 벌 올라와 있다는 것을 이미 확인했다). 모자라는 칸은 빈 칸으로 두고 색만 채운다.
		 */
		mosaicCells() {
			// 3x3 격자에서 **실제로 보이는 칸은 다섯 개뿐이다.** 나머지 넷은 상호 칸(오른쪽
			// 위)·제목 칸(가운데 두 칸)·집계 칸(오른쪽 아래)이 덮는다. 아홉 칸을 모두 채우면
			// 보이지도 않는 사진 넉 장을 더 받느라 인쇄가 느려지고, 무엇보다 **좋은 사진이
			// 덮이는 칸으로 가 버린다**.
			// 단, **웹에는 집계 칸이 없다** — 상호·주소·고지 띠는 화면 맨 아래 마무리에 같은
			// 내용이 다시 나오므로 표지에서 감춘다. 그 자리를 빈 칸으로 두면 오른쪽 아래가
			// 회색 사각형이 된다(실측 2026-09-16). 화면에서는 아홉 번째 칸도 사진이다.
			const SHOWN = this.pdfMode ? [0, 1, 5, 6, 7] : [0, 1, 5, 6, 7, 8];

			// 뽑는 것은 물건마다 **대표 사진 한 장**이다. 사진 목록을 균등 간격으로 끊는
			// 방식은 속장의 보조 사진에는 맞지만 표지에는 맞지 않는다 — 목록 가운데는
			// 주차램프·엘리베이터 같은 실내 근접 사진이라, 표지 격자의 1/5 이 무엇이
			// 찍혔는지도 모를 그림이 된다(실측 2026-09-16). 대표 사진은 외관이다.
			const pool = [];
			const seen = {};
			const push = (id) => {
				if (!id || seen[id]) return;
				seen[id] = true;
				pool.push(id);
			};
			this.items.forEach((it) => push(((it.snapshot && it.snapshot.img_ids) || [])[0]));
			// 물건이 다섯 건이 안 되면 같은 물건의 다른 사진으로 칸을 채운다. 실패한
			// 사진(원본 유실)은 아래에서 걸러진다.
			// **1·2·3번을 차례로 집으면 안 된다** — 같은 사진이 크기만 다르게 여러 벌
			// 올라와 있고 목록이 벌 단위로 섞여 있어서(실측: 24장짜리 매물이 8장짜리 세
			// 벌이었고 0·1·2번이 완전히 같은 장면), 대표 사진과 같은 장면이 한 지면에 두
			// 번 실린다(Codex 리뷰 지적). 목록을 균등 간격으로 끊어 벌을 건너뛴다.
			for (let k = 1; k < 5; k += 1) {
				this.items.forEach((it) => {
					const ids = (it.snapshot && it.snapshot.img_ids) || [];
					if (ids.length < 2) return;
					const step = Math.max(1, Math.floor(ids.length / 5));
					push(ids[Math.min(ids.length - 1, k * step)]);
				});
			}

			// 사진이 다섯 장에 못 미치는 리포트가 실제로 있다(한 건짜리 리포트, 사진 한 장).
			// 남는 칸을 전부 같은 어두운 색으로 두면 지면 아래쪽이 빈 사각형 넷이 된다
			// (실측 2026-09-16). 한 칸만 강조색으로
			// 바꾸면 시안 08 처럼 색 블록의 리듬으로 읽힌다. 둘 이상이면 주황이 지면의
			// 주인공이 되어 사진이 곁들이처럼 보인다(실측).
			const usable = pool.filter((id) => !this.mosaicFailed[id]);
			let blanks = 0;
			return Array.from({ length: 9 }, (_, i) => {
				const k = SHOWN.indexOf(i);
				const id = k < 0 ? '' : usable[k];
				if (id) return { key: id, url: this.imgUrl(id), tone: '' };
				blanks += 1;
				return { key: `blank${i}`, url: '', tone: k < 0 || blanks > 1 ? '' : 'accent' };
			});
		},
		/**
		 * 표지 물건 목록. 지면이 그림으로 차 있는 골격(모노그램·갤러리)에는 두지 않는다 —
		 * 거기서는 목록이 다음 장의 일이고, 표지에 얹으면 두 번 읽힌다.
		 */
		coverLots() {
			if (!['plate', 'aerial', 'split', 'grid'].includes(this.coverKind)) return [];
			return this.items.slice(0, 8).map((it, i) => ({
				no: i + 1,
				name: (it.snapshot && it.snapshot.building_name) || '매물',
				// 지역은 목록에서 이름만으로 구분이 안 될 때 쓰는 좌표다(시안 05·06).
				// 골격에 따라 이름 아래로 가거나 가운데 열에 선다. CSS 가 정한다.
				area: this.areaShort(it),
				// 색면 분할(시안 06)만 지번까지 적는다. 목록이 지면의 절반을 쓰는 골격이라
				// 한 줄을 더 받을 자리가 있고, 구(區)·동만 다섯 번 반복하는 것보다 쓸모 있다.
				addr: this.addrOf(it),
				// 항공사진 전면(시안 03)의 캡션 행은 다섯 칸이 한 줄에 서야 한다. 한 칸이
				// 35mm 라 '강남구 논현동' 은 말줄임으로 잘린다. 구(區)는 다섯 칸이 모두
				// 같으므로 빼고 동만 적는다.
				dong: this.dongOf(it),
				// 표지 목록의 금액은 **짧은 꼴**이다. 만원 단위가 섞인 매물(1,515,000만원)을 그대로
				// 적으면 '151억 5,000만원' 이 되어, 항공 표지의 35mm 칸에서 옆 칸 번호를 덮고
				// 이웃 금액끼리 12px 겹친다(Codex 리뷰 재현). 정확한 금액은 속장이 적는다.
				price: this.priceShort(it),
			}));
		},
		/**
		 * 제목 아래 한 줄. 기간 · 받는 분 · 수록 건수 · 합계를 가운뎃점으로 잇는다(시안 02·06·07).
		 * 도록(catalog)과 스위스 그리드는 각자 다른 구성이라 이 줄을 쓰지 않는다 — 도록은 제목
		 * 위에 기간·받는 분을 얹고, 그리드는 오른쪽 키-값 표가 같은 사실을 담는다.
		 */
		coverFacts() {
			if (['mark', 'grid'].includes(this.coverKind)) return '';
			const parts = [];
			if (this.period) parts.push(this.period);
			if (this.report.client_label) parts.push(String(this.report.client_label).trim());
			// 인쇄의 모자이크는 건수·합계를 오른쪽 아래 칸(coverTally)이 담는다. 여기에 또
			// 적으면 같은 사실이 한 지면에 두 번 선다. **화면에는 그 칸이 없으므로** 여기서
			// 빼면 수록 건수와 합계가 표지에서 통째로 사라진다(실측 2026-09-16).
			if (this.coverKind === 'mosaic' && this.pdfMode) return parts.join('  ·  ');
			if (this.items.length) parts.push(`수록 ${this.items.length}건`);
			const sum = this.items.reduce((a, it) => a + (Number((it.snapshot || {}).sell_price) || 0), 0);
			if (sum > 0) parts.push(`합계 ${this.won(sum)}`);
			return parts.join('  ·  ');
		},
		/**
		 * 사진 격자(시안 08)의 오른쪽 아래 칸. 그 지면에는 제목 아래 한 줄이 기간과 받는
		 * 분까지만 담으므로, 이번 주가 몇 건이고 얼마인지는 이 칸이 말한다.
		 */
		coverTally() {
			if (this.coverKind !== 'mosaic' || !this.items.length) return '';
			const t = this.coverTotal;
			return t ? `수록 ${this.items.length}건 / ${t}` : `수록 ${this.items.length}건`;
		},
		/**
		 * 제목 길이에 따른 활자 단계. 표지 제목은 골격마다 크기가 다른데, 가장 큰
		 * 스위스 그리드(인쇄 70px)에서 37자 제목이 여섯 줄이 되어 낱장을 5mm 넘겼다
		 * (실측 2026-09-16). 문장을 잘라내지 않는 것이 이 문서의 규칙이므로, 대신
		 * 활자를 한 단 낮춘다. 담당자가 쓰는 제목 길이는 미리 알 수 없다.
		 */
		titleLenClass() {
			const n = String((this.report && this.report.title) || '').trim().length;
			// 단계는 **골격마다** 다르다. 인쇄 제목 활자와 제목이 쓰는 폭이 골격마다 달라 한 줄에
			// 드는 글자 수가 여섯(스위스 70px, 110mm)에서 스물셋(갤러리 29px, 158mm)까지 벌어진다.
			// 한 표로 두면 스위스에 맞춘 13자가 다른 골격의 열네 자짜리 보통 제목까지 낮춰
			// 표지가 전부 작아진다(실측). 스위스는 21자 + 여덟 건에서 낱장을 11mm 넘겼고(Codex
			// 리뷰 재현), 제목은 최대 120자(편집 화면)라 그 끝까지 단계가 있어야 한다.
			// 넷째 값이 있으면 그 길이부터 한 단계 더 줄인다(스위스 PDF 표지만, 스타일의 is-xxxlong 주석 참고).
			const STEPS = {
				grid: [13, 25, 45, 80],
				split: [25, 40, 60],
				plate: [27, 40, 60],
				aerial: [27, 40, 60],
				mark: [27, 40, 60],
				mosaic: [36, 60, 90],
				gallery: [46, 70, 100],
			};
			const st = STEPS[this.coverKind] || STEPS.mark;
			if (st[3] && n >= st[3]) return 'is-xxlong is-xxxlong';
			if (n >= st[2]) return 'is-xxlong';
			if (n >= st[1]) return 'is-xlong';
			if (n >= st[0]) return 'is-long';
			return '';
		},
		/**
		 * 표지에 넘기는 CSS 변수. `--wr-lots-h` 는 목록이 눌리지 않고 서는 데 필요한 높이다
		 * (두 줄 행 62px, 일곱 건부터는 주소가 빠져 한 줄 행 40px). 색면 분할이 웹에서 표지의
		 * 최소 높이를 이 값으로 올린다.
		 */
		coverVars() {
			const n = Math.min(this.items.length, 8);
			const px = n >= 7 ? n * 40 : n * 62;
			return { '--wr-lots-h': `${px}px` };
		},
		/** 스위스 그리드 표지 머리의 합계. 요약 문장과 같은 값을 쓴다. */
		coverTotal() {
			const sum = this.items.reduce((a, it) => a + (Number((it.snapshot || {}).sell_price) || 0), 0);
			return sum > 0 ? this.won(sum) : '';
		},
		/**
		 * 수신인 표기. 담당자가 '김대표님' 처럼 이미 존칭을 붙여 입력하는 경우가 많아
		 * 무조건 '님께' 를 덧붙이면 "김대표님 님께" 가 되어 고객에게 그대로 나간다.
		 */
		addressee() {
			const s = String(this.report.client_label || '').trim();
			if (!s) return '';
			return /(님|귀하|대표|사장|회장|이사)$/.test(s) ? `${s}께` : `${s} 님께`;
		},
		/**
		 * 쪽번호를 붙일 수 있는가. 흐름 낱장이 섞이면 그 낱장이 몇 장을 차지하는지는
		 * 브라우저가 정하므로 총 장수를 알 수 없다. 틀린 번호를 찍느니 붙이지 않는다.
		 */
		canNumber() {
			return !!this.groups && !this.groups.some((g) => g.flow);
		},
		/**
		 * 표지 지도 아래 한 줄에 쓸 동네 이름. 항공사진에는 지명이 없어서, 어느 동네
		 * 이야기인지는 이 줄이 말해 준다. 같은 동이 여러 건이어도 한 번만 적는다.
		 */
		mapDongs() {
			const out = [];
			this.items.forEach((it) => {
				const dong = this.dongOf(it);
				if (dong && !out.includes(dong)) out.push(dong);
			});
			return out.length ? `${out.join(' · ')} 일대` : '';
		},
		/**
		 * 표지 활자 덩어리의 마지막 줄. 목록을 표지에서 내린 대신, 이번 주가 몇 건이고
		 * 얼마이며 어느 동네인지는 한 줄로 남긴다. 금액이 하나도 없으면 건수와 동네만.
		 */
		coverSummary() {
			// 제목 아래 한 줄(coverFacts)을 쓰는 골격에서는 같은 사실이 두 번 적히게 된다.
			if (this.coverFacts) return '';
			if (!this.items.length) return '';
			const parts = [`수록 물건 ${this.items.length}건`];
			const sum = this.items.reduce((a, it) => a + (Number((it.snapshot || {}).sell_price) || 0), 0);
			if (sum > 0) parts.push(`합계 ${this.won(sum)}`);
			if (this.mapDongs) parts.push(this.mapDongs.replace(/ 일대$/, ''));
			return parts.join(' · ');
		},
		/** 기간 표기. 같은 해면 뒤쪽 연도를 반복하지 않는다. */
		period() {
			const a = String(this.report.sdate || '').slice(0, 10).replace(/-/g, '.');
			const b = String(this.report.edate || '').slice(0, 10).replace(/-/g, '.');
			if (!a && !b) return '';
			if (!a || !b) return a || b;
			return a.slice(0, 4) === b.slice(0, 4) ? `${a} ~ ${b.slice(5)}` : `${a} ~ ${b}`;
		},
		/** 표지의 큰 월 숫자. 기간 시작일의 월을 두 자리로 쓴다. */
		monthNo() {
			const m = String(this.report.sdate || '').slice(5, 7);
			return /^\d{2}$/.test(m) ? m : '';
		},
		/**
		 * 낱장 목록. 웹은 한 덩어리로 흐르고, 인쇄는 측정이 끝나야 나뉜다.
		 * 측정 전에는 전부 한 장에 흘려 놓는다(그 상태를 재는 것이다).
		 */
		sheets() {
			const rows = this.items.map((it, i) => ({ it, no: i + 1 }));
			const hasBrief = !!this.report.brief;
			// 측정 단계(groups 가 아직 없을 때)에도 색인을 그려 둬야 그 높이가 재진다.
			// 감추고 재면 첫 낱장에 실제로는 안 들어가는 카드를 한 장 더 밀어 넣는다.
			const hasIndex = this.pdfMode && this.items.length > 1;
			if (!this.pdfMode || !this.groups) return [{ brief: hasBrief, index: hasIndex, rows, fit: 1 }];
			return this.groups;
		},
	},
	created() {
		// 검색엔진 색인 차단. API 응답에는 X-Robots-Tag 가 붙지만 이 HTML 은 apache 가 주는
		// SPA 껍데기라 헤더가 없다. 링크가 어디엔가 붙여지면 페이지 자체가 색인될 수 있다.
		// 토큰은 192bit 라 추측은 불가능하지만, 색인은 추측이 아니라 '노출된 링크' 로 일어난다.
		this.robotsMeta = document.createElement('meta');
		this.robotsMeta.name = 'robots';
		this.robotsMeta.content = 'noindex, nofollow, noarchive';
		document.head.appendChild(this.robotsMeta);
		this.load();
	},
	beforeUnmount() {
		this.alive = false;
		// SPA 안에서 뒤로가기로 떠나면 pagehide 가 오지 않는다. 모아 둔 값을 여기서 보낸다(Codex 리뷰).
		if (this.tickTimer) this.flush(true);
		this.teardownTracking();
		this.teardownWeb();
		if (this.robotsMeta && this.robotsMeta.parentNode) this.robotsMeta.parentNode.removeChild(this.robotsMeta);
		this.robotsMeta = null;
	},
	methods: {
		async load() {
			try {
				// 개발중 게이트를 통과하는 두 가지 — 어드민 로그인 토큰(같은 브라우저에서 사내
				// 시스템에 로그인해 둔 경우)과, 서버 PDF 렌더러가 URL 로 받은 통행증(g).
				// 고객에게는 둘 다 없으므로 평소와 똑같은 요청이 나간다(헤더가 붙지 않아
				// preflight 도 생기지 않는다).
				const t = this.$store && this.$store.getters.getToken;
				const params = { t: this.token };
				const g = String(this.$route.query.g || '');
				if (g) params.g = g;

				const re = await axios.get(`${this.apiBase}/pub/api/report`, {
					params,
					headers: t && t.accessToken ? { Authorization: t.accessToken } : undefined,
					// 응답이 오지 않으면 로딩 화면에 영원히 머문다 — 일정 시간 뒤 안내 문구로 바꾼다.
					timeout: 30000,
				});
				if (!this.alive) return;
				this.report = re.data.report || {};
				this.items = re.data.items || [];
				// 리포트에 저장된 조판 테마. 모르는 값은 서버가 이미 도록으로 접어 보낸다.
				this.themeSaved = themeOf(this.report.theme).id;
				this.coverMapMeta = re.data.map || null;
				// 사진 대기(waitImages)보다 **먼저** 끝내야 한다. 나중에 붙이면 인쇄 준비
				// 신호가 이미 올라간 뒤라 지도가 빈 자리로 찍힌다.
				await this.loadSpots(g);
				// 표지가 항공사진인 테마에서만 묶음 판을 받는다. 다른 테마에서 받으면 고객이
				// 쓰지도 않을 수백 KB 를 매번 내려받는다.
				if (this.coverKind === 'aerial') await this.loadCoverMap(g);
				const dev = re.data.dev || null;
				this.devPreview = !!(dev && dev.preview);
				// 게이트가 꺼진 뒤에도 담당자가 자기 링크를 열어 본 것은 고객 열람이 아니다. 서버가 로그인 토큰을
				// DB 로 확인해 준 값만 믿는다.
				this.staffPreview = !!re.data.preview;
				document.title = this.report.title || '위온어스 Weekly';
			} catch (err) {
				const d = err && err.response && err.response.data;
				if (d && d.dev) this.devBlocked = true;
				// 응답 자체가 없으면(연결 끊김·시간 초과) 링크 탓이 아니다. 만료 안내를 보이면 고객은 다시 열어 볼 생각을 하지 않는다.
				else if (!(err && err.response)) this.errorMsg = '리포트를 불러오지 못했습니다. 연결 상태를 확인한 뒤 다시 열어 주세요.';
				else this.errorMsg = (d && d.msg) || '링크가 올바르지 않거나 유효기간이 지났습니다.';
			} finally {
				this.loading = false;
				// 인쇄면 먼저 한 장에 다 흘려 두고(=측정 상태) 높이를 잰다.
				this.measuring = this.pdfMode && !this.errorMsg && !this.devBlocked;
				// 사진까지 자리를 잡고 낱장을 나눈 다음에 준비 표시를 올린다.
				// 서버 PDF 가 이 시점을 기다리므로, 나누기 전에 올리면 측정 상태가 그대로 찍힌다.
				this.$nextTick(() => {
					if (!this.alive) return;   // 응답을 기다리는 사이 화면을 떠났다
					Promise.all([this.waitFonts(), this.waitImages(), this.pickCoverPhoto()])
						.then(() => this.layoutSheets())
						.then(() => { this.ready = true; })
						.catch(() => { this.measuring = false; this.ready = true; });
					// 어드민·담당자 미리보기는 고객 열람이 아니다 — 세지 않는다.
					if (!this.errorMsg && !this.devBlocked && !this.devPreview && !this.staffPreview && !this.pdfMode) this.setupTracking();
					// 머리띠·등장 전환·사진 크게 보기. 어드민 미리보기에서도 화면은 고객과 같아야
					// 검수가 되므로 devPreview 를 가리지 않는다(추적만 가린다).
					if (!this.errorMsg && !this.devBlocked && !this.pdfMode) this.setupWeb();
				});
			}
		},

		/**
		 * 웹폰트가 다 뜰 때까지 기다린다. 명조가 늦게 뜨면 대체 글꼴로 잰 높이로 낱장을
		 * 나눈 뒤 글자만 바뀌어, 조판이 통째로 어긋난다. document.fonts 가 없는 환경은
		 * 그냥 통과시킨다. 8초는 사진 대기와 같은 상한이다.
		 */
		waitFonts() {
			if (!document.fonts || !document.fonts.ready) return Promise.resolve();
			return Promise.race([
				document.fonts.ready,
				new Promise((resolve) => setTimeout(resolve, 8000)),
			]).catch(() => undefined);
		},

		/**
		 * 사진이 다 뜬(또는 실패한) 뒤에 resolve. PDF 가 빈 사진으로 찍히는 것을 막는다.
		 * `loading="lazy"` 는 제외한다. 화면 밖 작은 사진은 스크롤 전까지 영영 로드되지
		 * 않아 상한을 통째로 쓰는데, 인쇄에는 그 사진이 그려지지 않으므로 기다릴 이유가 없다.
		 *
		 * **상한(`budget`)은 부르는 쪽이 정한다.** 서버 PDF 는 `#report-ready` 를 20초까지만
		 * 기다린다(`publicReport.js` 의 `PDF_READY_TIMEOUT_MS`). 대기를 여러 바퀴 돌면서
		 * 바퀴마다 8초를 새로 쓰면, 멎은 사진 한 장에 8초씩 쌓여 그 예산을 넘긴다(실측
		 * 2026-09-16: 세 바퀴에 24.3초, PDF 생성이 통째로 실패). 첫 바퀴만 넉넉히 주고
		 * 뒷바퀴는 짧게 준다. 뒷바퀴가 기다리는 것은 **교체된 사진**이고, 그건 이미 같은
		 * 서버에서 받아 온 것이라 실측 1.1~1.9초면 끝난다.
		 */
		waitImages(budget = 8000) {
			const imgs = Array.from(this.$el.querySelectorAll('img')).filter((im) => im.loading !== 'lazy');
			const pending = imgs.filter((im) => !im.complete);
			if (!pending.length) return Promise.resolve();
			return Promise.all(pending.map((im) => new Promise((resolve) => {
				im.addEventListener('load', resolve, { once: true });
				im.addEventListener('error', resolve, { once: true });
				setTimeout(resolve, budget);   // 한 장이 멎어도 리포트 전체가 멈추지 않게
			})));
		},

		/**
		 * 사진이 더 이상 갈아 끼워지지 않을 때까지 기다린다.
		 *
		 * 원본이 유실된 사진은 빈 200 을 주고, 그 실패가 다음 후보를 그 칸에 꽂는다. 그래서
		 * **바퀴 수를 못 박으면 안 된다** — 연달아 세 장이 실패하는 매물에서는 마지막에
		 * 꽂힌 사진을 기다리지 않은 채 `#report-ready` 가 뜬다(Codex 리뷰 지적, 브라우저에서
		 * `complete=false`·`naturalWidth=0` 으로 재현). 남은 사진이 없어질 때까지 돌되
		 * **전체 예산**을 넘기지 않는다. 서버 PDF 가 `#report-ready` 를 20초까지만
		 * 기다리므로(`publicReport.js` 의 `PDF_READY_TIMEOUT_MS`), 바퀴마다 예산을 새로
		 * 주면 멎은 사진 한 장에 그 한도를 넘긴다(실측 2026-09-16: 세 바퀴 24.3초).
		 */
		async settleImages(total = 8000) {
			const until = Date.now() + total;
			// 바퀴 수는 안전 상한일 뿐이다. 끝내는 기준은 '남은 사진 없음' 과 '전체 예산' 이다 — 6 바퀴로 못 박으면 빠르게
			// 실패하는 후보가 연달아 갈아 끼워질 때 예산이 남았는데도 마지막 사진을 기다리지 않았다(Codex 리뷰).
			for (let round = 0; round < 40; round += 1) {
				await this.$nextTick();
				const left = until - Date.now();
				if (left <= 0) break;
				const pending = Array.from(this.$el.querySelectorAll('img'))
					.filter((im) => im.loading !== 'lazy' && !im.complete);
				if (!pending.length) break;
				await this.waitImages(Math.min(2500, left));
			}
			return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
		},

		/** 인쇄 모드면 낱장을 나누고 한 프레임 더 기다린다. 웹은 표지 판만 잰다. */
		layoutSheets() {
			// 항공사진 표지의 번호는 판이 몇 px 로 그려졌는지 알아야 자리를 잡는다.
			this.measureCoverBox();
			if (!this.pdfMode || this.errorMsg || this.devBlocked) return Promise.resolve();
			try {
				this.groups = this.buildSheets();
			} catch (e) {
				// 측정 중 예외가 나도 낱장 구성은 반드시 확정해야 한다. 확정하지 않으면
				// 전 카드가 고정 높이 한 낱장에 남아 통째로 잘린다(Codex 리뷰).
				console.warn(`[weekly] 낱장 측정 실패, 흐름 조판으로 대체합니다: ${e && e.message}`);
				this.groups = [{
					brief: !!this.report.brief, fit: 1, flow: true,
					rows: this.items.map((it, i) => ({ it, no: i + 1 })),
				}];
			}
			this.measuring = false;
			// 낱장을 나눈 뒤에 붙는 사진(두 번째 사진)과, 실패해서 다른 사진으로 갈아
			// 끼운 칸(표지 격자)을 마저 받는다. 여기서 안 기다리면 그 자리가 빈 채로 찍힌다.
			return this.settleImages();
		},

		/**
		 * 매물마다 그 자리의 항공사진.
		 *
		 * 지도는 표지가 아니라 **속장**에 있다(2026-09-15 사용자 지시 — 위성뷰는 개별 매물이나
		 * 보조로 쓴다). 표지는 회사를 보여 주는 자리고, "이 건물이 그 블록의 어디에 서 있는가"
		 * 는 그 매물을 읽는 자리에 있어야 쓸모가 있다.
		 *
		 * 열쇠를 화면에 심지 않으려고 서버가 대신 받아 준다. 그래서 이 요청도 개발중 게이트를
		 * 지나는데 `<img src>` 에는 헤더를 붙일 수 없다 → 어드민 미리보기에서는 인증한 fetch 로
		 * 받아 blob 으로 건다(PDF 내려받기와 같은 방식). 서버 렌더러는 주소로 받은 **지도 전용
		 * 통행증(gm)** 을 쓴다. 그 통행증은 매물 수만큼 쓸 수 있다 — 1회용이던 시절 그대로 두면
		 * 두 번째 매물부터 그림 자리에 개발중 안내가 들어온다.
		 *
		 * 한 장이 실패해도 그 매물만 지도 없이 간다. 지도 하나 때문에 리포트를 막지 않는다.
		 */
		async loadSpots(gPass) {
			const list = this.items
				.map((it, i) => ({ no: i + 1, has: this.hasSpot(it) }))
				.filter((x) => x.has);
			if (!list.length) return;

			// 항공 지면의 속장 위치도는 표지와 같은 위성 사진(라벨 있는 하이브리드)이다. 다른
			// 지면은 일반 지도. 서버가 `kind` 로 판 종류만 바꾸고 좌표·배율은 같다.
			const kind = this.themeId === 'aerial' ? '&kind=sat' : '';
			const base = `${this.apiBase}/pub/api/report/map?t=${encodeURIComponent(this.token)}${kind}`;
			const gm = String(this.$route.query.gm || '');
			if (gm) {
				const urls = {};
				list.forEach((x) => { urls[x.no] = `${base}&no=${x.no}&g=${encodeURIComponent(gm)}`; });
				this.spotUrl = urls;
				return;
			}

			const t = this.$store && this.$store.getters.getToken;
			const headers = t && t.accessToken ? { Authorization: t.accessToken } : undefined;
			if (!headers && !gPass) {                                // 게이트가 꺼진 평시
				const urls = {};
				list.forEach((x) => { urls[x.no] = `${base}&no=${x.no}`; });
				this.spotUrl = urls;
				return;
			}
			// 어드민 미리보기. 한 장씩 인증해 받는다.
			const got = {};
			await Promise.all(list.map(async (x) => {
				try {
					const blob = await this.fetchWithin(`${base}&no=${x.no}`, { headers }, 15000, (res) => (res.ok ? res.blob() : null));
					if (blob) got[x.no] = URL.createObjectURL(blob);
				} catch (e) { /* 그 매물만 지도 없이 간다 */ }
			}));
			this.spotUrl = got;
		},

		/**
		 * 표지의 묶음 항공사진(항공 표지 전용). 개별 매물 판과 같은 라우트를 쓰되 no 를 붙이지
		 * 않으면 서버가 이번 주 물건을 모두 담는 판을 준다. 번호 좌표는 리포트 응답의 map 에
		 * 들어 있다(같은 투영으로 서버가 계산한 값이다 — 화면이 다시 계산하면 두 값이 어긋난다).
		 *
		 * 실패해도 표지는 완성된다 — 판이 없으면 활자만 남는다.
		 */
		async loadCoverMap(gPass) {
			if (this.coverMapUrl || !this.coverMapMeta) return;
			// 항공 지면의 속장 위치도는 표지와 같은 위성 사진(라벨 있는 하이브리드)이다. 다른
			// 지면은 일반 지도. 서버가 `kind` 로 판 종류만 바꾸고 좌표·배율은 같다.
			const kind = this.themeId === 'aerial' ? '&kind=sat' : '';
			const base = `${this.apiBase}/pub/api/report/map?t=${encodeURIComponent(this.token)}${kind}`;
			const gm = String(this.$route.query.gm || '');
			if (gm) { this.coverMapUrl = `${base}&g=${encodeURIComponent(gm)}`; return; }

			const t = this.$store && this.$store.getters.getToken;
			const headers = t && t.accessToken ? { Authorization: t.accessToken } : undefined;
			if (!headers && !gPass) { this.coverMapUrl = base; return; }
			try {
				const blob = await this.fetchWithin(base, { headers }, 15000, (res) => (res.ok ? res.blob() : null));
				if (blob) this.coverMapUrl = URL.createObjectURL(blob);
			} catch (e) { /* 표지는 판 없이 간다 */ }
		},

		/**
		 * 주소에서 동네 이름만. '서울특별시 강남구 논현동 263-4' 에서 '논현동' 을 집는다.
		 *
		 * **세 번째 토막을 집으면 안 된다.** 서울은 '시 구 동 번지' 라 맞지만, 경기도는
		 * '도 시 구 동 번지' 라 구(區)가 잡히고(성남시 분당구 정자동 → '분당구'), 세종은
		 * '시 동 번지' 라 **번지 숫자가 동 이름 자리에 그대로 실린다**(도담동 1 → '1').
		 * 도로명 주소로 폴백하면 길 이름이 잡힌다. 그래서 자리가 아니라 **끝 글자**로 고른다.
		 * 뒤에서부터 고르는 이유는 읍·면·리 주소에서 '청평면' 이 아니라 '대성리' 가 그 동네를
		 * 가리키기 때문이다. 도로명 주소는 괄호 안에 법정동을 적으므로 그쪽을 먼저 본다.
		 */
		dongOf(it) {
			const s = String(this.addrOf(it) || '');
			const hits = (t) => String(t).split(/[\s,]+/).filter((w) => /[동가읍면리]$/.test(w));
			// 도로명주소 괄호 안은 **'법정동, 건물명' 순서**다. 여기서 뒤에서부터 고르면
			// '중앙상가' 처럼 '가' 로 끝나는 건물명이 지역명으로 나간다(Codex 리뷰 지적:
			// `서울특별시 강남구 학동로 123 (논현동, 중앙상가)` → 중앙상가). 괄호 안은 앞에서,
			// 지번주소는 뒤에서 고른다(`서울 강남구 논현동 263-4` 의 동이 뒤에 있다).
			const paren = hits((s.match(/\(([^)]*)\)/) || [])[1] || '')[0] || '';
			const bare = s.replace(/\([^)]*\)/g, ' ');
			const rest = hits(bare);
			const parts = bare.split(/\s+/).filter(Boolean);
			return paren || rest[rest.length - 1] || parts[2] || parts[1] || '';
		},

		/**
		 * 표지 항공사진 위 번호의 자리.
		 *
		 * 서버가 준 좌표는 **판(700x990) 안의 논리 픽셀**이다. 판은 `object-fit: cover` 로
		 * 낱장을 덮으므로, 낱장 비율이 판과 다르면 판의 일부가 잘린다. 잘린 만큼을 빼지
		 * 않고 좌표를 그대로 백분율로 쓰면 번호가 건물에서 밀린다(실측: 화면 표지에서
		 * 번호 다섯 개가 전부 엉뚱한 자리에 섰다). 그래서 여기서 같은 cover 계산을 한다.
		 * 인쇄는 판과 지면 비율이 같아 배율만 곱해지고 잘림이 0 이다.
		 */
		pinStyle(m) {
			const mw = Number(this.coverMapMeta && this.coverMapMeta.w) || 1;
			const mh = Number(this.coverMapMeta && this.coverMapMeta.h) || 1;
			const bw = this.coverBox.w;
			const bh = this.coverBox.h;
			if (!bw || !bh) return { left: `${(m.x / mw) * 100}%`, top: `${(m.y / mh) * 100}%` };
			const s = Math.max(bw / mw, bh / mh);
			const x = (bw - mw * s) / 2 + m.x * s;
			const y = (bh - mh * s) / 2 + m.y * s;
			return { left: `${(x / bw) * 100}%`, top: `${(y / bh) * 100}%` };
		},

		/** 표지 판이 실제로 몇 px 인가. 번호 자리를 cover 로 계산하려면 이 값이 있어야 한다. */
		measureCoverBox() {
			const el = this.$el && this.$el.querySelector('.wr-cover__aerial');
			if (!el) return;
			const r = el.getBoundingClientRect();
			if (!r.width || !r.height) return;
			if (Math.abs(r.width - this.coverBox.w) < 0.5 && Math.abs(r.height - this.coverBox.h) < 0.5) return;
			this.coverBox = { w: r.width, h: r.height };
		},

		/** 듀오톤 필터의 한 채널. 테마가 두 끝점을 정한다. */
		duoVals(pair, i) {
			const p = pair || { d: [0, 0, 0], l: [1, 1, 1] };
			return `${p.d[i]} ${p.l[i]}`;
		},

		// ── 표시 헬퍼 ───────────────────────────────────────────────────────
		/** 그 매물의 좌표가 스냅샷에 있는가. 예전에 발행한 리포트에는 없다(그때는 안 굳혔다). */
		hasSpot(it) {
			const pt = (it.snapshot || {}).pt;
			const x = Number(pt && pt.x);
			const y = Number(pt && pt.y);
			return Number.isFinite(x) && Number.isFinite(y) && !(x === 0 && y === 0);
		},
		/** 위치도 주소. 아직 못 받았거나 실패했으면 빈 값 → 그 자리는 그리지 않는다. */
		spotSrc(no) {
			return this.spotFailed[no] ? '' : (this.spotUrl[no] || '');
		},
		/**
		 * 위치도 아래 한 줄. 동네 이름과 출처. 원본 좌하단의 제공자 로고는 2:1 로 자르면
		 * 판 밖으로 나가므로 출처는 여기서 적는다.
		 */
		spotCaption(it) {
			const parts = String(this.addrOf(it) || '').split(/\s+/).filter(Boolean);
			return parts.slice(1, 3).join(' ') || parts.slice(0, 2).join(' ');
		},
		imgUrl(id) {
			return `${this.apiBase}/users/item/image?id=${id}&w=1200&q=82`;
		},
		/**
		 * 대표 사진 id. 원본이 유실된 사진(R2)은 빈 200 이 와서 실패하므로 **다음 사진으로 넘어간다**. 예전엔 첫 사진
		 * 하나만 보고 실패하면 자리 표시로 굳어, 멀쩡한 사진이 더 있는 매물도 빈 칸으로 나갔다(Codex 리뷰).
		 */
		mainPhotoId(it) {
			const ids = (it && it.snapshot && it.snapshot.img_ids) || [];
			return ids.find((id) => !this.failedPhotoIds[id]) || null;
		},
		mainPhoto(it) {
			const id = this.mainPhotoId(it);
			return id ? this.imgUrl(id) : '';
		},
		/**
		 * 대표·표지 사진 실패. **실패한 그 이미지의 주소에서** 사진 id 를 읽어 기록한다. 지금 후보를 다시 계산해 기록하면,
		 * 갈아 끼운 뒤 늦게 도착한 앞 사진의 오류가 멀쩡한 새 후보까지 지운다(Codex 리뷰).
		 */
		onPhotoError(e) {
			const src = String((e && e.target && e.target.getAttribute && e.target.getAttribute('src')) || '');
			const m = /[?&]id=([^&]+)/.exec(src);
			if (m) this.failedPhotoIds = { ...this.failedPhotoIds, [decodeURIComponent(m[1])]: true };
		},
		/**
		 * 시간 제한이 있는 fetch. 직원 미리보기의 인증 요청에는 axios 의 시간 제한이 걸리지 않는다. 헤더만이 아니라
		 * **본문을 다 받기까지**(read) 한 제한 안에 둔다. AbortController 가 없는 웹뷰에서도 제한 시간이 되면 호출부는
		 * 실패로 끝난다(요청 자체는 브라우저가 알아서 버린다).
		 */
		async fetchWithin(url, opts, ms, read) {
			const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
			let timer = null;
			const expire = new Promise((resolve, reject) => {
				timer = setTimeout(() => { if (ctl) ctl.abort(); reject(new Error(`시간 초과 ${ms}ms`)); }, ms);
			});
			try {
				return await Promise.race([
					fetch(url, { ...(opts || {}), signal: ctl ? ctl.signal : undefined }).then(read),
					expire,
				]);
			} finally {
				clearTimeout(timer);
			}
		},
		/**
		 * 표지 도판으로 쓸 사진을 고른다. 후보는 물건마다 대표 사진 한 장(최대 8장)이고,
		 * 실제 크기를 받아 본 뒤 **큰 가로 사진**을 앞세운다. 첫 물건의 사진이 900px 이상이면
		 * 그대로 둔다(1번 물건이 표지에 서는 편이 자연스럽다). 사진을 못 받으면 후보에서 뺀다.
		 * 전체 2.5초 안에 끝낸다 — 서버 PDF 는 준비 신호를 20초까지만 기다리고, 여기서 받은
		 * 사진은 브라우저 캐시에 남아 표지 img 가 다시 받지 않는다.
		 */
		pickCoverPhoto() {
			if (!['plate', 'grid', 'gallery', 'aerial'].includes(this.coverKind)) return Promise.resolve();
			const cands = this.items.slice(0, 8)
				.map((it) => ((it.snapshot && it.snapshot.img_ids) || [])[0])
				.filter(Boolean);
			if (cands.length < 2) return Promise.resolve();
			const probe = (id) => new Promise((resolve) => {
				const im = new Image();
				im.onload = () => resolve({ id, w: im.naturalWidth, h: im.naturalHeight });
				im.onerror = () => resolve(null);
				im.src = this.imgUrl(id);
			});
			const budget = new Promise((resolve) => setTimeout(() => resolve('timeout'), 2500));
			return Promise.race([Promise.all(cands.map(probe)), budget]).then((res) => {
				if (!Array.isArray(res)) return;
				const ok = res.filter((r) => r && r.w > 0);
				if (!ok.length) return;
				const first = ok.find((r) => r.id === cands[0]);
				if (first && first.w >= 900 && first.w >= first.h) return;
				const score = (r) => r.w * (r.w >= r.h ? 1.3 : 1);
				const best = ok.reduce((a, r) => (score(r) > score(a) ? r : a), ok[0]);
				if (first && score(best) <= score(first)) return;
				this.coverPhotoId = best.id;
			});
		},
		addrOf(it) {
			const s = it.snapshot || {};
			return normalizeAddr(s.jibun_addr || s.road_addr || '');
		},
		won(v) {
			return formatMoneyMuch(v);
		},
		py(v) {
			const n = Number(v);
			return Number.isFinite(n) ? `${n.toLocaleString()}평` : '';
		},
		ymd(v) {
			return String(v || '').slice(0, 10);
		},
		/** 준공 표기. 도록에서는 연월이면 충분하다. */
		ym(v) {
			const s = String(v || '').slice(0, 7);
			return s.length === 7 ? s.replace('-', '.') : s;
		},
		pad(n) {
			return String(n).padStart(2, '0');
		},
		/**
		 * 문서에서 가장 중요한 수치. 단위가 스스로 설명하는 값은 라벨을 떼고 값만 쓴다
		 * ('매매가 151억' 이 아니라 '151억'). DESIGN.md Typography 참조.
		 */
		priceText(it) {
			const s = it.snapshot || {};
			return s.sell_price ? this.won(s.sell_price) : '';
		},
		/** 억 단위 한 자리 소수까지만(151.5억). 1억 미만은 만원 그대로. 표지 목록용. */
		priceShort(it) {
			const n = Number((it.snapshot || {}).sell_price);
			if (!Number.isFinite(n) || n <= 0) return '';
			if (n < 10000) return `${n.toLocaleString()}만원`;
			const eok = Math.round(n / 1000) / 10;
			return `${eok % 1 ? eok.toFixed(1) : eok.toFixed(0)}억`;
		},
		rateText(it) {
			const s = it.snapshot || {};
			return s.earning_rate ? `수익률 ${s.earning_rate}%` : '';
		},
		/**
		 * 제원. 라벨과 값을 한 쌍씩 돌려준다(화면에서 세로로 줄을 맞춘다).
		 * 값이 없는 항목은 줄 자체를 만들지 않는다 — 인쇄물에서 빈칸은 실수로 읽힌다.
		 * 보증금과 월임대는 늘 같이 읽는 값이라 한 줄로 묶는다.
		 */
		/**
		 * 제원. **항목이 없어도 자리를 비우지 않고 줄표를 세운다.** 있는 것만 담으면 같은
		 * 항목이 매물마다 다른 줄에 서서, 다섯 건을 나란히 놓고 비교할 수 없다(경매 도록의
		 * 물건 명세표가 빈 칸도 자리를 지키는 이유와 같다). 임대 조건만 예외로, 값이 있을
		 * 때만 마지막에 한 줄 붙는다 — 매매 물건에는 아예 없는 개념이라 줄표가 어색하다.
		 */
		specRows(it) {
			const s = it.snapshot || {};
			const DASH = '\u2013';
			const out = [
				{ k: '대지', v: this.py(s.land_size_p) || DASH },
				{ k: '연면적', v: this.py(s.total_size_p) || DASH },
				{ k: '층수', v: this.floors(it) || DASH },
				{ k: '용도', v: s.main_purpose ? String(s.main_purpose) : DASH },
				{ k: '준공', v: s.build_date ? this.ym(s.build_date) : DASH },
				{ k: '주차', v: s.park_cnt_real ? `${s.park_cnt_real}대` : DASH },
			];
			const dep = s.ex_sum_rent_deposit ? this.won(s.ex_sum_rent_deposit) : '';
			const rent = s.ex_sum_rent_money ? this.won(s.ex_sum_rent_money) : '';
			if (dep && rent) out.push({ k: '보증금/월임대', v: `${dep} / ${rent}`, wide: true });
			else if (dep) out.push({ k: '보증금', v: dep, wide: true });
			else if (rent) out.push({ k: '월임대', v: rent, wide: true });
			return out;
		},
		hasFeatures(it) {
			const f = it.snapshot && it.snapshot.feature_lines;
			return Array.isArray(f) && f.length > 0;
		},
		floors(it) {
			const s = it.snapshot || {};
			// 지하 1층을 -1 로 적은 옛 스냅샷이 있다('지하 -1층' 으로 나갔다). 음수도 그 층수로 읽는다.
			const b = Math.abs(Number(s.floor_cnt_B)) || 0;
			const f = Math.abs(Number(s.floor_cnt_F)) || 0;
			if (!b && !f) return '';
			return b ? `지하 ${b}층 / 지상 ${f}층` : `지상 ${f}층`;
		},
		/** 표지 목차의 오른쪽 칸. 구·동까지만 남긴다(번지는 속장에 있다). */
		area(it) {
			const parts = String(this.addrOf(it) || '').split(/\s+/).filter(Boolean);
			return parts.slice(0, 3).join(' ');
		},
		/** 표지 캡션용. 칸이 좁아 시/도는 떼고 구·동만 쓴다. */
		areaShort(it) {
			const parts = String(this.addrOf(it) || '').split(/\s+/).filter(Boolean);
			return parts.slice(1, 3).join(' ') || parts.slice(0, 2).join(' ');
		},
		/**
		 * 한 건이 지면보다 큰 예외에서만 쓰는 축소. 잘라내는 대신 줄여서 담는다.
		 * 폭·높이를 되돌려 놓지 않으면 scale 이 뒤쪽 여백까지 같이 줄여 버린다.
		 */
		/** 낱장 머리에 적을 그 면의 로트 번호. 여러 건이면 범위로 줄인다. */
		/** 흐름 낱장을 실제 표로 감쌀 때인가(인쇄, 조판 확정 뒤). */
		flowTab(sh) {
			return this.pdfMode && !this.measuring && !!(sh && sh.flow);
		},
		runLabel(sh) {
			const rows = (sh && sh.rows) || [];
			if (!rows.length) return '위클리 리포트';
			const a = this.pad(rows[0].no);
			const b = this.pad(rows[rows.length - 1].no);
			return a === b ? `물건 ${a}` : `물건 ${a} ~ ${b}`;
		},
		/**
		 * 낱장 안쪽의 조판 값. 축소가 걸린 낱장이면 축소가 이기고(자리가 모자란 상황이라
		 * 간격을 벌릴 여지가 없다), 아니면 측정 때 계산한 블록 간격을 쓴다.
		 */
		innerStyle(sh) {
			if (sh.fit < 1) return this.fitStyle(sh.fit, sh.avail);
			return sh.gap ? { rowGap: sh.gap } : null;
		},
		/**
		 * 축소 낱장의 안쪽 상자. 배율 f 로 줄이는 대신 폭·높이를 1/f 로 키워 눈에 보이는
		 * 크기가 가용 지면과 같아지게 한다.
		 *
		 * 높이는 백분율이 아니라 **잰 가용 높이(avail)/f 픽셀**이다. `100/f%` 로 두면 낱장
		 * 상자의 내용 높이(머리·꼬리말 포함)를 기준으로 풀린 뒤 flex-shrink 가 다시 가용
		 * 높이로 눌러 버려, 안쪽은 늘 잰 높이 그대로이고 내용만 밖으로 넘친다(실측: 안쪽
		 * 952px 에 내용 1125px). 그러면 마지막 카드의 grow 가 받을 자리가 없어 지면 아래가
		 * 8mm 빈다. 음수 아래 마진이 늘어난 만큼(avail/f - avail)을 흐름에서 되돌려
		 * 꼬리말이 제자리에 남는다 — 변형(transform)은 레이아웃을 바꾸지 않기 때문이다.
		 */
		fitStyle(f, avail) {
			const st = {
				transform: `scale(${f})`,
				transformOrigin: 'top left',
				width: `${(100 / f).toFixed(3)}%`,
				height: `${(100 / f).toFixed(3)}%`,
				// 왼쪽 사진의 재단 여백(-14mm)은 축소와 함께 줄어든다. 낱장 여백은 축소 밖에
				// 있어 그대로 14mm 라, 이 값을 모르면 사진이 재단선에서 2~3mm 떨어진다(실측).
				'--wr-fit': f,
			};
			if (avail > 0) {
				const h = avail / f;
				st.height = `${h.toFixed(2)}px`;
				st.marginBottom = `${(avail - h).toFixed(2)}px`;
				st.flex = '0 0 auto';
			}
			return st;
		},

		/**
		 * 인쇄용 낱장 나누기. 한 장에 전부 흘려 둔 상태에서 카드의 **실제 높이**를 재고,
		 * 낱장 안쪽의 **실제 가용 높이**로 나눈다. 둘 다 재는 값이라 글꼴·문장 길이·사진
		 * 유무가 달라져도 따라간다. 측정에 실패하면(선택자 변경 등) 한 장에 두 건이라는
		 * 안전한 기본값으로 물러난다. 절대 잘라내지 않는다.
		 */
		buildSheets() {
			const MM_PX = 3.7795275591;          // 96dpi 기준 1mm
			const rows = this.items.map((it, i) => ({ it, no: i + 1 }));
			const hasBrief = !!this.report.brief;
			const hasIndex = this.items.length > 1;   // 한 건짜리 리포트에 목차는 군더더기다
			// 측정이 안 되면 **한 장에 몇 건** 을 추측하지 않는다. 추측한 수가 지면을 넘으면
			// 그대로 잘려 나가기 때문이다(Codex 리뷰). 지면 고정을 풀어 브라우저가 흘리게 하면
			// 모양은 단순해져도 내용은 한 줄도 잃지 않는다.
			const fallback = () => [{ brief: hasBrief, index: hasIndex, rows, fit: 1, flow: true }];

			const inner = this.$el.querySelector('.wr-sheet--body .wr-sheet__inner');
			if (!inner) return fallback();
			const avail = inner.clientHeight;
			const gap = parseFloat(window.getComputedStyle(inner).rowGap) || 0;
			const cards = Array.from(inner.querySelectorAll('.wr-lot[data-building-uid]'));
			// 측정값이 말이 되는지 먼저 본다. 0 이 나오는데 그대로 나누면 물건 하나에
			// 한 장씩 배정되어 조용히 스무 장짜리 PDF 가 된다.
			if (!avail || cards.length !== rows.length) return fallback();
			const heights = cards.map((el) => el.getBoundingClientRect().height);
			if (heights.some((h) => !h)) return fallback();
			const briefEl = inner.querySelector('.wr-brief');
			const briefH = briefEl ? briefEl.getBoundingClientRect().height : 0;
			const indexEl = inner.querySelector('.wr-index');
			const indexH = indexEl ? indexEl.getBoundingClientRect().height + gap : 0;

			// 무엇을 재서 이렇게 나눴는지 남긴다. 낱장 수가 이상할 때 이 값이 없으면
			// 원인이 측정인지 조판인지 구분할 방법이 없다(PDF 검증 스크립트가 읽는다).
			window.__wrLayout = { avail, gap, briefH, indexH, heights };

			const out = [];
			let cur = {
				brief: hasBrief,
				index: hasIndex,
				rows: [],
				fit: 1,
				used: (hasBrief ? briefH : 0) + (hasIndex ? indexH : 0),
			};
			for (let i = 0; i < rows.length; i++) {
				const h = heights[i];
				const filled = cur.rows.length || cur.brief || cur.index;
				const need = (filled ? gap : 0) + h;
				// **머리 블록(색인·brief)만 있는 낱장은 물건을 하나 더 안고 줄여서 담는다.**
				// 카드가 지면의 2/3 를 쓰게 되면서, 색인 62mm + brief 30mm 위에 카드가 얹히지
				// 못해 첫 낱장이 목록만으로 채워졌다(실측 2026-09-16: 7쪽 중 2쪽의 2/3 가 빈
				// 종이였다). 이 낱장만 MIN_FIT 까지 줄이면 들어간다(실측 필요 배율 0.93).
				const headOnly = !cur.rows.length && (cur.brief || cur.index);
				const room = headOnly ? avail / MIN_FIT : avail;
				if (filled && (cur.used + need > room || cur.rows.length >= MAX_CARDS_PER_SHEET)) {
					out.push(cur);
					cur = { brief: false, index: false, rows: [], fit: 1, used: 0 };
					cur.used = h;
				} else {
					cur.used += need;
				}
				cur.rows.push(rows[i]);
			}
			if (cur.rows.length || cur.brief || cur.index) out.push(cur);

			// 남는 자리를 어떻게 쓰나. **카드가 먼저 받고, 그다음이 블록 사이 간격이고,
			// 마지막이 여백이다.**
			//
			// **사진 띠로 받지 않는다.** 예전에는 남는 자리를 그 낱장 물건의 다른 사진이
			// 받았는데, 뽑히는 사진이 실내 클로즈업이라 무엇이 찍혔는지도 모를 그림이 지면의
			// 1/4 을 차지했다(실측: 화장실 거울 사진이 그 장에서 가장 큰 그림이었다.
			// 2026-09-16 사용자 지적 "그냥 사진 몇장 붙여놓고 딱 됐습니다 그런게 아니라고").
			// 도록에서 지면 아래가 남으면 여백이 오지 사진이 오지 않는다 — 그 경로는 지웠다.
			const GAP_MAX = 20 * MM_PX;
			// 카드가 자랄 수 있는 몫. 시안 04 의 물건 면은 사진이 지면 높이의 대부분을 쓰고
			// 글 단 바닥에 위치도가 붙는다. 카드 자연 높이가 143~179mm 이고 가용 지면이
			// 252mm 라(실측 2026-09-16), 상한이 40mm 면 한 건짜리 낱장에서 40~70mm 가 바닥에
			// 남아 지면 아래가 통째로 빈다. 92mm 면 카드가 지면을 채우고, 자라는 곳은 왼쪽
			// 사진이라 82mm 폭의 세로로 긴 건물 사진이 된다 — 시안의 비율이 정확히 그것이다.
			// 2026-09-17 "여백 없이 딱 맞아 떨어지게" — 한 건짜리 낱장은 카드가 지면을 끝까지 쓴다.
			// 자란 몫은 오른쪽 단 아래의 위치도가 받고(격자 1fr 행), 왼쪽 사진은 카드 높이를 따라간다.
			const CARD_MAX_ADD = 200 * MM_PX;
			for (const g of out) {
				const blocks = g.rows.length + (g.brief ? 1 : 0) + (g.index ? 1 : 0);
				const spare = avail - g.used;
				g.gap = null;
				if (spare <= 0) continue;

				// 1) 카드가 먼저 조금 자란다. 자라는 곳은 왼쪽 띠의 **사진**이다(글은 위에
				//    정렬된 채라 아래가 빈다) — 그래서 상한을 둔다. 상한 없이 늘리면 사진이
				//    62mm 폭에 세로로만 길어져 극단적으로 잘린다(실측 80x150mm).
				let rest = spare;
				// 블록이 카드 하나뿐이면 나눠 줄 간격이 없다 — 전부 카드가 받는다(안 그러면 지면
				// 아래 26mm 가 늘 빈다, 실측 2026-09-17). 둘 이상이면 8할만 받고 나머지는 간격.
				const share = blocks > 1 ? 0.8 : 1;
				const addCard = Math.min((spare * share) / g.rows.length, CARD_MAX_ADD);
				if (g.rows.length && addCard > 2) {
					// rows 는 목록 순서 그대로고 no 는 1 부터다 — 잰 높이를 그 자리에서 찾는다.
					g.rows = g.rows.map((r) => ({ ...r, h: `${(heights[r.no - 1] + addCard).toFixed(2)}px` }));
					rest = spare - addCard * g.rows.length;
				}

				// 2) 남은 몫은 블록 사이 간격이 받는다. 상한을 두는 이유는 두 블록이 그보다
				//    벌어지면 한 문서가 아니라 따로 놀기 시작해서다. 그러고도 남으면 여백이다.
				if (blocks > 1 && rest > 0) {
					const add = Math.min(rest / (blocks - 1), GAP_MAX - gap);
					if (add > 0) g.gap = `${(gap + add).toFixed(2)}px`;
				}
			}

			// 한 건만으로도 지면을 넘는 경우(특징이 사내 메모에서 통째로 넘어와 30줄인 물건 등).
			// 조금 넘치면 그 낱장만 줄여 담고, 줄여도 안 들어가면 **낱장 고정을 풀어** 브라우저가
			// 여러 장에 걸쳐 흘리게 한다. 어느 쪽이든 문장을 잘라내지는 않는다.
			// (실측: 특징 30줄 + 긴 코멘트를 0.8 로 줄여도 13번째 줄부터 잘려 나갔다.)
			for (const g of out) {
				if (avail > 0 && g.used > avail) {
					const need = avail / g.used;
					if (need >= MIN_FIT) {
						g.fit = need;
						g.avail = avail;   // innerStyle 이 안쪽 높이를 픽셀로 잡을 때 쓴다
					} else {
						g.flow = true;   // 지면 고정 해제. 쪽수는 브라우저가 정한다
					}
				}
			}
			return out;
		},

		// ── 열람 추적 ───────────────────────────────────────────────────────
		setupTracking() {
			this.bumpActivity = this.bumpActivity.bind(this);
			this.onVisibility = this.onVisibility.bind(this);
			this.onLeave = this.onLeave.bind(this);

			for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll']) {
				window.addEventListener(ev, this.bumpActivity, { passive: true });
			}
			document.addEventListener('visibilitychange', this.onVisibility);
			window.addEventListener('pagehide', this.onLeave);

			// 카드는 낱장 안에 중첩돼 있다. ref 배열 대신 DOM 에서 직접 걷어야
			// 낱장 구조가 바뀌어도 추적이 조용히 비지 않는다.
			this.cardEls = Array.from(this.$el.querySelectorAll('.wr-lot[data-building-uid]'));

			this.tickTimer = setInterval(this.tick, TICK_MS);
			this.flushTimer = setInterval(() => this.flush(false), FLUSH_MS);
			this.flush(false);   // 최초 열람을 바로 남긴다(첫 열람일시)
		},

		teardownTracking() {
			for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll']) {
				window.removeEventListener(ev, this.bumpActivity);
			}
			document.removeEventListener('visibilitychange', this.onVisibility);
			window.removeEventListener('pagehide', this.onLeave);
			if (this.tickTimer) clearInterval(this.tickTimer);
			if (this.flushTimer) clearInterval(this.flushTimer);
			this.tickTimer = this.flushTimer = null;
			this.cardEls = [];
		},

		bumpActivity() {
			this.lastActiveAt = Date.now();
		},
		onVisibility() {
			this.lastTickAt = Date.now();   // 숨김·보임 경계에서 체류 구간을 새로 시작한다
			if (document.visibilityState === 'visible') this.bumpActivity();
			else this.flush(true);   // 탭을 떠날 때 모아 둔 값을 흘리지 않고 보낸다
		},
		onLeave() {
			this.flush(true);
		},

		/** 지금 이 순간 "실제로 보고 있는가". 이 판정이 체류 수치의 의미를 결정한다. */
		isWatching() {
			return document.visibilityState === 'visible'
				&& (Date.now() - this.lastActiveAt) < IDLE_MS;
		},

		/**
		 * 지금 화면에 보이는 매물 번호. 1초마다 직접 잰다.
		 *
		 * IntersectionObserver 를 쓰지 않는 이유: 콜백이 **임계값을 지날 때만** 온다.
		 * 화면보다 두 배 넘게 긴 카드는 아무리 스크롤해도 교차 비율이 다음 임계값을 넘지
		 * 못해 콜백이 오지 않고, 그 매물의 체류가 영원히 0 으로 남는다(Codex 리뷰 실측:
		 * 화면 600px, 카드 1200px 에서 비율 0.2 → 0.275). 카드는 6건 이하라 매초 재도 싸다.
		 */
		visibleNow() {
			const vh = window.innerHeight || 0;
			const out = [];
			if (!vh) return out;
			for (const el of this.cardEls) {
				const r = el.getBoundingClientRect();
				const shown = Math.min(r.bottom, vh) - Math.max(r.top, 0);
				if (shown <= 0) continue;
				// "보고 있다" = 카드의 절반 이상이 보이거나, 화면의 절반 이상을 카드가 채우고 있거나.
				if (shown >= r.height * VISIBLE_RATIO || shown >= vh * VISIBLE_RATIO) {
					const id = Number(el.getAttribute('data-building-uid'));
					if (id) out.push(id);
				}
			}
			return out;
		},

		tick() {
			// 고정 1초가 아니라 **직전 tick 이후 실제로 흐른 시간**을 더한다. 화면이 무거워 타이머가 늦게 돌면 1초씩만
			// 더해 실제보다 적게 셌다(Codex 리뷰). 다만 한 번에 2틱을 넘기지 않는다 — 숨김에서 돌아온 직후나 기기가
			// 잠들었다 깬 직후의 긴 공백은 '보고 있던 시간' 이 아니다(그 경계에서 lastTickAt 을 새로 잡는다).
			const now = Date.now();
			const dt = this.lastTickAt ? Math.min(Math.max(0, now - this.lastTickAt), TICK_MS * 2) : TICK_MS;
			this.lastTickAt = now;
			if (!this.isWatching()) return;
			this.pendingActiveMs += dt;
			// 사진을 크게 보는 동안에는 그 매물만 본 것이다. 뒤에 깔린 카드에 시간을 주면 가려진 매물의 관심도가
			// 올라간다(Codex 리뷰).
			const ids = this.lb && this.lb.open ? [Number(this.lb.uid)].filter(Boolean) : this.visibleNow();
			for (const id of ids) {
				const slot = this.pendingItems[id] || (this.pendingItems[id] = { visibleMs: 0, photoCnt: 0 });
				slot.visibleMs += dt;
			}
		},

		onPhoto(it) {
			if (!this.mainPhotoId(it)) return;
			this.openPhotos(it, this.shownIdx(it));
		},
		/** 화면에 보이는 대표 사진(첫 사진이 유실됐으면 대신 들어간 사진)의 원래 목록 번호. 0 번으로 열면 실패한 사진이 크게 뜬다. */
		shownIdx(it) {
			const id = this.mainPhotoId(it);
			const ids = (it.snapshot && it.snapshot.img_ids) || [];
			return id ? Math.max(0, ids.indexOf(id)) : 0;
		},

		// ── 웹 전용 장치 ────────────────────────────────────────────────────
		// 머리띠·등장 전환·사진 크게 보기. 인쇄에서는 setupWeb 자체를 부르지 않는다.

		setupWeb() {
			try {
				this.reduceMotion = !!(window.matchMedia
					&& window.matchMedia('(prefers-reduced-motion: reduce)').matches);
			} catch (e) { this.reduceMotion = false; }

			this.onScrollWeb = this.onScrollWeb.bind(this);
			this.onKeyWeb = this.onKeyWeb.bind(this);
			window.addEventListener('scroll', this.onScrollWeb, { passive: true });
			window.addEventListener('resize', this.onScrollWeb, { passive: true });
			document.addEventListener('keydown', this.onKeyWeb);
			this.onScrollWeb();

			// 등장 전환은 **관찰을 실제로 걸 수 있을 때만** 켠다. 먼저 숨겨 놓고 켜면,
			// 관찰이 실패한 브라우저에서 본문이 투명한 채로 남는다(고객은 빈 화면을 본다).
			if (this.reduceMotion || typeof window.IntersectionObserver !== 'function') return;
			const els = Array.from(this.$el.querySelectorAll('.wr-lot'));
			if (!els.length) return;

			// 첫 화면에 이미 보이는 카드는 전환 없이 그대로 둔다. 페이지를 여는 순간
			// 전부 한 번씩 밀려 올라오면 등장이 아니라 지연으로 읽힌다.
			const vh = window.innerHeight || 0;
			const below = els.filter((el) => el.getBoundingClientRect().top > vh * 0.9);
			for (const el of els) if (below.indexOf(el) < 0) el.classList.add('is-in');
			if (!below.length) return;

			this.revealIo = new window.IntersectionObserver((entries, io) => {
				for (const e of entries) {
					if (!e.isIntersecting) continue;
					e.target.classList.add('is-in');
					io.unobserve(e.target);
				}
			}, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
			this.anim = true;
			this.$nextTick(() => {
				for (const el of below) this.revealIo.observe(el);
			});
			// 안전장치. 어떤 이유로든 콜백이 오지 않으면 전부 보이는 상태로 되돌린다.
			this.revealGuard = setTimeout(() => {
				for (const el of below) el.classList.add('is-in');
			}, 6000);
		},

		teardownWeb() {
			if (this.onScrollWeb) {
				window.removeEventListener('scroll', this.onScrollWeb);
				window.removeEventListener('resize', this.onScrollWeb);
			}
			if (this.onKeyWeb) document.removeEventListener('keydown', this.onKeyWeb);
			if (this.revealIo) this.revealIo.disconnect();
			if (this.revealGuard) clearTimeout(this.revealGuard);
			if (this.scrollRaf) window.cancelAnimationFrame(this.scrollRaf);
			this.revealIo = null;
			this.revealGuard = null;
			this.scrollRaf = 0;
			this.unlockScroll();
		},

		/** 머리띠 노출과 '지금 읽는 물건'. 스크롤마다 재지만 한 프레임에 한 번으로 묶는다. */
		onScrollWeb() {
			if (this.scrollRaf) return;
			this.scrollRaf = window.requestAnimationFrame(() => {
				this.scrollRaf = 0;
				const cover = this.$el.querySelector('.wr-sheet--cover');
				this.barOn = !!cover && cover.getBoundingClientRect().bottom < 8;
				// 창 폭이 바뀌면 표지 판이 잘리는 정도가 달라진다. 번호를 따라 옮긴다.
				this.measureCoverBox();

				// 읽고 있는 물건 = 화면 위쪽 1/3 선을 지난 마지막 카드. 카드가 화면보다 길어도
				// 맞는다(교차 비율로 판정하면 긴 카드에서 콜백이 오지 않는다 — tick() 주석 참고).
				const line = (window.innerHeight || 0) * 0.34;
				let cur = 0;
				for (let i = 1; i <= this.items.length; i++) {
					const el = document.getElementById(`wr-lot-${i}`);
					if (!el) continue;
					const r = el.getBoundingClientRect();
					if (r.top <= line && r.bottom > 0) cur = i;
				}
				this.activeLot = cur;
			});
		},

		/** 목차·머리띠에서 물건으로 건너뛴다. 감속 설정이면 부드러운 이동을 쓰지 않는다. */
		goLot(no) {
			const el = document.getElementById(`wr-lot-${no}`);
			if (!el) return;
			this.bumpActivity();
			try {
				el.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
			} catch (e) {
				el.scrollIntoView();   // 옵션 객체를 모르는 오래된 브라우저
			}
		},

		// ── 사진 ────────────────────────────────────────────────────────────
		photoCount(it) {
			const ids = (it.snapshot && it.snapshot.img_ids) || [];
			return ids.length;
		},

		/**
		 * 대표 사진 아래 작은 사진들. 대표(0번)는 이미 크게 떠 있으므로 1번부터 고른다.
		 * 마지막 칸에 남은 장수를 얹어 "여기서 끝이 아니다" 를 화면에 남긴다.
		 */
		/**
		 * 사진 격자(mosaic) 지면의 보조 사진 두 장. 대표(0번)를 뺀 목록을 세 토막으로 끊어
		 * 토막마다 한 장씩 — 1·2번을 차례로 집으면 같은 장면의 다른 크기 벌이 나란히 실린다
		 * (표지 격자와 같은 이유). 실패한 사진은 다음 후보로 갈아 끼운다.
		 */
		gridPhotos(it) {
			const all = (it.snapshot && it.snapshot.img_ids) || [];
			const ids = all.filter((id) => !this.gridFailed[id]);
			if (ids.length < 2) return [];
			// 목록 앞쪽에서 고른다(표지 격자와 같은 간격, n/6). 목록 한가운데는 주차램프·
			// 엘리베이터 같은 실내 근접 사진이라(실측) 격자 칸이 무엇을 찍었는지 모를 그림이 된다.
			const step = Math.max(1, Math.floor(ids.length / 6));
			const out = [];
			[step, step * 2].forEach((at) => {
				const id = ids[Math.min(ids.length - 1, at)];
				if (!id || out.some((o) => o.id === id)) return;
				// 확대창은 원래 목록(img_ids)으로 열리므로 번호도 원래 목록 기준이다. 실패를 거른 목록의 번호를 주면 앞의
				// 사진 하나가 실패한 매물에서 옆 사진이 열린다(Codex 리뷰).
				out.push({ id, idx: all.indexOf(id), url: `${this.apiBase}/users/item/image?id=${id}&w=640&q=78` });
			});
			return out;
		},
		thumbs(it) {
			const ids = (it.snapshot && it.snapshot.img_ids) || [];
			if (ids.length < 2) return [];
			const MAX = 5;
			const out = [];
			for (let i = 1; i < ids.length && out.length < MAX; i++) {
				if (this.thumbFailed[ids[i]]) continue;
				out.push({
					id: ids[i],
					idx: i,
					url: `${this.apiBase}/users/item/image?id=${ids[i]}&w=320&q=72`,
					more: 0,
				});
			}
			if (!out.length) return out;
			const rest = ids.length - 1 - out.length;
			if (rest > 0) out[out.length - 1].more = rest;
			return out;
		},

		/**
		 * 사진 크게 보기. 예전에는 새 탭으로 원본을 열었는데, 카톡 인앱 브라우저에서는 그
		 * 탭이 리포트를 덮어 고객이 돌아오지 못했다. 열람 횟수는 **열 때 한 번만** 센다
		 * (넘겨 보는 것은 같은 열람이다).
		 */
		openPhotos(it, idx) {
			const ids = (it.snapshot && it.snapshot.img_ids) || [];
			if (!ids.length) return;
			const uid = Number(it.building_uid);
			const slot = this.pendingItems[uid] || (this.pendingItems[uid] = { visibleMs: 0, photoCnt: 0 });
			slot.photoCnt += 1;
			this.bumpActivity();
			this.lbFailed = false;
			this.lbDir = 1;
			this.lb = {
				open: true,
				ids,
				idx: Math.max(0, Math.min(Number(idx) || 0, ids.length - 1)),
				name: (it.snapshot && it.snapshot.building_name) || '매물',
				uid,
			};
			this.lockScroll();
			this.preloadLb();
			this.lbOpener = document.activeElement;
			this.$nextTick(() => {
				const x = this.$el.querySelector('.wr-lb__x');
				if (x) x.focus();
			});
		},

		closeLb() {
			this.lb = { open: false, ids: [], idx: 0, name: '', uid: 0 };
			this.lbFailed = false;
			this.unlockScroll();
			const back = this.lbOpener;
			this.lbOpener = null;
			if (back && typeof back.focus === 'function') {
				this.$nextTick(() => { try { back.focus(); } catch (e) { /* 사라진 요소 */ } });
			}
		},

		stepLb(d) {
			const n = this.lb.ids.length;
			if (!n) return;
			this.lbDir = d < 0 ? -1 : 1;
			this.lb.idx = (this.lb.idx + d + n) % n;
			this.lbFailed = false;
			this.bumpActivity();
			this.preloadLb();
		},

		/**
		 * 원본이 사라진 사진(R2 유실)은 **넘기던 방향 그대로 건너뛴다.** 실제로 한 물건의
		 * 사진 절반이 비어 있는 경우가 있어서(운영 r2-missing 목록), 그대로 두면 고객이
		 * 화살표를 누를 때마다 '불러오지 못했습니다' 를 본다. 한 바퀴를 다 돌아도 남는 것이
		 * 없을 때만 안내를 띄운다(전부 유실된 물건에서 무한히 돌지 않게 한다).
		 */
		onLbError() {
			const ids = this.lb.ids;
			const n = ids.length;
			const cur = ids[this.lb.idx];
			if (cur) this.thumbFailed[cur] = true;
			const dir = this.lbDir < 0 ? -1 : 1;
			for (let k = 1; k < n; k++) {
				const j = ((this.lb.idx + dir * k) % n + n) % n;
				if (!this.thumbFailed[ids[j]]) {
					this.lb.idx = j;
					this.lbFailed = false;
					return;
				}
			}
			this.lbFailed = true;
		},

		/** 앞뒤 한 장씩 미리 받아 둔다. 넘길 때 흰 칸이 스치는 것을 막는다. */
		preloadLb() {
			const n = this.lb.ids.length;
			if (n < 2) return;
			for (const d of [1, -1]) {
				const id = this.lb.ids[(this.lb.idx + d + n) % n];
				if (!id || this.thumbFailed[id]) continue;
				const im = new window.Image();
				im.src = `${this.apiBase}/users/item/image?id=${id}&w=1600&q=84`;
			}
		},

		onKeyWeb(e) {
			if (!this.lb.open) return;
			if (e.key === 'Escape') { this.closeLb(); return; }
			if (e.key === 'ArrowLeft') { e.preventDefault(); this.stepLb(-1); }
			else if (e.key === 'ArrowRight') { e.preventDefault(); this.stepLb(1); }
		},

		lockScroll() {
			// 이미 잠겨 있으면 다시 잠그지 않는다 — 두 번 잠그면 원래 값 대신 'hidden' 을 기억해 닫아도 풀리지 않는다.
			if (this.scrollLocked !== undefined) return;
			try {
				this.scrollLocked = document.body.style.overflow;
				document.body.style.overflow = 'hidden';
			} catch (e) { /* 잠그지 못해도 사진은 보인다 */ }
		},
		unlockScroll() {
			try {
				if (this.scrollLocked !== undefined) document.body.style.overflow = this.scrollLocked || '';
				this.scrollLocked = undefined;
			} catch (e) { /* 무시 */ }
		},

		/**
		 * 모아 둔 값을 보낸다.
		 * @param {boolean} leaving 탭을 떠나는 중이면 sendBeacon 을 쓴다. 이때 fetch/axios 는
		 *   브라우저가 요청을 취소해 마지막 체류가 통째로 사라진다.
		 */
		flush(leaving) {
			// 어드민 미리보기에서는 추적을 걸지 않으므로 보낼 것도 없다. 그런데 'PDF로 저장' 이
			// flush() 를 직접 부르기 때문에, 여기서 막지 않으면 최초 전송 조건(sentOnce=false)에
			// 걸려 빈 비콘이 한 번 나간다(서버 게이트가 버리긴 하지만 보낼 이유가 없다).
			if (this.devPreview || this.staffPreview) return;

			const items = Object.keys(this.pendingItems).map((k) => ({
				buildingUid: Number(k),
				visibleMs: this.pendingItems[k].visibleMs,
				photoCnt: this.pendingItems[k].photoCnt,
			})).filter((x) => x.visibleMs > 0 || x.photoCnt > 0);

			const first = !this.sentOnce;
			if (!first && !this.pendingActiveMs && !items.length) return;   // 보낼 것이 없으면 조용히 넘어간다

			const payload = {
				t: this.token,
				sessionKey: this.sessionKey,
				activeMs: this.pendingActiveMs,
				items,
			};
			const snapshotItems = this.pendingItems;
			this.pendingActiveMs = 0;
			this.pendingItems = {};
			this.sentOnce = true;
			const wasSentOnce = !first;   // 실패하면 되돌린다(최초 열람 기록이 통째로 사라진다)

			// 전송이 실패하면 값을 되돌려 다음 전송에 싣는다. 먼저 비우고 실패를 무시하면 잠깐의
			// 네트워크 끊김에 그 구간의 체류가 통째로 사라진다(Codex 리뷰). 서버가 실제 경과시간으로
			// 상한을 두므로 되돌려 보내도 부풀지 않는다.
			const restore = () => {
				// 최초 전송이 실패했는데 sentOnce 만 남으면, 누적값이 0 인 동안에는 다시 보낼
				// 조건이 서지 않아 '첫 열람' 이 영영 기록되지 않는다(Codex 리뷰).
				this.sentOnce = wasSentOnce;
				this.pendingActiveMs += payload.activeMs;
				for (const k of Object.keys(snapshotItems)) {
					const slot = this.pendingItems[k] || (this.pendingItems[k] = { visibleMs: 0, photoCnt: 0 });
					slot.visibleMs += snapshotItems[k].visibleMs;
					slot.photoCnt += snapshotItems[k].photoCnt;
				}
			};

			const url = `${this.apiBase}/pub/api/report/track`;
			try {
				if (leaving && navigator.sendBeacon) {
					// type 은 반드시 CORS 안전목록(text/plain) 이어야 한다. application/json 으로 보내면
					// 교차출처 preflight 가 필요한데, 언로드 중에는 그걸 끝낼 수 없어 브라우저가 비콘을
					// 조용히 버린다(실측). 마지막 체류가 통째로 사라진다. 서버가 text/plain 을 푼다.
					const queued = navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'text/plain;charset=UTF-8' }));
					if (!queued) restore();   // 브라우저가 큐에 넣지 못한 경우(큐 한도), 다음 기회에 다시
					return;
				}
				// 추적 실패가 화면에 어떤 영향도 주지 않아야 한다. 화면에는 알리지 않고 값만 보존한다.
				axios.post(url, payload).catch(restore);
			} catch (e) { restore(); }
		},

		async downloadPdf() {
			this.pdfBusy = true;
			// 주소 이동이 일어나므로 일반 요청은 취소될 수 있다 — 떠날 때와 같이 비콘으로 보낸다(Codex 리뷰: 오류 페이지로
			// 이동하면서 마지막 체류·사진 클릭이 사라졌다).
			this.flush(true);
			// 편집 화면의 미리보기(?theme=)로 들어온 화면이면 그 지면으로 찍는다. 화면과 다른
			// 지면이 나오면 무엇을 보고 있는 것인지 확인할 방법이 없다. 저장된 값과 같으면 붙이지 않는다.
			const themeQ = this.themeId !== this.themeSaved ? `&theme=${encodeURIComponent(this.themeId)}` : '';
			const url = `${this.apiBase}/pub/api/report/pdf?t=${encodeURIComponent(this.token)}&s=${encodeURIComponent(this.sessionKey)}${themeQ}`;

			// 고객은 예전 그대로 주소 이동이다. 파일 응답이라 화면은 바뀌지 않는다.
			if (!this.devPreview && !this.staffPreview) {
				window.location.href = url;
				// 버튼만 잠시 뒤 되돌린다(전환이 없어 끝을 알 수 없다).
				setTimeout(() => { this.pdfBusy = false; }, 6000);
				return;
			}

			// 미리보기(게이트가 켜진 어드민, 또는 로그인한 담당자)에서는 **요청 자체를 인증**해서 받는다.
			// 담당자의 다운로드가 고객의 'PDF 받음' 으로 세지지 않게 서버가 그 토큰을 본다.
			// 통행증을 주소에 실어 주면 그 주소를 넘기는 순간 게이트를 통과할 자격까지 함께
			// 넘어간다(Codex 리뷰). 주소 이동으로는 헤더를 실을 수 없으니 직접 받아서 저장한다.
			try {
				const t = this.$store && this.$store.getters.getToken;
				// 서버 예산은 이동 45초 + 준비 20초 + 인쇄 60초다. 그보다 넉넉히 기다리되, 멎으면 버튼이 영영 잠기지 않게 끊는다.
				const blob = await this.fetchWithin(url, { headers: t && t.accessToken ? { Authorization: t.accessToken } : {} }, 180000, (res) => {
					const ctype = res.headers.get('content-type') || '';
					// 게이트에 막히면 PDF 가 아니라 안내 HTML 이 온다. 그걸 .pdf 로 저장하면
					// 열리지 않는 파일이 손에 남는다(사내 다른 출력에서 겪은 유형).
					if (!res.ok || !/pdf|octet-stream/i.test(ctype)) throw new Error(`PDF 응답이 아님 (${res.status} ${ctype})`);
					return res.blob();
				});
				const href = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = href;
				a.download = `weonus_weekly_${this.report.period_key || ''}.pdf`;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				setTimeout(() => URL.revokeObjectURL(href), 10000);
			} catch (e) {
				alert(this.devPreview
					? 'PDF 를 받지 못했습니다. 어드민 계정으로 로그인했는지 확인해 주세요.'
					: 'PDF 를 받지 못했습니다. 잠시 후 다시 시도해 주세요.');
			} finally {
				this.pdfBusy = false;
			}
		},
	},
};
</script>

<style scoped lang="scss">
@import "@/assets/css/design-tokens.scss";

/* 고객 문서의 활자. 읽고 세는 글은 Pretendard, 이름과 금액은 S-CoreDream 이다.
   S-CoreDream 은 회사 홈페이지(weonus.net) 본문 서체라, 홈페이지를 본 고객이 같은
   활자의 문서를 받는다(DESIGN.md Typography).

   셋 다 한글 음절 전체를 담은 서브셋이다 — 드문 글자에서 서체가 갈아타지 않는다.
   swap 인 이유: 합쳐 1.4MB 라 느린 회선에서 block 이면 글자가 몇 초간 안 보인다.
   인쇄 조판은 waitFonts() 가 document.fonts.ready 를 기다린 뒤에 재므로 영향이 없다. */
@font-face {
	font-family: 'Pretendard';
	src: url('../assets/css/fonts/Pretendard4.woff2') format('woff2');
	font-weight: 400;
	font-style: normal;
	font-display: swap;
}
@font-face {
	font-family: 'Pretendard';
	src: url('../assets/css/fonts/Pretendard6.woff2') format('woff2');
	font-weight: 600;
	font-style: normal;
	font-display: swap;
}
@font-face {
	font-family: 'SCoreDream';
	src: url('../assets/css/fonts/SCoreDream7.woff2') format('woff2');
	font-weight: 700;
	font-style: normal;
	font-display: swap;
}

/* 고객 문서(DESIGN.md 의 두 번째 표면). 기준 레퍼런스는 경매 회사의 물건 도록이다.
   상자를 두르지 않는다. 구획은 여백과 얇은 괘선이 만든다. */
.wr-page {
	background: var(--wr-paper);
	color: var(--wr-ink);
	font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Malgun Gothic', sans-serif;
	line-height: 1.6;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	/* 한국어는 어절 단위로 꺾되(keep-all), 띄어쓰기 없는 긴 이름은 어디서든 꺾는다.
	   붙여 쓴 건물명("...복합상업시설분양센터")이 밖으로 나가 잘려 나갔다(Codex 리뷰). */
	word-break: keep-all;
	overflow-wrap: anywhere;
	-webkit-print-color-adjust: exact;
	print-color-adjust: exact;
}
/* 안내·오류 화면은 내용이 짧아 크림 바탕이 화면 위쪽만 덮고 아래가 흰 종이로 남는다.
   고객이 링크를 눌러 처음 보는 화면이라(개발중 안내) 한 장으로 보이게 채운다. */
.wr-page:not(.wr-pdf) {
	min-height: 100vh;
}
.wr-state {
	padding: 80px 20px;
	text-align: center;
	color: var(--wr-ink-soft);
}
.wr-state--err .wr-state__title {
	@include dt-type-doc-lot-title;
	color: var(--wr-ink);
	margin-bottom: 8px;
}
/* 개발중 안내(고객이 보는 화면). 오류가 아니라 안내이므로 도록의 크림 바탕을 그대로 쓴다. */
.wr-state--dev {
	padding: 96px 20px;
	line-height: 1.9;
}
.wr-state--dev .wr-state__title {
	@include dt-type-doc-lot-title;
	color: var(--wr-accent);
	margin-bottom: 12px;
}
/* 어드민 미리보기 띠. 문서의 일부로 보이면 안 되므로 도록 서체를 쓰지 않는다. */
.wr-devbar {
	@include dt-type-doc-note;
	background: var(--wr-paper-deep);
	border-bottom: 1px solid var(--wr-line);
	color: var(--wr-ink-soft);
	padding: 9px 16px;
	text-align: center;
	line-height: 1.6;
}

/* ── 공통 조각 ───────────────────────────────────────────────────────────── */
.wr-caption {
	@include dt-type-doc-caption;
	color: var(--wr-paper-deep);
	white-space: nowrap;
}
.wr-caption--dark {
	color: var(--wr-gold);
}
.wr-sep {
	margin: 0 7px;
	color: var(--wr-ink-soft);
	opacity: 0.55;
}
.wr-mark {
	display: block;
	width: 118px;
	height: 18px;
	flex: 0 0 auto;
}
.wr-mark--light {
	background: url(../assets/images/weonus_wordmark_wh.png) no-repeat right center / contain;
}
.wr-mark--dark {
	width: 92px;
	height: 14px;
	background: url(../assets/images/weonus_wordmark_bk.png) no-repeat right center / contain;
	opacity: 0.75;
}
.wr-rule {
	display: block;
	height: 1px;
	background: var(--wr-gold);
	opacity: 0.85;
}
.wr-rule--short {
	width: 46px;
	margin: 18px 0;
}

/* ── 표지 ────────────────────────────────────────────────────────────────
   이번 주 물건이 어디에 모여 있는지를 지도 한 장으로 먼저 보여 주고, 그 아래 목록을
   놓는다. 지도와 도판은 같은 두 색조로만 찍어 낸다(듀오톤) — 실제 매물 사진은 전선·
   주차 차량·제각각인 하늘이 섞여 있어 컬러로 나란히 놓으면 표지가 지저분해진다.
   구획은 상자가 아니라 여백과 얇은 괘선이 만든다(DESIGN.md). */
.wr-duo-def {
	position: absolute;
	width: 0;
	height: 0;
	overflow: hidden;
}
/* 표지. 마크가 지면을 장악하고 활자는 왼쪽 아래 한 덩어리로 모인다.
   `overflow: hidden` 이 마크를 재단선에서 끊는다 — 지우면 마크가 낱장 밖으로
   삐져나가 인쇄에서 빈 페이지가 따라붙는다. */
.wr-sheet--cover {
	position: relative;
	overflow: hidden;
	background: var(--wr-paper);
	color: var(--wr-ink);
	padding: 26px 24px 22px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	.wr-caption { color: var(--wr-ink-soft); }
	.wr-mark--dark { opacity: 1; }
}
/* 브랜드 마크. 흰 PNG 를 마스크로 떠서 버건디로 칠한다(원본을 그대로 쓰면
   어포스트로피가 버건디라 그 획만 떠 보인다). 종이 결이 잉크를 비치도록
   곱하기로 얹는다. */
.wr-cover__mark {
	position: absolute;
	z-index: 1;
	display: block;
	background: var(--wr-accent);
	opacity: 0.92;
	/* **`mix-blend-mode` 를 되살리지 말 것.** 곱하기는 이 요소를 독립 합성 그룹으로 만든다.
	   PDF 렌더러가 그 그룹을 사각형 투명 그룹으로 구우면서, 마크의 아래 모서리를 따라
	   지면 폭을 가로지르는 옅은 가로줄이 남는다(실측 2~3/255). 종이 결은 이미 지면 전체를
	   덮는 `.wr-cover__tex` 가 곱하기로 얹으므로 잉크가 눌린 느낌은 그대로다. */
	-webkit-mask-image: url(../assets/images/weonus_symbol_wh.png);
	mask-image: url(../assets/images/weonus_symbol_wh.png);
	-webkit-mask-size: contain;
	mask-size: contain;
	-webkit-mask-repeat: no-repeat;
	mask-repeat: no-repeat;
	-webkit-mask-position: center;
	mask-position: center;
	/* 웹 기본값. 인쇄는 mm 로 다시 잡는다(시안 01 비율: 지면 폭의 0.55). */
	width: 55vw;
	max-width: 290px;
	aspect-ratio: 1 / 1;
	right: 2vw;
	top: 4%;
}
/* 종이 결. 마크 위에 얹혀 잉크가 종이에 눌린 것처럼 만든다. */
.wr-cover__tex {
	position: absolute;
	inset: 0;
	z-index: 3;
	background: url(../assets/images/paper_texture.png) repeat;
	background-size: 400px 400px;
	opacity: 0.42;
	mix-blend-mode: multiply;
	pointer-events: none;
}
.wr-cover__head,
.wr-cover__low,
.wr-cover__band { position: relative; z-index: 2; }
.wr-cover__head {
	flex: 0 0 auto;
	.wr-mark--dark { width: 128px; height: 19px; background-position: left center; }
	.wr-caption { display: block; margin-top: 9px; }
}
/* 머리와 발 사이를 벌리는 자리. 마크가 서는 지면이다. */
.wr-cover__gap { flex: 1 1 auto; min-height: 104px; }
.wr-cover__low { flex: 0 0 auto; }
.wr-cover__rule {
	display: block;
	width: 20mm;
	height: 1px;
	margin-bottom: 4.2mm;
	background: var(--wr-gold);
}
.wr-period {
	@include dt-type-doc-spec;
	display: block;
	color: var(--wr-ink-soft);
}
.wr-to {
	@include dt-type-doc-label;
	display: block;
	margin-top: 1.4mm;
	font-size: 15px;
	color: var(--wr-ink);
}
/* 표지에서 가장 큰 활자. 마크가 색을 다 가져갔으므로 제목은 잉크로 둔다. */
.wr-title {
	@include dt-type-doc-title;
	font-size: calc(32px * var(--wr-tscale, 1));
	margin: 4.4mm 0 0;
	color: var(--wr-ink);
	text-wrap: balance;
}
.wr-cover__sum {
	@include dt-type-doc-note;
	margin: 4.6mm 0 0;
	color: var(--wr-ink-soft);
}
/* 제목 아래 사실 한 줄. 제목과 같은 왼쪽 선에서 시작하고, 항목 사이는 가운뎃점이
   벌린다(시안 02·06·07). 라벨을 붙이지 않는다 — 날짜·이름·건수·금액은 생김새만으로
   무엇인지 읽힌다. */
.wr-cover__meta1 {
	@include dt-type-doc-spec;
	margin: 4.6mm 0 0;
	color: var(--wr-ink-soft);
}

/* ── 표지 ① 모노그램(catalog) ─────────────────────────────────────────────
   시안 01 의 마크는 원이 거의 다 보이고 오른쪽 위만 재단선 밖으로 나간다. 158mm 로
   키워 아래까지 내려오면 원의 절반이 잘려 '무슨 도형인지 모를 곡선' 이 되고, 지면 아래
   활자까지 밀고 내려온다(실측 비교). 지름은 그대로 크게 두되 **위로 올려** 원의 아래가
   지면 중간 위에서 끝나게 한다. */
/* ── 이번 주 수록 물건 ─────────────────────────────────────────────────────
   표지에서 내려온 목록. 행마다 괘선을 긋지 않는다 — 다섯 줄짜리 목록에 밑줄을
   다섯 번 그으면 웹 표가 되고, 구획은 번호와 여백이 이미 만들고 있다. */
.wr-index {
	background: var(--wr-paper);
	color: var(--wr-ink);
	padding: 26px 24px 30px;
}
.wr-index__head {
	@include dt-type-doc-label;
	margin: 0 0 14px;
	padding-bottom: 8px;
	border-bottom: 1px solid var(--wr-line);
	color: var(--wr-ink-soft);
	font-weight: 600;
}
.wr-index__list {
	list-style: none;
	margin: 0;
	padding: 0;
	display: grid;
	gap: 14px 28px;
}
.wr-index__link {
	display: grid;
	grid-template-columns: auto 1fr auto;
	align-items: baseline;
	column-gap: 12px;
	color: inherit;
	text-decoration: none;
}
.wr-index__no,
.wr-index__name,
.wr-index__price { grid-row: 1; }
.wr-index__no {
	@include dt-type-doc-label;
	font-size: 13px;
	color: var(--wr-accent);
	font-variant-numeric: tabular-nums;
}
.wr-index__name {
	@include dt-type-doc-spec;
	font-size: 15px;
	font-weight: 600;
	color: var(--wr-ink);
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.wr-index__price {
	font-family: SCoreDream, Pretendard, sans-serif;
	font-weight: 700;
	font-size: 14px;
	color: var(--wr-ink);
	white-space: nowrap;
}
/* 지역은 이름 아래에 붙는다. 오른쪽 끝으로 보내면 그 줄이 웹 표가 된다. */
.wr-index__area {
	@include dt-type-doc-note;
	grid-column: 2;
	font-size: 11.5px;
	color: var(--wr-ink-soft);
}
/* 표지 아래 상호·고지는 **인쇄 표지 전용**이다. 웹에서는 같은 내용이 맨 아래
   마무리에 다시 나오므로, 여기에 두면 본문 시작 전에 고지가 두 번 나온다. */
.wr-page:not(.wr-pdf) .wr-cover__band {
	display: none;
}
.wr-cover__band {
	flex: 0 0 auto;
	/* 표지가 `align-items: flex-start` 라 이게 없으면 띠가 고지 문장 폭(158mm)으로 줄어
	   괘선이 오른쪽 재단선에서 16mm 못 미친다. 바로 위 목록 괘선은 174mm 라 두 줄의
	   오른쪽 끝이 어긋나 보인다(실측). 글줄 길이 제한은 `.wr-cover__note` 가 따로 한다. */
	align-self: stretch;
	margin-top: 18px;
	padding-top: 12px;
	border-top: 1px solid var(--wr-line);
	color: var(--wr-ink);
}
.wr-cover__corp {
	@include dt-type-doc-note;
	margin-bottom: 5px;
	strong { margin-right: 8px; }
	span { color: var(--wr-ink-soft); }
}
.wr-cover__agent::before {
	content: '·';
	margin: 0 7px;
	opacity: 0.6;
}
.wr-cover__note {
	@include dt-type-doc-note;
	margin: 0;
	color: var(--wr-ink-soft);
}
/* 오른쪽 아래 칸의 집계. 사진 격자(시안 08)만 쓴다. */
.wr-cover__tally,
.wr-cover__dongs { display: none; }

/* ── 속장 ────────────────────────────────────────────────────────────────── */
.wr-sheet--body {
	padding: 0 16px 8px;
}
.wr-run {
	display: none;
}
.wr-sheet__inner {
	padding-top: 24px;
}

/* Weekly Brief. 상자가 아니라 괘선 사이의 글이다. */
.wr-brief {
	border-top: 1px solid var(--wr-line);
	border-bottom: 1px solid var(--wr-line);
	padding: 16px 0 18px;
	margin-bottom: $dt-spacing-doc-block;
}
.wr-brief__label {
	@include dt-type-doc-caption;
	color: var(--wr-accent);
	margin-bottom: 10px;
}
.wr-brief__body {
	@include dt-type-doc-read;
	margin: 0;
	color: var(--wr-ink);
}

/* ── 물건 로트 ───────────────────────────────────────────────────────────── */
.wr-lot {
	display: flex;
	flex-direction: column;
	margin-bottom: 34px;
	break-inside: avoid;
	page-break-inside: avoid;
}
.wr-lot__photo {
	position: relative;
	background: var(--wr-paper-deep);
	flex: 0 0 auto;
	img {
		display: block;
		width: 100%;
		height: 240px;
		object-fit: cover;
		cursor: zoom-in;
	}
}
.wr-lot__nophoto {
	height: 140px;
	display: flex;
	align-items: center;
	justify-content: center;
	@include dt-type-doc-label;
	color: var(--wr-ink-soft);
	background: var(--wr-surface);
	opacity: 0.5;
}

/* 사진 격자(mosaic 지면만 그린다). 대표 사진 아래 두 칸, 표지 격자와 같은 2mm 검은 홈. */
.wr-lot__grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 6px;
	margin-top: 6px;
	flex: 0 0 auto;
}
.wr-lot__cell {
	position: relative;
	display: block;
	padding: 0;
	border: 0;
	background: var(--wr-paper-deep);
	overflow: hidden;
	aspect-ratio: 4 / 3;
	img {
		position: absolute;
		inset: 0;
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
}
button.wr-lot__cell { cursor: zoom-in; }
/* 도판 캡션(gallery 지면만 그린다). */
.wr-lot__photocap {
	display: flex;
	justify-content: space-between;
	gap: 10px;
	margin-top: 7px;
	@include dt-type-doc-note;
	color: var(--wr-ink-soft);
	flex: 0 0 auto;
}
/* 번호·이름·주소·금액 덩어리. 기본 지면에서는 보이지 않는 감싸개다(색면 분할이 색을 입힌다). */
.wr-lot__top {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

/* 그 자리의 위치도. 현장 사진 바로 아래 같은 폭으로 붙어서 "무엇" 다음에 "어디" 를
   준다. **라벨이 있는 일반 지도**를 손대지 않고 그대로 쓴다 — 항공사진을 두 색으로 누른
   판은 작은 띠에서 회색 얼룩이 되어 알아볼 수 없었다(2026-09-17 사용자 지적). 지면의 색
   체계와 어긋나는 것은 알고 두는 것이다: 지도는 도판이 아니라 참조 자료이고, 도로명과
   상호가 읽혀야 존재 이유가 있다. 아래 한 줄이 동네와 출처를 적는다. */
.wr-lot__spot {
	margin: 18px 0 0;
}
.wr-lot__spotimg {
	position: relative;
	overflow: hidden;
	background: var(--wr-paper-deep);
	/* 흑백 지면(먹지)에서 흰 지도가 뚫려 보이지 않게 괘선 한 줄로 판을 잡는다. */
	box-shadow: inset 0 0 0 1px var(--wr-line);
	/* 카드 전폭의 판. 휴대폰에서는 16:10(390px 에 244px), 넓은 화면과 인쇄는 아래에서
	   더 납작하게 편다. 원본(640x400 = 1.6:1)보다 납작하게 잘라 좌하단의 제공자 로고를
	   판 밖으로 보낸다(출처는 캡션이 적는다). 건물은 한가운데라 잘리지 않는다. */
	aspect-ratio: 16 / 10;
	img {
		/* 절대 위치 — 이미지의 원래 높이(1440px)가 판의 자연 높이에 섞이지 않게. 인쇄에서
		   판은 격자 행(남는 지면)이 정하는데, 흐름에 두면 측정 패스가 이미지 높이만큼 카드를
		   크게 재서 모든 물건이 축소 경로로 빠졌다(실측 fit 0.89~0.96). */
		position: absolute;
		inset: 0;
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
		/* 상점 아이콘의 원색이 지면에서 튀지 않게 채도만 조금 낮춘다. 라벨은 그대로 읽힌다. */
		filter: saturate(0.82);
	}
}
/* 번호 핀. 꼭짓점이 판 한가운데(=건물)에 선다. 강조색 몸통에 흰 테두리와 그림자 —
   지도 위의 상호 아이콘(원 안의 그림)과 꼴이 달라 한눈에 구별된다. */
.wr-lot__pin {
	position: absolute;
	left: 50%;
	top: 50%;
	width: 34px;
	height: 44px;
	transform: translate(-50%, -100%);
	overflow: visible;
	filter: drop-shadow(0 2px 2.5px rgba(0, 0, 0, 0.35));
}
.wr-lot__pinhalo {
	fill: rgba(0, 0, 0, 0.28);
}
.wr-lot__pinbody {
	fill: var(--wr-accent);
	stroke: #fff;
	stroke-width: 2;
	stroke-linejoin: round;
}
.wr-lot__pinno {
	@include dt-type-doc-label;
	font-size: 12px;
	font-weight: 700;
	fill: var(--wr-pin-ink, #fff);
	letter-spacing: 0;
}
/* 원형 배지(aerial 지면). 배지 한가운데가 건물이라 변형이 다르다(꼭짓점이 아니라 중심). */
.wr-lot__pin--badge {
	width: 44px;
	height: 44px;
	transform: translate(-50%, -50%);
}
.wr-lot__ring {
	fill: none;
	stroke: #fff;
	stroke-width: 1.5;
	opacity: 0.9;
}
.wr-lot__pin--badge .wr-lot__pinbody { stroke-width: 2.5; }
.wr-lot__spotcap {
	display: flex;
	justify-content: space-between;
	gap: 10px;
	margin: 5px 0 0;
	@include dt-type-doc-note;
	color: var(--wr-ink-soft);
}
.wr-lot__spotsrc {
	flex: 0 0 auto;
	opacity: 0.7;
}

.wr-lot__body {
	display: flex;
	flex-direction: column;
	min-width: 0;
	padding-top: 14px;
}
/* 번호와 갈래. 번호가 글 단의 첫 글자다. */
/* 번호 · 물건명 · 금액이 이 지면의 세 층위다(시안 04). 셋이 비슷한 크기로 서면
   무엇을 먼저 읽어야 하는지가 사라지고, 도록이 아니라 표가 된다. */
.wr-lot__head {
	display: flex;
	align-items: baseline;
	gap: 10px;
	margin-bottom: 2px;
}
.wr-lot__no {
	font-family: SCoreDream, Pretendard, sans-serif;
	font-weight: 700;
	font-size: 40px;
	line-height: 1;
	letter-spacing: -0.03em;
	color: var(--wr-accent);
	font-variant-numeric: tabular-nums;
	flex: 0 0 auto;
}
.wr-lot__name {
	@include dt-type-doc-lot-title;
	font-size: 30px;
	color: var(--wr-ink);
	margin: 0;
}
/* 태그를 버건디로 두면 눈이 물건명보다 태그로 먼저 간다. 속장에서 버건디가 등장하는
   자리는 로트 번호와 십자뿐이다(DESIGN.md Colors). */
.wr-lot__tags {
	@include dt-type-doc-label;
	color: var(--wr-ink-soft);
	min-width: 0;
}
.wr-lot__addr {
	@include dt-type-doc-spec;
	color: var(--wr-ink-soft);
	margin-top: 5px;
}
/* 금액. 문서에서 가장 큰 활자다. 수익률은 같은 줄 기준선에 맞춰 작게 붙인다 —
   줄을 나누면 두 값이 서로 다른 층위처럼 읽힌다. */
.wr-lot__figure {
	display: flex;
	align-items: baseline;
	flex-wrap: wrap;
	gap: 2px 12px;
	margin-top: 16px;
}
.wr-lot__price {
	@include dt-type-doc-lot-figure;
	font-size: 44px;
	color: var(--wr-ink);
	font-variant-numeric: tabular-nums;
}
.wr-lot__rate {
	@include dt-type-doc-label;
	color: var(--wr-ink-soft);
	font-variant-numeric: tabular-nums;
}

/* 제원 격자. 배경도 테두리도 칠하지 않는다 — 라벨 열과 값 열이 같은 자리에 서는 것이
   구획이다(DESIGN.md). 위쪽 괘선 하나로 금액과 갈라 둔다. */
.wr-spec {
	margin: 16px 0 0;
	padding-top: 14px;
	border-top: 1px solid var(--wr-line);
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	column-gap: 24px;
	row-gap: 10px;
}
/* **라벨이 값 위에 오고 값이 크다**(시안 04). 라벨을 왼쪽에 두고 값을 오른쪽에 붙이면
   읽는 눈이 라벨 열을 한 번 훑고 값 열을 다시 훑어야 한다. 위아래로 세우면 한 덩어리가
   하나의 사실이 되고, 값이 커져도 줄이 밀리지 않는다. 값이 없어도 줄표로 자리를 지키는
   규칙은 그대로다 — 같은 항목이 매물마다 같은 칸에 서야 나란히 비교할 수 있다. */
.wr-spec__row {
	display: block;
	padding: 2px 0;
	min-width: 0;
}
.wr-spec dt {
	@include dt-type-doc-label;
	display: block;
	color: var(--wr-ink-soft);
	font-weight: 400;
	white-space: nowrap;
}
.wr-spec dd {
	@include dt-type-doc-spec;
	display: block;
	margin: 1px 0 0;
	font-size: 17px;
	font-weight: 600;
	color: var(--wr-ink);
	font-variant-numeric: tabular-nums;
}
.wr-lot__features {
	list-style: none;
	margin: 14px 0 0;
	padding: 0;
	li {
		@include dt-type-doc-spec;
		font-size: 12.5px;
		color: var(--wr-ink-soft);
		padding-left: 12px;
		text-indent: -12px;
		&::before {
			content: '·';
			margin-right: 6px;
			opacity: 0.55;
		}
	}
}
.wr-lot__note {
	@include dt-type-doc-read;
	color: var(--wr-ink);
	margin: 10px 0 0;
}

/* ── 웹 마무리 ───────────────────────────────────────────────────────────── */
.wr-foot {
	border-top: 1px solid var(--wr-line);
	margin: 8px 16px 0;
	padding: 24px 0 48px;
}
.wr-foot__actions {
	text-align: center;
	margin-bottom: 24px;
}
.wr-btn {
	border: 1px solid var(--wr-accent);
	background: transparent;
	color: var(--wr-accent);
	@include dt-type-doc-label;
	padding: 14px 30px;
	cursor: pointer;
}
.wr-btn:disabled {
	opacity: 0.55;
	cursor: default;
}
.wr-foot__corp {
	@include dt-type-doc-note;
	color: var(--wr-ink);
	margin-bottom: 8px;
	span {
		display: inline-block;
		margin-left: 8px;
		color: var(--wr-ink-soft);
	}
}
.wr-foot__note {
	@include dt-type-doc-note;
	color: var(--wr-ink-soft);
	margin: 0;
}

/* 민·형사 경고. 회색 고지와 같은 활자에 색과 굵기만 올린다 — 고지 묶음 안에서 이것만 눈에 걸리게.
   색은 테마의 강조색을 쓰지 않는다(버건디·금·주황은 장식으로 읽힌다). 빨강은 `--wr-legal` 하나다. */
.wr-legal {
	@include dt-type-doc-note;
	margin: 6px 0 0;
	color: var(--wr-legal);
	font-weight: 600;
}
.wr-cover-legal {
	position: relative;
	margin: 0;
	padding: 16px max(24px, calc((100% - 1080px) / 2));
	background: var(--wr-paper);
}
.wr-print-legal { display: none; }

/* 흐름 낱장용 표 자리. 표가 아닐 때(웹·일반 낱장·측정 중)는 div 이고 흔적을 남기지 않는다. */
div.wr-flowtab,
div.wr-flowtab__part { display: contents; }

/* 워터마크. 내용 위에 옅게 얹는다 — 바탕에만 두면 사진·지도가 덮어 캡처에서 사라진다(매물 자료와
   같은 이유, common.scss 의 .print-section::after). 머리띠(30)·사진 크게 보기(60) 아래에 둔다. */
.wr-wm {
	position: fixed;
	inset: 0;
	z-index: 25;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	pointer-events: none;
	user-select: none;
}
.wr-wm span {
	font-family: Arial, 'Helvetica Neue', sans-serif;
	font-weight: 800;
	font-size: clamp(88px, 22vw, 320px);
	letter-spacing: 0.06em;
	line-height: 1;
	white-space: nowrap;
	/* 채움은 지면 잉크, 테두리는 옅은 흰색. 워터마크는 쪽마다 반복되는 요소 하나라 쪽별로 색을
	   바꿀 수 없는데, 항공지도 표지·색면 표지처럼 판이 어두운 쪽에서는 잉크 채움이 묻힌다(실측).
	   밝은 지면에서는 흰 테두리가 보이지 않으므로 두 경우 모두 한 벌로 된다. */
	color: color-mix(in srgb, var(--wr-ink) 7%, transparent);
	-webkit-text-stroke: 1.5px rgba(255, 255, 255, 0.16);
	transform: rotate(-30deg);
}
/* WebKit 인쇄 전용 사본(표지·물건 카드·끝 마무리마다 하나). 화면과 다른 엔진의 인쇄에서는 그리지 않는다. */
.wr-wm.wr-wm--card,
.wr-lot__legal,
.wr-brief__legal { display: none; }

/* ══ 웹 화면 ══════════════════════════════════════════════════════════════
   같은 문서를 화면에서 읽는다. 도록의 규칙(무상자·괘선·명조·버건디)은 그대로 두고,
   종이에서 불가능한 것만 더한다 — 사진 전부 보기, 지금 읽는 물건 표시, 바로가기.
   이 아래 규칙은 전부 `.wr-page:not(.wr-pdf)` 안에 있어 인쇄 조판에 닿지 않는다.

   폭: 본문 글줄이 한 줄 40자 안쪽에 머무르는 값이다(사진 46% + 글 54%). */
$wr-web-max: 1080px;
$wr-pad: 20px;
$wr-pad-lg: 44px;
$wr-ease: cubic-bezier(0.2, 0, 0, 1);

/* ── 머리띠(고정) ─────────────────────────────────────────────────────────
   표지를 지나야 나타난다. fixed 인 이유: sticky 로 두면 표지 위에 자리를 차지해
   문서 첫 화면이 그만큼 내려간다. 숨긴 동안에는 클릭도 받지 않는다. */
.wr-bar {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 30;
	background: rgba(247, 242, 232, 0.93);
	-webkit-backdrop-filter: saturate(150%) blur(10px);   /* 사파리(카톡 인앱)는 아직 접두사가 필요하다 */
	backdrop-filter: saturate(150%) blur(10px);
	border-bottom: 1px solid var(--wr-line);
	transform: translateY(-100%);
	opacity: 0;
	pointer-events: none;
	transition-property: transform, opacity;
	transition-duration: 220ms;
	transition-timing-function: $wr-ease;
}
.wr-bar.is-on {
	transform: none;
	opacity: 1;
	pointer-events: auto;
}
.wr-bar__in {
	display: flex;
	align-items: center;
	gap: 14px;
	height: 52px;
	max-width: $wr-web-max;
	margin: 0 auto;
	padding: 0 $wr-pad;
}
.wr-bar__mark {
	width: 80px;
	height: 12px;
	opacity: 0.8;
}
.wr-bar__title {
	@include dt-type-doc-label;
	color: var(--wr-ink-soft);
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	letter-spacing: 0.04em;
}
.wr-bar__nav {
	display: flex;
	align-items: center;
	flex: 0 0 auto;
}
.wr-bar__no {
	position: relative;
	border: 0;
	background: transparent;
	cursor: pointer;
	@include dt-type-doc-label;
	color: var(--wr-ink-soft);
	min-width: 34px;
	height: 40px;
	padding: 0 3px;
	font-variant-numeric: tabular-nums;
	transition-property: color;
	transition-duration: 140ms;
	transition-timing-function: ease-out;
}
.wr-bar__no:hover { color: var(--wr-ink); }
/* 지금 읽는 물건은 색과 괘선 **둘 다**로 표시한다. 색 하나로만 알리면 전환이 끝난
   뒤에 화면을 본 사람에게는 아무 단서가 없다. */
.wr-bar__no.is-on { color: var(--wr-accent); }
.wr-bar__no.is-on::after {
	content: '';
	position: absolute;
	left: 5px;
	right: 5px;
	bottom: 8px;
	height: 1px;
	background: var(--wr-accent);
}
.wr-bar__pdf {
	flex: 0 0 auto;
	border: 1px solid var(--wr-accent);
	background: transparent;
	color: var(--wr-accent);
	@include dt-type-doc-label;
	height: 34px;
	padding: 0 14px;
	cursor: pointer;
	transition-property: background-color, color, transform;
	transition-duration: 150ms;
	transition-timing-function: ease-out;
}
.wr-bar__pdf:hover:not(:disabled) {
	background: var(--wr-accent);
	color: var(--wr-paper);
}
.wr-bar__pdf:active:not(:disabled) { transform: scale(0.96); }
.wr-bar__pdf:disabled { opacity: 0.5; cursor: default; }

/* ── 지면 ─────────────────────────────────────────────────────────────── */
.wr-page:not(.wr-pdf) {
	min-height: 100vh;

	/* 표지는 화면 끝까지 버건디로 채우고, 글만 가운데 단 안에 세운다
	   (내용층은 흘러넘치고 글·조작은 여백 안에 머문다). */
	.wr-sheet--cover {
		padding: 30px $wr-pad 26px;
	}
	.wr-sheet--body {
		max-width: $wr-web-max;
		margin: 0 auto;
		padding: 0 $wr-pad;
	}
	.wr-sheet__inner { padding-top: 30px; }

	/* 수록 물건 색인 */
	.wr-index__link {
		transition-property: opacity, color;
		transition-duration: 140ms;
		transition-timing-function: ease-out;
	}
	.wr-index__link:hover .wr-index__name { color: var(--wr-accent); }
	.wr-index__link:active { opacity: 0.72; }

	/* 브리핑. 읽는 글이라 글줄 길이를 잡아 둔다. */
	.wr-brief__body {
		font-size: 15px;
		line-height: 1.8;
		max-width: 660px;
		text-wrap: pretty;
	}

	/* ── 물건 로트 ───────────────────────────────────────────────────── */
	.wr-lot {
		margin-bottom: 56px;
		scroll-margin-top: 68px;   /* 머리띠가 제목을 덮지 않게 */
	}
	.wr-lot__media {
		display: flex;
		flex-direction: column;
		gap: 7px;
		min-width: 0;
	}
	/* 사진 비율을 고정한다. 예전에는 글 단 높이에 맞춰 늘어나서, 글이 짧은 물건은
	   사진만 길쭉하게 남고 그 아래가 통째로 비었다(실측 04·05번). */
	.wr-lot__photo {
		aspect-ratio: 4 / 3;
		overflow: hidden;
	}
	.wr-lot__photo img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition-property: transform;
		transition-duration: 560ms;
		transition-timing-function: $wr-ease;
	}
	.wr-lot__photo:hover img { transform: scale(1.035); }
	.wr-lot__nophoto { height: 100%; }
	/* 사진이 더 있다는 사실은 화면에 단서가 있어야 존재한다(없으면 아무도 누르지 않는다). */
	.wr-lot__more {
		position: absolute;
		right: 0;
		bottom: 0;
		display: inline-flex;
		align-items: center;
		min-height: 40px;      /* 손가락이 닿는 크기. 실측 31px 이었다 */
		border: 0;
		cursor: pointer;
		background: rgba(36, 29, 29, 0.7);
		color: var(--wr-paper);
		@include dt-type-doc-label;
		padding: 0 14px;
		transition-property: background-color;
		transition-duration: 150ms;
		transition-timing-function: ease-out;
	}
	.wr-lot__more:hover { background: var(--wr-accent); }

	.wr-lot__thumbs {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		grid-template-columns: repeat(5, 1fr);
		gap: 7px;
	}
	.wr-thumb {
		position: relative;
		display: block;
		width: 100%;
		aspect-ratio: 4 / 3;
		padding: 0;
		border: 0;
		background: var(--wr-paper-deep);
		overflow: hidden;
		cursor: pointer;
		transition-property: transform;
		transition-duration: 150ms;
		transition-timing-function: ease-out;
	}
	.wr-thumb img {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition-property: opacity, transform;
		transition-duration: 260ms;
		transition-timing-function: $wr-ease;
	}
	.wr-thumb:hover img { opacity: 0.88; transform: scale(1.06); }
	.wr-thumb:active { transform: scale(0.96); }
	.wr-thumb__more {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(36, 29, 29, 0.58);
		color: var(--wr-paper);
		@include dt-type-doc-label;
		letter-spacing: 0.04em;
		font-variant-numeric: tabular-nums;
	}

	/* 글 단. 이름 > 금액 > 제원 순으로 한 단계씩 내려온다. */
	.wr-lot__body { padding-top: 18px; }
	.wr-lot__name { font-size: 22px; text-wrap: balance; }
	.wr-lot__addr { font-size: 14px; margin-top: 6px; }
	.wr-lot__price { font-size: 26px; }
	.wr-lot__rate { font-size: 13px; }
	.wr-lot__features li { font-size: 13.5px; line-height: 1.7; padding-left: 13px; text-indent: -13px; }
	/* 특징은 사실 나열, 코멘트는 담당자의 해석이다. 금색 괘선 한 줄로 갈라 둔다. */
	.wr-lot__note {
		font-size: 15px;
		line-height: 1.8;
		margin-top: 16px;
		padding-left: 14px;
		border-left: 2px solid rgba(168, 130, 63, 0.45);
		max-width: 640px;
		text-wrap: pretty;
	}

	/* ── 마무리 ──────────────────────────────────────────────────────── */
	.wr-foot {
		max-width: $wr-web-max;
		margin: 16px auto 0;
		padding: 0 $wr-pad 64px;
		border-top: 0;
	}
	/* 괘선은 안쪽 여백 **안에서** 긋는다. 바깥 상자에 테두리를 두면 브리핑 괘선보다
	   좌우로 여백만큼 더 길어져, 같은 문서 안에서 줄 길이가 두 가지가 된다. */
	.wr-foot::before {
		content: '';
		display: block;
		height: 1px;
		background: var(--wr-line);
		margin-bottom: 36px;
	}
	.wr-foot__actions { margin-bottom: 28px; }
	.wr-btn {
		min-width: 236px;
		height: 52px;
		padding: 0 30px;
		transition-property: background-color, color, transform;
		transition-duration: 150ms;
		transition-timing-function: ease-out;
	}
	.wr-btn:hover:not(:disabled) {
		background: var(--wr-accent);
		color: var(--wr-paper);
	}
	.wr-btn:active:not(:disabled) { transform: scale(0.96); }
}

/* ── 등장 전환 ────────────────────────────────────────────────────────────
   .wr-anim 은 관찰을 **실제로 건 뒤에만** 붙는다. 먼저 숨겨 놓고 켜면 관찰이
   실패한 브라우저에서 본문이 투명한 채로 남는다(고객은 빈 화면을 본다). */
.wr-page.wr-anim:not(.wr-pdf) .wr-lot {
	transition-property: opacity, transform;
	transition-duration: 520ms;
	transition-timing-function: $wr-ease;
}
.wr-page.wr-anim:not(.wr-pdf) .wr-lot:not(.is-in) {
	opacity: 0;
	transform: translateY(14px);
}

/* ── 사진 크게 보기 ──────────────────────────────────────────────────── */
.wr-lb {
	position: fixed;
	inset: 0;
	z-index: 60;
	display: flex;
	flex-direction: column;
	background: rgba(20, 15, 15, 0.97);
	animation: wr-lb-in 180ms ease-out;
}
@keyframes wr-lb-in {
	from { opacity: 0; }
	to { opacity: 1; }
}
.wr-lb__head {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 10px 12px 10px 18px;
}
.wr-lb__name {
	@include dt-type-doc-label;
	color: var(--wr-paper);
	opacity: 0.85;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.wr-lb__x,
.wr-lb__nav {
	display: flex;
	align-items: center;
	justify-content: center;
	border: 0;
	background: transparent;
	color: var(--wr-paper);
	cursor: pointer;
	transition-property: background-color, border-color, opacity, transform;
	transition-duration: 150ms;
	transition-timing-function: ease-out;
	svg {
		width: 22px;
		height: 22px;
		fill: none;
		stroke: currentColor;
		stroke-width: 1.5;
		stroke-linecap: round;
		stroke-linejoin: round;
	}
}
.wr-lb__x { width: 44px; height: 44px; opacity: 0.85; }
.wr-lb__x:hover { opacity: 1; }
.wr-lb__x:active { transform: scale(0.96); }
.wr-lb__stage {
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 14px;
	img {
		display: block;
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}
}
.wr-lb__gone {
	@include dt-type-doc-note;
	color: var(--wr-paper-deep);
	opacity: 0.8;
}
.wr-lb__foot {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 20px;
	padding: 18px 14px calc(22px + env(safe-area-inset-bottom));
}
.wr-lb__nav {
	width: 54px;
	height: 54px;
	border: 1px solid rgba(247, 242, 232, 0.3);
}
.wr-lb__nav:hover {
	background: rgba(247, 242, 232, 0.12);
	border-color: rgba(247, 242, 232, 0.6);
}
.wr-lb__nav:active { transform: scale(0.96); }
.wr-lb__count {
	@include dt-type-doc-label;
	color: var(--wr-paper);
	font-variant-numeric: tabular-nums;
	min-width: 78px;
	text-align: center;
	i { font-style: normal; opacity: 0.45; margin: 0 5px; }
}

/* ── 키보드 초점 ─────────────────────────────────────────────────────────
   마우스로 누를 때는 나타나지 않고, 탭으로 옮겨 다닐 때만 보인다. */
.wr-page :focus-visible {
	outline: 2px solid var(--wr-gold);
	outline-offset: 2px;
}
.wr-sheet--cover :focus-visible,
.wr-lb :focus-visible {
	outline-color: var(--wr-paper);
}

/* ── 좁은 화면(859px 이하) ───────────────────────────────────────────────
   카톡으로 받은 링크를 휴대폰에서 여는 것이 이 문서의 주 경로다. 사진은 화면
   끝까지 채우고, 글과 조작만 여백 안에 남긴다. */
@media (max-width: 859px) {
	.wr-page:not(.wr-pdf) {
		.wr-lot { margin-bottom: 44px; }
		.wr-lot__media { margin-left: -$wr-pad; margin-right: -$wr-pad; }
		.wr-lot__photo { aspect-ratio: 3 / 2; }
		/* 다음 사진이 화면 끝에서 살짝 걸치게 둔다. 정확히 맞아떨어지면 아무도 넘기지 않는다. */
		.wr-lot__thumbs {
			display: flex;
			grid-template-columns: none;
			gap: 6px;
			overflow-x: auto;
			padding: 0 $wr-pad;
			scroll-padding-inline: $wr-pad;
			scroll-snap-type: x mandatory;
			-webkit-overflow-scrolling: touch;
			scrollbar-width: none;
			li {
				flex: 0 0 27%;
				scroll-snap-align: start;
			}
		}
		.wr-lot__thumbs::-webkit-scrollbar { display: none; }
		.wr-lot__body { padding-top: 16px; }
		.wr-lot__note { max-width: none; }
		.wr-spec__row { grid-template-columns: 84px minmax(0, 1fr); }
		.wr-btn { width: 100%; min-width: 0; }
	}
	.wr-bar__title { display: none; }
	.wr-bar__in { gap: 10px; }
	.wr-bar__nav { flex: 1 1 auto; justify-content: flex-end; }
}

/* 항공사진 표지의 띠는 **한 줄에 N 칸**이라 좁은 화면에서 칸이 글자보다 좁아진다.
   번호와 금액은 줄지 않는 항목(`flex: 0 0 auto`)이라 칸 밖으로 흘러 옆 칸 글자와 겹친다
   (실측 2026-09-16: 390px 화면에 여덟 건이면 칸 폭이 31px 이고 글자 겹침이 7군데).
   좁은 화면에서는 칸 최소 폭을 정해 **줄을 접는다** — 띠가 두세 줄이 되더라도 글자가
   겹치는 것보다 낫다. 넓은 화면은 지금까지처럼 한 줄이다. */
/* 사진 격자의 제목 칸은 가운데 행 왼쪽 두 칸(높이 33.333%)에 고정인데, 390px 화면에서는 칸이
   150px 뿐이라 석 줄 제목과 요약이 칸을 넘어 아래 사진 위에 겹친다(Codex 리뷰 재현). 좁은
   화면에서는 칸이 글만큼 자라게 둔다. 불투명한 칸이라 자란 만큼 아래 사진을 가릴 뿐 글이
   사진 위에 얹히지는 않는다. */
@media (max-width: 859px) {
	.wr-page:not(.wr-pdf) .wr-cover--mosaic .wr-cover__low {
		height: auto;
		min-height: 33.333%;
		bottom: auto;
		padding-top: 18px;
		padding-bottom: 18px;
	}
}
@media (max-width: 859px) {
	.wr-page:not(.wr-pdf) .wr-cover--aerial .wr-cover__lots {
		grid-auto-flow: row;
		grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
		row-gap: 7px;
	}
	/* 좁은 화면에서는 판을 **위에 흐름으로** 세우고 활자는 그 아래에 둔다. 판 위에 활자를
	   겹치는 인쇄 골격을 그대로 두면 표지가 515px 뿐이라 활자 덩어리가 판의 아래 절반을
	   덮고, 번호가 제목 뒤에 깔린다(실측 2026-09-17: 02번이 제목과 22x20px 겹침). 상호만
	   판 왼쪽 위에 얹는다(그 자리는 지도 여백이 비워 둔다). */
	.wr-page:not(.wr-pdf) .wr-cover--aerial {
		min-height: 0;
		padding-top: 0;
		.wr-cover__aerial {
			position: relative;
			inset: auto;
			margin: 0 -#{$wr-pad};
			aspect-ratio: 700 / 990;
			/* 태블릿(768px)에서는 판 비율 그대로면 1,086px 이라 표지가 화면 1.4장이 된다. 높이를
			   화면의 8할로 막는다. 가로 위치와 폭(자르지 않고 폭을 줄이는 규칙)은 아래 '좁은 화면 골격의
			   판 폭' 블록이 정한다. 여기에 적으면 미디어 조건 없는 넓은 골격 블록에 진다. */
			max-height: 80vh;
		}
		.wr-cover__aerial.is-photo { aspect-ratio: 4 / 3; }
		.wr-cover__head {
			position: absolute;
			left: $wr-pad;
			top: 26px;
			z-index: 2;
			margin-bottom: 0;
		}
		.wr-cover__low { margin-top: 18px; }
	}
}

/* 색면 분할(field)은 좁은 화면에서 **절대 위치를 푼다**. 색면·제목·목록을 모두 비율로
   앉히면 표지 높이가 화면에서 정해지는데(min(92vh, 100vw*1.15) = 390px 화면에서 448px),
   그 안의 목록 상자는 153px 뿐이라 다섯 행이 31px 로 눌린다. 행 높이만 줄고 글자는 그대로라
   앞 행 주소와 다음 행 이름이 243x17px 겹쳐 찍히고, 첫 행 금액과 마지막 행 주소가 상자
   밖으로 잘린다(Codex 리뷰 지적 + 실측 2026-09-16). 같은 이유로 긴 제목은 색면 위로
   자라 상호를 92x39px 덮는다. 좁은 화면에서는 색면이 활자만큼만 차지하고 목록이 그 아래로
   흐르게 둔다 — 지면 성격(위 색면, 아래 목록)은 그대로이고 높이만 내용이 정한다. */
@media (max-width: 859px) {
	.wr-page:not(.wr-pdf) .wr-cover--split {
		min-height: 0;
		padding-bottom: 26px;
		/* 비율로 깔던 색면 대신 머리·제목 덩어리가 직접 색을 입는다. */
		.wr-cover__field { display: none; }
		.wr-cover__head,
		.wr-cover__low {
			position: static;
			width: auto;
			align-self: stretch;
			background: var(--wr-accent);
			margin-left: -$wr-pad;
			margin-right: -$wr-pad;
			padding-left: $wr-pad;
			padding-right: $wr-pad;
		}
		.wr-cover__head { margin-top: -30px; padding-top: 30px; }
		.wr-cover__low { padding-bottom: 26px; }
		.wr-cover__gap { min-height: 14px; background: var(--wr-accent); align-self: stretch; margin: 0 -#{$wr-pad}; }
		.wr-cover__lots {
			position: static;
			margin-top: 20px;
			display: block;
			overflow: visible;
			/* 행 격자(번호 / 이름·주소 / 금액)는 그대로 둔다. block 으로 바꾸면 번호와
			   이름이 한 덩어리로 붙어 '01논현동 신축빌딩' 이 된다(실측). */
			li { padding: 10px 0; }
		}
		.wr-cover__band { margin-top: 24px; }
	}
}

/* 아주 좁은 화면. 표지 머리줄은 자간을 벌린 캡션 + 고정폭 워드마크라 폭이 줄어들지
   않는다(실측: 360px 에서 문서 폭이 384px 이 되어 가로 스크롤이 생겼다). */
@media (max-width: 400px) {
	.wr-page:not(.wr-pdf) {
		.wr-mark { width: 96px; height: 15px; }
		.wr-title { font-size: calc(26px * var(--wr-tscale, 1)); }
		/* 스위스 머리의 라벨 행은 네 칸이 `white-space: nowrap` 이라 좁은 화면에서 줄지
		   않는다. 390px 에서 합계 511px 이 되어 155px 이 낱장 밖으로 나가고, 발행 주차와
		   기간이 조용히 잘린다(실측 — `overflow: hidden` 이 잘라서 가로 스크롤도 안 생기고
		   화면만 봐서는 사고가 안 보인다). 가장 덜 쓰이는 칸부터 접는다. */
		.wr-cover--grid .wr-cover__raildate { display: none; }
		.wr-index__name { font-size: 14px; }
		.wr-index__price { font-size: 13px; }
		.wr-to { font-size: 14px; }
	}
	.wr-bar__mark { width: 66px; height: 10px; }
	.wr-bar__no { min-width: 30px; }
}

/* 태블릿 폭(859px 이하)에서도 스위스 머리 행은 한 줄에 다 못 선다(실측 768px 에서 넘침).
   라틴 캡션을 접어 브랜드와 주차만 남긴다. */
@media (max-width: 859px) {
	.wr-page:not(.wr-pdf) .wr-cover--grid .wr-cover__railcap { display: none; }
	/* 갤러리 머리의 라틴 세 자리도 390px 에서는 워드마크와 붙는다(운영 실측: "we'onusWEEKLY PROPERTY BRIEF"). */
	.wr-page:not(.wr-pdf) .wr-cover--gallery .wr-cover__railcap { display: none; }
}

/* ── 넓은 화면(860px 이상) ──────────────────────────────────────────────── */
@media (min-width: 860px) {
	.wr-page:not(.wr-pdf) {
		/* 표지는 폭 전체를 쓰고 글만 가운데 단에 세운다. 배경만 흘러넘치게 하는 방법은
		   안쪽 여백을 창 폭에서 계산하는 것이다(감싸개를 더하면 인쇄 구조가 바뀐다). */
		.wr-sheet--cover {
			padding-top: 68px;
			padding-bottom: 58px;
			padding-left: max(#{$wr-pad-lg}, calc((100% - #{$wr-web-max}) / 2 + #{$wr-pad-lg}));
			padding-right: max(#{$wr-pad-lg}, calc((100% - #{$wr-web-max}) / 2 + #{$wr-pad-lg}));
		}
		/* 절대 위치로 앉힌 표지 덩어리는 낱장 안쪽 여백을 모른다(절대 위치의 기준은 패딩
		   상자다). 넓은 화면에서 머리·제목·목록이 서로 다른 왼쪽 선에 서지 않도록 같은
		   식으로 다시 잡는다(실측: 머리 224px, 제목 24px 으로 갈라져 있었다). */
		.wr-cover--split .wr-cover__low,
		.wr-cover--split .wr-cover__lots {
			left: max(#{$wr-pad-lg}, calc((100% - #{$wr-web-max}) / 2 + #{$wr-pad-lg}));
		}
		.wr-cover--split .wr-cover__lots {
			right: max(#{$wr-pad-lg}, calc((100% - #{$wr-web-max}) / 2 + #{$wr-pad-lg}));
		}
		/* 표지. 제목을 키우고 마크에 지면을 준다. */
		.wr-title { font-size: calc(54px * var(--wr-tscale, 1)); }
		/* 마크와 활자 사이의 큰 여백은 **모노그램 지면만의 것**이다. 골격을 안 짚으면 이
		   미디어 쿼리(0,3,0)가 골격별 `.wr-cover--plate .wr-cover__gap`(0,2,0)을 이겨, 먹지
		   도판에서 사진과 제목 사이가 268px 비었다(실측 2026-09-17). */
		.wr-cover--mark .wr-cover__gap { min-height: 268px; }
		.wr-cover__head .wr-mark--dark { width: 160px; height: 24px; }
		.wr-cover__mark { width: 36vw; max-width: 356px; right: -7vw; top: 11%; }
		.wr-cover__sum { font-size: 13px; }
		/* 색인은 표지와 같은 단을 쓰되 두 단으로 편다. */
		.wr-index {
			padding-top: 46px;
			padding-bottom: 56px;
			padding-left: max(#{$wr-pad-lg}, calc((100% - #{$wr-web-max}) / 2 + #{$wr-pad-lg}));
			padding-right: max(#{$wr-pad-lg}, calc((100% - #{$wr-web-max}) / 2 + #{$wr-pad-lg}));
		}
		.wr-index__list { grid-template-columns: 1fr 1fr; gap: 20px 40px; }
		.wr-index__name { font-size: 17px; }
		.wr-index__price { font-size: 16px; }

		.wr-sheet--body { padding-left: $wr-pad-lg; padding-right: $wr-pad-lg; }
		.wr-sheet__inner { padding-top: 40px; }
		.wr-foot { padding-left: $wr-pad-lg; padding-right: $wr-pad-lg; }
		.wr-bar__in { padding-left: $wr-pad-lg; padding-right: $wr-pad-lg; }

		/* 사진은 왼쪽, 정보는 오른쪽. 인쇄물과 같은 구성으로 본다.
		   **왼쪽 단은 건물 사진이 카드 높이를 꽉 채우고, 위치도는 오른쪽 글 아래에 든다**
		   (2026-09-17 사용자 지시 "왼쪽은 무조건 건물 사진으로 꽉 채우고 네이버 지도가 오른쪽
		   텍스트 아래에"). 카드 높이는 오른쪽(글 + 지도)이 정하고 사진이 거기에 맞춰 잘린다 —
		   두 단의 아래끝이 늘 맞아 카드 안에 빈 자리가 없다. */
		.wr-lot {
			display: grid;
			grid-template-columns: 52% minmax(0, 1fr);
			grid-template-rows: auto auto;
			column-gap: 36px;
			row-gap: 18px;
			margin-bottom: 64px;
		}
		.wr-lot__media { grid-column: 1; grid-row: 1 / 3; min-height: 0; }
		/* 사진이 단의 남는 높이를 전부 갖는다(썸네일 줄은 아래 고정). 비율 고정을 푼다. */
		.wr-lot__photo { flex: 1 1 auto; aspect-ratio: auto; min-height: 300px; }
		.wr-lot__thumbs { flex: 0 0 auto; }
		.wr-lot__body { grid-column: 2; grid-row: 1; padding-top: 0; }
		.wr-lot__spot { grid-column: 2; grid-row: 2; margin: 0; }
		.wr-lot__spotimg { aspect-ratio: 4 / 3; }
		.wr-lot__name { font-size: 27px; }
		.wr-lot__price { font-size: 32px; }
		.wr-lot__rate { font-size: 14px; }
		.wr-spec__row { grid-template-columns: 100px minmax(0, 1fr); }
	}
}

/* ── 움직임을 줄이도록 설정한 사람 ───────────────────────────────────────
   전환은 전부 끄되, 상태 표시는 색·괘선으로 그대로 남는다(움직임이 유일한
   알림 수단이면 안 된다). */
@media (prefers-reduced-motion: reduce) {
	.wr-page:not(.wr-pdf) *,
	.wr-lb * {
		transition-duration: 1ms !important;
		animation-duration: 1ms !important;
	}
	.wr-page.wr-anim:not(.wr-pdf) .wr-lot:not(.is-in) { opacity: 1; transform: none; }
}

/* ── 서버 PDF (A4 낱장) ──────────────────────────────────────────────────
   여백은 지면 안쪽에서만 준다. puppeteer 쪽 margin 은 0 이어야 하며, 두 곳에서
   주면 낱장 높이 계산이 어긋나 빈 페이지가 생긴다(DESIGN.md Layout). */
.wr-pdf {
	background: var(--wr-paper);

	/* 왼쪽 띠. 현장 사진과 그 자리의 항공사진을 세로로 묶는다.
	   **지면 왼쪽 재단선까지 나간다**(음수 마진이 낱장 안쪽 여백 14mm 를 되돌린다).
	   낱장의 `overflow: hidden` 이 재단선에서 끊는다. 이 한 수가 지면을 위아래 스택이
	   아니라 좌우 분할로 만든다 — 예전 조판이 'AI 티' 로 읽힌 이유가 전폭 블록을 위에서
	   아래로만 쌓은 것이었다(2026-09-15 사용자 지적). */
	.wr-lot__media {
		/* 격자 칸(84mm)에 왼쪽 재단 여백 14mm 를 더해 사진이 재단선까지 나간다.
		   축소된 낱장(`--wr-fit` < 1)에서는 안쪽이 그 배율로 줄어드니 여백을 배율로 나눠
		   되돌린다 — 안 그러면 재단선과 사진 사이가 2~3mm 뜬다(실측 0.817 배 낱장). */
		width: calc(100% + 14mm / var(--wr-fit, 1));
		margin-left: calc(-14mm / var(--wr-fit, 1));
		display: flex;
		flex-direction: column;
		min-width: 0;
		min-height: 0;
	}

	.wr-sheet {
		box-sizing: border-box;
		width: 210mm;
		height: 297mm;
		margin: 0;
		overflow: hidden;
		/* overflow:hidden 은 그리기만 자른다. 페이지 나눔은 레이아웃 오버플로 기준이라
		   안쪽이 넘치면 빈 페이지가 생긴다. contain 으로 낱장을 격리한다(실측). */
		contain: size layout paint;
		break-after: page;
		page-break-after: always;
		display: flex;
		flex-direction: column;
	}
	.wr-sheet:last-of-type {
		break-after: auto;
		page-break-after: auto;
	}

	/* 표지 — 마크가 주인공이다.
	   마크는 지름 158mm 로 지면의 위·오른쪽 재단선 밖으로 나간다. 낱장의
	   `overflow: hidden` 이 그것을 재단선에서 끊는다 — 그 규칙을 지우면 마크가
	   낱장 밖으로 삐져나가 인쇄에서 빈 페이지가 한 장 따라붙는다. */
	.wr-sheet--cover { padding: 16mm 18mm 13mm; }
	.wr-mark { width: 150px; height: 23px; }
	/* 시안 01 을 픽셀로 재서 맞춘 값이다(2026-09-16 2차). 시안의 원은 지름이 지면 폭의
	   0.616(=129mm)이고, **위와 오른쪽 두 곳**에서 재단선 밖으로 나간다. 지름만 키우고
	   지면 안에 가둬 두면(1차 값 116mm) 원이 통째로 보여 도장처럼 읽히고, 표지에서
	   가장 큰 그림이 지면 가운데 떠 있는 어정쩡한 자리를 차지한다. 반대로 158mm 까지
	   키우면 원의 절반이 잘려 무슨 도형인지 모를 곡선이 된다. */
	.wr-cover__mark {
		width: 129mm;
		max-width: none;
		right: -11mm;
		top: -7mm;
	}
	.wr-cover__head .wr-mark--dark { width: 38mm; height: 5.76mm; }
	.wr-cover__head .wr-caption { margin-top: 2.6mm; font-size: 10.5px; }
	.wr-cover__gap { min-height: 0; }
	.wr-period { font-size: 12.5px; }
	.wr-to { font-size: 15px; }
	.wr-title { font-size: calc(51px * var(--wr-tscale, 1)); margin-top: 4.6mm; line-height: 1.2; }
	.wr-cover__sum { font-size: 11px; margin-top: 4.6mm; }
	.wr-cover__band {
		margin-top: 7mm;
		padding-top: 3.6mm;
	}
	.wr-cover__corp { font-size: 10.5px; }
	.wr-cover__note { font-size: 9.5px; max-width: 158mm; }

	/* 수록 물건 색인 — 속장 첫 장 머리에 얹힌다(낱장을 따로 쓰면 다섯 건짜리
	   리포트에서 한 장이 목록만으로 채워진다). 두 단으로 편다. */
	.wr-index { padding: 0 0 6mm; background: none; }
	.wr-index__head { margin-bottom: 3.4mm; padding-bottom: 2mm; font-size: 11px; }
	.wr-index__list {
		grid-template-columns: 1fr 1fr;
		gap: 3.4mm 10mm;
	}
	/* 물건이 많으면 세 단. 두 단으로 두면 열세 건째부터 목록이 낱장의 절반을 먹어
	   첫 장에 물건이 한 건도 못 들어간다. */
	.wr-index--three .wr-index__list { grid-template-columns: repeat(3, 1fr); gap: 3mm 7mm; }
	.wr-index--three .wr-index__name { font-size: 12.5px; }
	.wr-index--three .wr-index__price { font-size: 12px; }
	.wr-index__no { font-size: 12.5px; }
	.wr-index__name { font-size: 13.5px; }
	.wr-index__price { font-size: 13px; }
	.wr-index__area { font-size: 10.5px; }

	/* 속장 */
	.wr-sheet--body { padding: 12mm 14mm var(--wr-print-reserve); }
	.wr-run {
		display: flex;
		align-items: center;
		gap: 12px;
		flex: 0 0 auto;
		padding-bottom: 5mm;
	}
	/* 머리는 **양끝 정렬이고 그 사이는 비어 있다**(시안 04). 괘선으로 이으면 로트 번호와
	   상호가 한 덩어리의 머리띠가 되어, 매 장 같은 장식이 반복되는 웹 헤더처럼 읽힌다. */
	.wr-run__line {
		flex: 1 1 auto;
		height: 1px;
		background: transparent;
	}
	.wr-sheet__inner {
		flex: 1 1 auto;
		min-height: 0;
		padding-top: 0;
		display: flex;
		flex-direction: column;
		gap: 9mm;
	}
	.wr-brief {
		flex: 0 0 auto;
		margin-bottom: 0;
		padding: 5mm 0 5.5mm;
	}
	.wr-brief__body { font-size: 12.5px; line-height: 1.75; }

	/* 대표 사진은 흐름에서 뺀다. `height:100%` 는 부모 높이가 확정되지 않으면 auto 로
	   풀려 **원본 사진의 세로 길이**가 로트 높이에 더해진다. 그래서 세로로 긴 사진
	   (1200x3000)이 실린 로트는 측정 353px, 출력 756px 로 벌어져 아래 로트의 문장이
	   잘렸다(Codex 리뷰 실측). 절대 배치면 측정과 출력의 높이 계산이 같아진다. */
	.wr-lot__photo img {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.wr-lot {
		/* **늘이지 않고 줄이지도 않는다.** 늘리면 지면을 채우려고 사진만 세로로
		   늘어나고(실측 80x150mm 극단 크롭) 글 옆은 통째로 비어, 지면 절반이 흰 종이로
		   남는다. 이제 카드 높이는 글이 정하고 사진이 거기에 맞춰 잘린다 — 두 단이 늘
		   같은 높이라 카드 안에 빈 자리가 생기지 않는다. 남는 지면은 카드가 자라서 받고
		   (buildSheets 의 CARD_MAX_ADD), 그러고도 남으면 여백으로 둔다.
		   shrink 는 예전대로 0 이다. 살려 두면 남는 몇 mm 를 카드에서 빼앗아 본문
		   끝줄이 조용히 잘린다(실측 7mm 압축). */
		flex: 0 0 auto;
		min-height: 62mm;
		margin-bottom: 0;
		/* **격자다**(2026-09-17 사용자 지시 "여백 없이 딱 맞아 떨어지게. 왼쪽은 무조건 건물
		   사진으로 꽉 채우고 네이버 지도가 오른쪽 텍스트 아래에"). 왼쪽 단 84mm(+재단 14mm)는
		   현장 사진 하나가 카드 높이를 다 쓴다. 오른쪽 단은 위가 글(auto), 아래가 위치도이고
		   위치도가 남는 높이를 전부 받는다(1fr). 카드가 지면을 채우려고 자랄 때(row.h) 자란
		   몫은 전부 지도로 간다 — 그래서 지면에 빈 자리가 없다. 사진은 세로로 긴 크롭이 된다
		   (84x252mm, 1:3) — 건물 사진이라 세로 크롭이 자연스럽고, 그것이 지시다. */
		display: grid;
		grid-template-columns: 84mm minmax(0, 1fr);
		grid-template-rows: auto minmax(50mm, 1fr);
		column-gap: 8mm;
		row-gap: 6mm;
	}
	.wr-lot__media { grid-column: 1; grid-row: 1 / 3; }
	/* 사진이 왼쪽 단을 전부 갖는다(단 높이는 격자가 정한다). */
	.wr-lot__photo { flex: 1 1 auto; min-height: 40mm; }
	.wr-lot__nophoto { position: absolute; inset: 0; height: auto; }
	/* 위치도는 오른쪽 단 아래 칸. 칸 높이(=남는 지면)를 전부 쓴다. */
	.wr-lot__spot { grid-column: 2; grid-row: 2; margin: 0; padding: 0; display: flex; flex-direction: column; min-height: 0; }
	/* 자연 높이는 50mm(측정 패스가 재는 값), 실제 높이는 칸이 주는 만큼(flex-grow). */
	.wr-lot__spotimg { aspect-ratio: auto; flex: 1 1 auto; height: auto; min-height: 50mm; }
	.wr-lot__pin { width: 32px; height: 41px; }
	.wr-lot__pinno { font-size: 12.5px; }
	.wr-lot__spotcap { flex: 0 0 auto; margin-top: 1.8mm; font-size: 9px; }
	/* 낱장의 마지막 카드는 남는 지면을 받는다(그 몫은 격자 1fr 행, 곧 위치도로 간다).
	   예전에 grow 를 금지한 이유는 자란 몫이 왼쪽 사진에만 가서 글 옆이 비었기 때문인데,
	   이제는 오른쪽 단 아래의 지도가 받으므로 두 단이 같은 바닥에 선다. 축소된 낱장은
	   안쪽 폭이 1/f 로 넓어져 글이 덜 접히고 잰 높이보다 짧아지는데, 그 차이(실측 8mm)도
	   이 grow 가 메운다. 측정 낱장과 흐름 낱장은 제외한다 — 측정 중에 자라면 한 건짜리
	   리포트의 카드 높이가 지면 높이로 재어져 조판이 어긋난다. */
	.wr-sheet--body:not(.wr-sheet--measure):not(.wr-sheet--flow) .wr-sheet__inner > .wr-lot:last-child {
		flex-grow: 1;
	}

	.wr-lot__body { grid-column: 2; grid-row: 1; padding-top: 0; min-width: 0; }
	/* **번호 · 물건명 · 금액이 세 층위로 벌어진다**(시안 04). 예전 값(27/20/22px)은 셋이
	   거의 같아서, 지면을 펼쳤을 때 무엇을 먼저 읽어야 하는지가 없었다 — 도록이 아니라
	   제원표로 읽혔다. 금액이 지면에서 가장 큰 활자다. */
	.wr-lot__head { gap: 8px; margin-bottom: 0; }
	.wr-lot__no { font-size: 52px; }
	.wr-lot__tags { font-size: 10.5px; }
	.wr-lot__name { font-size: 33px; }
	.wr-lot__addr { font-size: 12px; margin-top: 3px; }
	.wr-lot__figure { margin-top: 10px; gap: 0 12px; }
	.wr-lot__price { font-size: 56px; }
	.wr-lot__rate { font-size: 13px; }
	/* 제원 격자. 지면에서는 **두 단으로 접는다** — 여섯 항목을 세로로 늘어놓으면 글 단이
	   제원만으로 반을 먹는다. 항목이 없어도 줄표로 자리를 지키므로 두 단이어도 같은
	   항목이 같은 칸에 온다. 라벨은 값 **위**에 온다(시안 04). */
	.wr-spec {
		margin-top: 11px;
		padding-top: 8px;
		column-gap: 8mm;
		row-gap: 5.4mm;
	}
	.wr-spec__row { padding: 0; }
	.wr-spec__row--wide { grid-column: 1 / -1; }
	.wr-spec dt { font-size: 10px; line-height: 1.4; }
	/* 글 단이 90mm 라 한 칸이 41mm 다. 19px 이면 '제2종근린생활시설' 이 45mm 로 접힌다. */
	.wr-spec dd { font-size: 17px; line-height: 1.3; }
	/* 특징은 사실 나열, 코멘트는 해석이다. 크기가 같으면 어디까지가 사실인지 흐려진다. */
	.wr-lot__features { margin-top: 10px; }
	.wr-lot__features li { font-size: 12px; line-height: 1.62; }
	.wr-lot__note { font-size: 12.5px; line-height: 1.72; margin-top: 8px; }

	/* 흐름 낱장. 한 건이 A4 한 장보다 큰 예외에서만 쓴다. 지면 고정을 풀어 브라우저가
	   여러 장에 걸쳐 흘리게 하고, 사진은 위로 올려 세로로 쌓는다(가로 2단인 채로 흘리면
	   사진이 지면 높이만큼 늘어나 페이지 나눔이 엉킨다). 내용은 한 줄도 잘리지 않는다. */
	.wr-sheet--flow {
		height: auto;
		min-height: 297mm;
		contain: none;
		overflow: visible;
		.wr-sheet__inner { display: block; }
		.wr-lot {
			display: flex;
			flex: 0 0 auto;
			flex-direction: column;
			break-inside: auto;
			page-break-inside: auto;
			min-height: 0;
		}
		/* 세로로 쌓이므로 격자를 풀고 재단도 하지 않는다(예외 경로라 안쪽에 둔다). */
		.wr-lot__media { flex: 0 0 auto; width: auto; margin-left: 0; }
		.wr-lot__photo { height: 72mm; flex: 0 0 auto; }
		.wr-lot__body { padding-top: 6mm; }
		.wr-lot__spot { flex: 0 0 auto; margin-top: 6mm; }
		.wr-lot__spotimg { flex: 0 0 auto; height: 64mm; }
		.wr-lot + .wr-lot { margin-top: 9mm; }
	}

	/* 측정 패스. 낱장 크기(= 가용 높이)는 실제와 같아야 하므로 건드리지 않는다.
	   카드는 이제 렌더에서도 `flex: 0 0 auto` 라 **재는 높이와 찍히는 높이가 같다**
	   (예전에는 렌더에서만 늘어나서 둘이 달랐다). 넘치는 부분은 화면에서 잘려 보이지만
	   레이아웃 높이는 그대로라 측정에는 지장이 없다. */
	.wr-sheet--measure {
	}

	/* 꼬리말. 표지에는 붙이지 않는다(DESIGN.md Layout). */
	.wr-foot-line {
		flex: 0 0 auto;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;
		border-top: 1px solid var(--wr-line);
		margin-top: 4mm;
		padding: 3mm 0 1mm;
		@include dt-type-doc-label;
		color: var(--wr-ink-soft);
	}
	.wr-foot-line__no { font-weight: 700; }
	/* 민·형사 경고는 괘선 바로 밑, 상호·쪽번호 줄 위에 온다. 측정 중에도 그려지므로 가용 높이에서
	   빠진 채로 잰다(위 '측정 중에도 그린다' 주석). */
	.wr-foot-line__legal {
		flex: 0 0 100%;
		margin: 0 0 1.6mm;
		font-size: 7.5px;
		line-height: 1.45;
		font-weight: 600;
		letter-spacing: -0.01em;
		text-transform: none;
		color: var(--wr-legal);
	}

	/* 흐름 낱장의 표(인쇄 전용). */
	table.wr-flowtab {
		width: 100%;
		border-collapse: collapse;
		border-spacing: 0;
		td { padding: 0; vertical-align: top; }
	}

	/* 표지의 경고. 회색 고지(9.5px) 바로 밑. */
	.wr-legal { font-size: 9px; line-height: 1.5; max-width: 158mm; margin-top: 2mm; }

	/* 워터마크는 지면 크기로 고정한다(웹의 vw 는 렌더러 창 폭을 따른다). */
	/* 42mm 이면 회전한 글자 폭이 지면 안에 든다(46mm 는 대체 서체에서 양끝이 닿을 수 있다). */
	.wr-wm span { font-size: 42mm; -webkit-text-stroke-width: 0.45mm; }

	/* 뒤표지 */
	.wr-sheet--back {
		padding: 16mm 18mm 14mm;
		background: var(--wr-paper);
		color: var(--wr-ink);
	}
	.wr-sheet--back .wr-mark--dark { width: 38mm; height: 5.76mm; flex: 0 0 auto; }
	.wr-back__gap { flex: 1 1 auto; }
	.wr-back__legal {
		flex: 0 0 auto;
		border: 1.2px solid var(--wr-legal);
		padding: 6mm 7mm;
		color: var(--wr-legal);
		strong {
			display: block;
			font-size: 13px;
			font-weight: 700;
			margin-bottom: 2.4mm;
		}
		p {
			margin: 0;
			font-size: 11.5px;
			line-height: 1.7;
			font-weight: 600;
		}
	}
	.wr-back__corp {
		flex: 0 0 auto;
		margin-top: 9mm;
		padding-top: 3.6mm;
		border-top: 1px solid var(--wr-line);
		font-size: 10.5px;
		strong { margin-right: 8px; }
		span { margin-right: 8px; color: var(--wr-ink-soft); }
	}
	.wr-back__note {
		flex: 0 0 auto;
		margin: 2mm 0 0;
		font-size: 9.5px;
		line-height: 1.6;
		color: var(--wr-ink-soft);
	}
}

/* ══════════════════════════════════════════════════════════════════════════
   조판 테마 (2026-09-16)

   지면은 일곱 벌이다. 색만 바뀌는 것이 아니라 **표지 골격이 통째로 다르다** — 색상만
   스물네 벌을 내고 "디자인 전체를 바꾼 시안을 달라" 는 답을 받은 뒤의 결론이다.

   위의 모든 규칙은 이미 `var(--wr-*)` 로 색을 읽는다. 여기서는 그 변수에 값을 넣고,
   표지 골격마다 필요한 배치만 더한다. 그래서 새 테마를 더할 때 손대는 곳이
   **이 한 구역과 테마 목록(utils/reportThemes.js)** 뿐이다.

   변수 이름은 역할이지 색이 아니다(paper/ink/accent). `--wr-cream` 같은 이름을 쓰면
   먹지 테마에서 "크림이 검정" 이 되어 다음 사람이 읽을 수 없다.
   ══════════════════════════════════════════════════════════════════════════ */
.wr-page {
	--wr-paper: #{$dt-color-doc-cream};
	--wr-paper-deep: #{$dt-color-doc-cream-deep};
	--wr-line: #{$dt-color-doc-cream-line};
	--wr-ink: #{$dt-color-doc-ink};
	--wr-ink-soft: #{$dt-color-doc-ink-soft};
	--wr-accent: #{$dt-color-doc-burgundy};
	--wr-accent-deep: #{$dt-color-doc-burgundy-deep};
	--wr-gold: #{$dt-color-doc-gold};
	--wr-surface: #{$dt-color-doc-surface};
	--wr-mark: #{$dt-color-doc-burgundy};
	--wr-tex: 0.42;              /* 종이 결. 먹지 계열은 0(곱하기가 더 어둡게만 만든다) */
	--wr-photo: none;            /* 본문 사진 보정. 기본은 손대지 않는다 */
	--wr-legal: #c4161c;         /* 민·형사 경고. 밝은 지면에서 5:1 이상 */
	/* A4 경고 두 줄(5.76mm) + 바닥 3mm + 본문과의 간격 3.24mm. 조판 측정도 같은 여백을 쓴다. */
	--wr-print-reserve: 12mm;
}

/* 프리미엄 다크 — 먹지에 금 괘선. 사진이 지면의 유일한 빛이다. */
.wr-page.wr-t-noir {
	--wr-paper: #15130f;
	--wr-paper-deep: #1f1c16;
	--wr-line: #3a3228;
	--wr-ink: #f2ece0;
	--wr-ink-soft: #b3a692;
	--wr-accent: #c9a253;
	--wr-accent-deep: #a8823f;
	--wr-gold: #c9a253;
	--wr-surface: #100e0b;
	--wr-mark: #c9a253;
	--wr-tex: 0;
	--wr-pin-ink: #15130f;       /* 금색 핀 위의 번호. 흰 글자는 2.3:1 로 안 읽힌다 */
	--wr-legal: #ff6b61;         /* 먹지 위의 빨강. 기본 빨강은 2.6:1 로 묻힌다 */
}
/* 항공지도 전면 — 지면은 도시의 회청색을 따라간다. 표지만 어둡고 속장은 밝다. */
.wr-page.wr-t-aerial {
	--wr-paper: #f1f4f6;
	--wr-paper-deep: #e1e7ec;
	--wr-line: #c9d2d9;
	--wr-ink: #16202a;
	--wr-ink-soft: #55636f;
	--wr-accent: #0f6b8c;
	--wr-accent-deep: #0a4e67;
	--wr-gold: #8ba2b0;
	--wr-surface: #ffffff;
	--wr-mark: #0f6b8c;
	--wr-tex: 0;
}
/* 스위스 그리드 — 흰 종이, 검정 활자, 주황 하나. 괘선이 격자를 드러낸다. */
.wr-page.wr-t-swiss {
	--wr-paper: #ffffff;
	--wr-paper-deep: #f1f1f1;
	--wr-line: #d5d5d5;
	--wr-ink: #111111;
	--wr-ink-soft: #5b5b5b;
	--wr-accent: #e8590c;
	--wr-accent-deep: #c24a09;
	--wr-gold: #111111;
	--wr-surface: #ffffff;
	--wr-mark: #e8590c;
	--wr-tex: 0;
}
/* 색면 분할 — 딥그린 색면 아래로 목록이 내려온다. */
.wr-page.wr-t-field {
	--wr-paper: #f6f3ea;
	--wr-paper-deep: #e8e3d5;
	--wr-line: #d5cebc;
	--wr-ink: #16241c;
	--wr-ink-soft: #4d6056;
	--wr-accent: #1f4d38;
	--wr-accent-deep: #143a29;
	--wr-gold: #9c8f5f;
	--wr-surface: #fbf9f3;
	--wr-mark: #1f4d38;
	--wr-tex: 0.30;
}
/* 갤러리 도판 — 여백이 주인공이라 색을 거의 쓰지 않는다. 지면은 흰 종이가 아니라
   **따뜻한 미색**이다(시안 07). 순백으로 두면 사진만 색이 있는 화면이 되어, 여백이
   디자인이 아니라 덜 채운 자리로 읽힌다(실측 비교). */
.wr-page.wr-t-gallery {
	--wr-paper: #f3f0e9;
	--wr-paper-deep: #e7e3d9;
	--wr-line: #dcd6c9;
	--wr-ink: #1c1b19;
	--wr-ink-soft: #77726b;
	--wr-accent: #6b6257;
	--wr-accent-deep: #4e4740;
	--wr-gold: #b9ad97;
	--wr-surface: #ffffff;
	--wr-mark: #6b6257;
	--wr-tex: 0.24;
}
/* 카탈로그 모자이크 — 먹지 위 사진 격자, 상호는 테라코타 색면. */
.wr-page.wr-t-mosaic {
	--wr-paper: #141414;
	--wr-paper-deep: #1e1e1e;
	--wr-line: #343434;
	--wr-ink: #f3f1ec;
	--wr-ink-soft: #a8a39b;
	--wr-accent: #c2643a;
	--wr-accent-deep: #9d4e2c;
	--wr-gold: #c2643a;
	--wr-surface: #0f0f0f;
	--wr-mark: #c2643a;
	--wr-tex: 0;
	--wr-legal: #ff6b61;
}

/* 먹지 계열에서는 워드마크가 흰 판이어야 한다. 검정 PNG 를 그대로 두면 지면에 묻어
   상호가 사라진다(마크는 투명 배경 PNG 라 색을 반전할 방법이 이것뿐이다). */
.wr-page.wr-t-noir .wr-mark--dark,
.wr-page.wr-t-mosaic .wr-mark--dark,
.wr-cover--aerial .wr-mark--dark {
	background-image: url(../assets/images/weonus_wordmark_wh.png);
	opacity: 1;
}

/* ══════════════════════════════════════════════════════════════════════════
   속장 테마별 조판 (2026-09-17 사용자 지시 "이런식으로 해서 테마에 맞게 전체 제대로 구성")

   골격은 일곱 지면이 같다 — 왼쪽 단은 건물 사진이 카드 높이를 꽉 채우고, 오른쪽 단은 글
   아래에 위치도가 남는 높이를 받는다. 그 골격 위에 **표지가 쓰는 언어를 속장에도 그대로**
   얹는다: 금선 도판은 사진과 지도에 금 괘선을 두르고, 항공 지면은 위치도가 위성 사진이며,
   스위스 그리드는 굵은 먹선과 흑백 지도, 색면 분할은 번호·이름·금액 덩어리가 색면 위에,
   갤러리는 도판이 여백 안에 캡션과 함께, 사진 격자는 왼쪽 단이 격자다. 색만 바꾸면 같은
   문서가 일곱 벌 나온다는 것을 표지에서 이미 확인했다(2026-09-16).

   여기 규칙은 웹과 인쇄 양쪽에 걸린다. 인쇄 치수는 `.wr-pdf` 를 함께 짚는다.
   ══════════════════════════════════════════════════════════════════════════ */

/* ② 금선 도판(noir) — 사진과 지도를 금 괘선으로 두른다(표지의 도판과 같은 처리).
   괘선은 그림 **안쪽**에 얹히는 각인이지 상자가 아니다 — 카드 둘레에 두르지 않는다. */
.wr-page.wr-t-noir {
	.wr-lot__photo::after,
	.wr-lot__spotimg::after,
	.wr-lot__cell::after {
		content: '';
		position: absolute;
		inset: 8px;
		border: 1px solid var(--wr-gold);
		opacity: 0.85;
		pointer-events: none;
	}
	.wr-lot__price { color: var(--wr-gold); }
	.wr-spec { border-top-color: var(--wr-gold); }
	.wr-brief { border-color: var(--wr-gold); }
	.wr-lot__note { border-left: 1px solid var(--wr-gold); padding-left: 12px; }
	/* 흰 지도가 먹지에서 뚫려 보이지 않게 채도와 밝기를 조금 누른다. 라벨은 읽힌다. */
	.wr-lot__spotimg img { filter: saturate(0.7) brightness(0.94); }
	.wr-lot__spotimg { box-shadow: none; }
	&.wr-pdf {
		.wr-lot__photo::after,
		.wr-lot__spotimg::after { inset: 3mm; }
		.wr-run__line { background: var(--wr-gold); opacity: 0.55; height: 1px; }
		.wr-foot-line { border-top-color: var(--wr-gold); }
	}
}

/* ③ 항공지도 전면(aerial) — 위치도가 위성 사진(라벨 하이브리드)이고 번호는 표지와 같은
   원형 배지다. 머리와 꼬리에 가는 청색 괘선. */
.wr-page.wr-t-aerial {
	.wr-lot__spotimg img { filter: saturate(1.05) contrast(1.06); }
	.wr-lot__spotimg { box-shadow: inset 0 0 0 1px rgba(15, 107, 140, 0.45); }
	.wr-lot__pinbody { stroke: #fff; }
	.wr-spec { border-top: 1px solid var(--wr-accent); }
	.wr-brief { border-top-color: var(--wr-accent); }
	.wr-lot__note { border-left: 2px solid var(--wr-accent); padding-left: 12px; }
	&.wr-pdf {
		.wr-run { border-bottom: 1px solid var(--wr-accent); padding-bottom: 3mm; margin-bottom: 2mm; }
		.wr-foot-line { border-top-color: var(--wr-accent); }
		.wr-lot__pin--badge { width: 52px; height: 52px; }
		.wr-lot__pin--badge .wr-lot__pinno { font-size: 13.5px; }
	}
}

/* ④ 스위스 그리드(swiss) — 굵은 먹선, 괘선이 드러나는 제원 격자, 번호 매긴 특징, 흑백 지도에
   주황 핀 하나. 지면이 검정·흰색·주황 셋으로만 선다. */
.wr-page.wr-t-swiss {
	.wr-lot__body { border-top: 2px solid var(--wr-ink); padding-top: 14px; }
	.wr-lot__no { color: var(--wr-accent); }
	.wr-spec { border-top: 2px solid var(--wr-ink); padding-top: 0; row-gap: 0; column-gap: 20px; }
	.wr-spec__row { border-bottom: 1px solid var(--wr-line); padding: 8px 0 7px; }
	.wr-spec dt { color: var(--wr-ink); font-size: 10.5px; }
	.wr-lot__features {
		counter-reset: wrf;
		li {
			padding-left: 24px;
			text-indent: -24px;
			color: var(--wr-ink);
			&::before {
				counter-increment: wrf;
				content: counter(wrf, decimal-leading-zero);
				display: inline-block;
				width: 24px;
				text-indent: 0;
				margin-right: 0;
				opacity: 1;
				color: var(--wr-accent);
				font-weight: 600;
				font-variant-numeric: tabular-nums;
			}
		}
	}
	.wr-lot__note { border-left: 3px solid var(--wr-accent); padding-left: 12px; }
	.wr-lot__spotimg { box-shadow: inset 0 0 0 1px var(--wr-ink); }
	.wr-lot__spotimg img { filter: grayscale(1) contrast(1.08); }
	.wr-brief { border-top: 2px solid var(--wr-ink); }
	.wr-index__head { border-bottom: 2px solid var(--wr-ink); color: var(--wr-ink); }
	&.wr-pdf {
		.wr-lot__body { border-top: 0; padding-top: 0; }
		.wr-run { border-bottom: 2px solid var(--wr-ink); padding-bottom: 2.6mm; margin-bottom: 1.4mm; }
		.wr-run .wr-caption { color: var(--wr-ink); }
		.wr-foot-line { border-top: 2px solid var(--wr-ink); }
		.wr-spec__row { padding: 2.2mm 0 2mm; }
	}
}

/* ⑤ 색면 분할(field) — 번호·이름·주소·금액 덩어리가 딥그린 색면 위에 앉고 그 아래가 지면.
   표지의 위 절반이 색면이고 아래가 목록인 것과 같은 분할이다. 색면은 오른쪽 재단선까지
   나간다(왼쪽 사진이 왼쪽 재단선까지 나가는 것과 짝). */
.wr-page.wr-t-field {
	--wr-pin-ink: #f6f3ea;
	.wr-lot__top {
		background: var(--wr-accent);
		color: #f6f3ea;
		padding: 16px 20px 18px;
		margin-bottom: 14px;
	}
	.wr-lot__top .wr-lot__no,
	.wr-lot__top .wr-lot__name,
	.wr-lot__top .wr-lot__price { color: #f6f3ea; }
	.wr-lot__top .wr-lot__tags,
	.wr-lot__top .wr-lot__addr,
	.wr-lot__top .wr-lot__rate { color: rgba(246, 243, 234, 0.72); }
	.wr-spec { border-top: 0; padding-top: 0; }
	.wr-lot__note { border-left: 2px solid var(--wr-accent); padding-left: 12px; }
	.wr-lot__spotimg img { filter: saturate(0.72); }
	.wr-index__head { color: var(--wr-accent); }
	&.wr-pdf {
		.wr-lot__top {
			/* 글 단 오른쪽 끝이 지면 여백선이라 14mm 를 되돌리면 재단선까지 나간다. 축소 낱장은
			   왼쪽 사진과 같은 이유로 배율로 나눈다. */
			margin: 0 calc(-14mm / var(--wr-fit, 1)) 5mm 0;
			padding: 5.5mm calc(14mm / var(--wr-fit, 1)) 6mm 6mm;
		}
		.wr-lot__spot { margin-right: 0; }
	}
}

/* ⑥ 갤러리 도판(gallery) — 도판은 재단선까지 나가지 않고 여백 안에 앉는다. 그 밑에 캡션.
   활자는 조용하다: 번호는 캡션 크기, 이름은 고딕 600, 금액은 크되 가볍다. 괘선을 거의 쓰지
   않는다 — 구획은 여백이 한다. */
.wr-page.wr-t-gallery {
	.wr-lot__no {
		font-family: Pretendard, sans-serif;
		font-weight: 600;
		font-size: 13px;
		letter-spacing: 0.06em;
		color: var(--wr-ink-soft);
	}
	.wr-lot__name { font-family: Pretendard, sans-serif; font-weight: 600; letter-spacing: -0.02em; }
	/* 금액은 크되 가볍다(시안: 가는 활자). 번들 글꼴에 400/600 만 있어 400 을 쓴다. */
	.wr-lot__price { font-family: Pretendard, sans-serif; font-weight: 400; letter-spacing: -0.03em; }
	.wr-spec { border-top: 0; padding-top: 4px; }
	.wr-lot__note { color: var(--wr-ink-soft); }
	.wr-lot__spotimg { box-shadow: none; }
	.wr-lot__spotimg img { filter: saturate(0.45) contrast(0.98); }
	.wr-lot__features li { color: var(--wr-ink-soft); }
	.wr-brief { border: 0; padding-left: 0; }
	.wr-index__head { border-bottom: 0; }
	&.wr-pdf {
		.wr-lot__media { width: 100%; margin-left: 0; }
		.wr-lot { column-gap: 10mm; }
		.wr-lot__photocap { margin-top: 2mm; font-size: 9.5px; }
		.wr-foot-line { border-top: 0; }
		.wr-lot__no { font-size: 11px; }
		.wr-lot__name { font-size: 28px; }
		.wr-lot__price { font-size: 50px; }
	}
}

/* ⑦ 사진 격자(mosaic) — 왼쪽 단이 격자다(대표 사진 + 작은 사진 두 칸, 2mm 검은 홈). 번호는
   표지의 상호 칸처럼 테라코타 색면 위에 검정으로 선다. */
.wr-page.wr-t-mosaic {
	--wr-pin-ink: #141414;
	.wr-lot__grid { gap: 4px; margin-top: 4px; }
	.wr-lot__thumbs { display: none; }
	.wr-lot__head { align-items: center; }
	.wr-lot__no {
		background: var(--wr-accent);
		color: #141414;
		font-size: 24px;
		line-height: 1;
		padding: 9px 11px 7px;
	}
	.wr-lot__price { color: var(--wr-accent); }
	.wr-lot__spotimg img { filter: saturate(0.75) brightness(0.96); }
	.wr-lot__spotimg { box-shadow: none; }
	.wr-lot__note { border-left: 2px solid var(--wr-accent); padding-left: 12px; }
	&.wr-pdf {
		.wr-lot__grid { gap: 2mm; margin-top: 2mm; }
		.wr-lot__cell { aspect-ratio: auto; height: 34mm; }
		.wr-lot__no { font-size: 30px; padding: 3mm 3.4mm 2.4mm; }
		.wr-lot__head { margin-bottom: 2mm; }
	}
}

/* 종이 결은 테마가 끈다. 규칙을 지우지 말고 값만 0 으로 둔다 — 먹지에서 곱하기는
   지면을 더 어둡게만 만들어 결이 보이지 않는다. */
.wr-cover__tex { opacity: var(--wr-tex); }

/* 표지 골격의 **쌓는 순서**.
   그림 블록(도판·사진 띠·격자)은 템플릿에서 머리보다 앞에 있다 — 절대 위치로 까는 골격
   (항공·모자이크·색면)에서 그림이 먼저 와야 활자가 그 위에 오기 때문이다. 흐름에 놓이는
   골격(도판·갤러리·그리드)에서는 그 순서가 그대로면 상호가 사진 아래로 내려간다.
   DOM 을 골격마다 나누는 대신 order 로 되돌린다. */
.wr-cover--plate,
.wr-cover--gallery,
.wr-cover--grid {
	.wr-cover__head { order: 1; }
	.wr-cover__plate,
	.wr-cover__single,
	.wr-cover__band2 { order: 2; }
	.wr-cover__gap { order: 3; }
	.wr-cover__low { order: 4; }
	.wr-cover__lots { order: 5; }
	.wr-cover__band { order: 6; }
}
/* 스위스 그리드만 **제목이 사진보다 먼저**다(시안 05). 사진을 위에 두면 지면을 여는 것이
   도판이 되어, 격자와 활자가 주인공인 이 지면의 성격과 어긋난다. */
.wr-cover--grid {
	.wr-cover__low { order: 2; }
	.wr-cover__band2 { order: 3; }
	.wr-cover__gap { order: 4; min-height: 0; }
}

/* ── 표지 ① 모노그램 (catalog) ─────────────────────────────────────────────
   기존 조판 그대로다. 마크 색만 테마 변수를 읽는다. */
.wr-cover__mark { background: var(--wr-mark); }
/* 활자 덩어리는 지면 왼쪽 아래 한 귀퉁이에 모인다. 시안 01 에서 그 덩어리는 작지 않다 —
   금 괘선이 굵고, 받는 분이 기간보다 한 단 크며, 제목이 지면 폭의 대부분을 쓴다. */
.wr-cover--mark {
	.wr-cover__rule { width: 23mm; height: 2px; }
	.wr-to { font-size: 18px; margin-top: 2.4mm; }
}

/* ── 표지 ② 도판 (noir) ──────────────────────────────────────────────────
   대표 사진을 금선으로 두른다. 먹지에서 사진은 컬러 그대로 간다 — 듀오톤으로 누르면
   지면 전체가 한 덩어리가 되어 무엇이 찍혔는지 안 읽힌다. */
.wr-cover__plate {
	position: relative;
	z-index: 2;
	flex: 0 0 auto;
	align-self: stretch;
	width: 100%;
	margin: 22px 0 0;
	padding: 7px;
	border: 1px solid var(--wr-gold);
	background: var(--wr-paper-deep);
}
.wr-cover__plate img {
	display: block;
	width: 100%;
	aspect-ratio: 16 / 10;
	object-fit: cover;
}
.wr-cover__plateoff {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	aspect-ratio: 16 / 10;
	color: var(--wr-ink-soft);
	letter-spacing: 0.3em;
	font-size: 13px;
}
.wr-cover--plate .wr-cover__gap { min-height: 26px; }

/* ── 표지 ③ 항공사진 전면 (aerial) ───────────────────────────────────────
   판이 지면 전체를 덮고 활자가 그 위에 선다. 번호는 **실제 위치**에 얹는다 —
   서버가 준 논리 좌표를 백분율로 바꾼 값이라 이미지가 어떤 크기로 늘어나도 맞는다. */
.wr-cover__aerial {
	position: absolute;
	inset: 0;
	z-index: 0;
	overflow: hidden;
}
.wr-cover__aerial img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	/* 색 보정은 아래 `.wr-cover--aerial img` 가 정한다(제 색 그대로). 여기서는 손대지 않는다. */
}
.wr-cover__scrim {
	position: absolute;
	inset: 0;
	background: linear-gradient(180deg, rgba(8, 14, 20, 0.52) 0%, rgba(8, 14, 20, 0.12) 34%, rgba(8, 14, 20, 0.78) 100%);
}
.wr-cover__pin {
	position: absolute;
	transform: translate(-50%, -50%);
	display: flex;
	align-items: center;
	justify-content: center;
	width: 26px;
	height: 26px;
	border-radius: 50%;
	border: 1px solid rgba(255, 255, 255, 0.92);
	background: rgba(15, 107, 140, 0.88);
	color: #fff;
	font-family: Pretendard, sans-serif;
	font-size: 11px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
}
/* 판 위에 서는 활자는 지면 잉크가 아니라 흰 잉크다. 표지에서만 뒤집는다 —
   속장은 밝은 지면 그대로 간다. */
.wr-cover--aerial {
	color: #f2f6f8;
	.wr-caption { color: rgba(242, 246, 248, 0.82); }
	.wr-period { color: rgba(242, 246, 248, 0.82); }
	.wr-to { color: #fff; }
	.wr-title { color: #fff; }
	.wr-cover__sum { color: rgba(242, 246, 248, 0.82); }
	/* 제목 아래 사실 한 줄도 밝게 해야 한다. 이 지면은 판이 어두워서, 기본 잉크색으로
	   두면 어두운 판 위에 어두운 글자가 되어 사실상 안 보인다(실측 2026-09-16: 찍힌
	   픽셀로 잰 대비 2.7. 다른 글자는 6.5~17.1 이다). 배경이 CSS 색이 아니라 사진이라
	   DOM 을 거슬러 올라가 배경색을 보는 방식으로는 이 사고가 잡히지 않는다. */
	.wr-cover__meta1 { color: rgba(242, 246, 248, 0.86); }
	.wr-cover__rule { background: #fff; }
	.wr-cover__band { border-top-color: rgba(255, 255, 255, 0.3); color: #f2f6f8; }
	.wr-cover__corp span { color: rgba(242, 246, 248, 0.78); }
	.wr-cover__note { color: rgba(242, 246, 248, 0.7); }
	.wr-legal { color: #ff8a80; }
	.wr-cover__lotno { color: #fff; }
	.wr-cover__lotname { color: rgba(242, 246, 248, 0.92); }
	.wr-cover__lotprice { color: #fff; }
	.wr-cover__tex { display: none; }
}

/* ── 표지 ④ 사진 격자 (mosaic) ───────────────────────────────────────────
   재단선까지 꽉 찬 3x3. 활자는 격자의 칸을 하나씩 차지한 색면 위에 앉는다 —
   사진 위에 그냥 얹으면 어느 칸에서는 읽히고 어느 칸에서는 안 읽힌다. */
.wr-cover--mosaic { padding: 0; }
.wr-cover__mosaic {
	position: absolute;
	inset: 0;
	z-index: 0;
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	grid-template-rows: repeat(3, 1fr);
	gap: 2px;
	background: var(--wr-paper);
}
.wr-cover__cell {
	overflow: hidden;
	background: var(--wr-paper-deep);
	img { width: 100%; height: 100%; object-fit: cover; }
	/* 사진이 모자랄 때의 색면. 전부 같은 색이면 빈 칸으로 읽히고, 번갈아 주면 격자가 된다. */
	&.is-accent { background: var(--wr-accent); }
}
.wr-cover--mosaic {
	.wr-cover__head {
		position: absolute;
		z-index: 2;
		left: 0;
		top: 0;
		width: 33.333%;
		height: 33.333%;
		box-sizing: border-box;
		padding: 0 5%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		background: var(--wr-accent);
		.wr-mark--dark { background-position: left center; }
		.wr-caption { color: rgba(255, 255, 255, 0.86); }
	}
	.wr-cover__gap { display: none; }
	.wr-cover__low {
		position: absolute;
		z-index: 2;
		left: 0;
		bottom: 0;
		width: 66.666%;
		height: 33.333%;
		box-sizing: border-box;
		padding: 0 5%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		background: rgba(14, 14, 14, 0.94);
	}
	.wr-cover__band {
		position: absolute;
		z-index: 2;
		right: 0;
		bottom: 0;
		width: 33.333%;
		height: 33.333%;
		box-sizing: border-box;
		margin: 0;
		padding: 5%;
		border-top: 0;
		/* 제목 칸과 같은 먹지로 두면 지면 아래 한 줄이 통째로 한 덩어리가 되어
		   격자가 두 줄짜리로 읽힌다. 한 단 밝은 색면으로 칸을 되살린다. */
		background: var(--wr-paper-deep);
		border-left: 1px solid var(--wr-line);
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
	}
	.wr-cover__tex { display: none; }
}

/* ── 표지 ⑤ 색면 분할 (field) ────────────────────────────────────────────
   위 56% 가 색면, 아래가 지면이다. 제목 덩어리는 색면 안에 **절대 위치로** 앉힌다 —
   흐름에 두면 목록이 길어질 때 제목이 색면 밖으로 밀려 경계에 걸친다. */
.wr-cover__field {
	position: absolute;
	left: 0;
	right: 0;
	top: 0;
	height: 56%;
	z-index: 0;
	background: var(--wr-accent);
}
.wr-cover--split {
	.wr-cover__head .wr-mark--dark {
		background-image: url(../assets/images/weonus_wordmark_wh.png);
		opacity: 1;
	}
	.wr-cover__head .wr-caption { color: rgba(255, 255, 255, 0.8); }
	/* 제목 덩어리는 색면의 **아래쪽 끝**에 붙는다(시안 06). 색면 가운데에 띄우면 위아래
	   여백이 같아져 색면이 그냥 큰 색칠이 되고, 제목이 목록과 멀어져 두 덩어리가 서로
	   상관없어 보인다. */
	.wr-cover__low {
		position: absolute;
		left: 24px;
		right: auto;
		width: 64%;
		bottom: 46%;
	}
	.wr-cover__rule { background: rgba(255, 255, 255, 0.72); }
	.wr-period { color: rgba(255, 255, 255, 0.8); }
	.wr-to { color: #fff; }
	.wr-title { color: #fff; }
	.wr-cover__meta1 { color: rgba(255, 255, 255, 0.86); }
	.wr-cover__sum { color: rgba(255, 255, 255, 0.82); }
	.wr-cover__gap { min-height: 0; }
	/* 목록은 색면이 끝나는 자리에서 시작해 **꼬리말 바로 위에서 끝난다**. 그 사이를 다섯
	   행이 **똑같이 나눠 가진다**(`flex: 1 1 auto`). 행 높이를 글이 정하게 두면 다섯 행이
	   상자보다 20mm 길어져 마지막 행이 고지 위에 겹쳐 찍히고(실측 2026-09-16), 반대로
	   맞아떨어져도 남는 자리가 목록 아래에 몰려 지면이 반쯤 빈다. */
	.wr-cover__lots {
		position: absolute;
		left: 24px;
		right: 24px;
		top: 57%;
		bottom: 40px;
		margin-top: 0;
		display: flex;
		flex-direction: column;
		overflow: hidden;      /* 무슨 일이 있어도 꼬리말을 덮지 않는다 */
		li {
			flex: 1 1 auto;
			min-height: 0;     /* 이게 없으면 flex 항목이 글 높이 밑으로 줄지 않는다 */
			align-content: center;
		}
	}
	.wr-cover__band { margin-top: auto; }
	.wr-cover__tex { display: none; }
}

/* ── 표지 ⑥ 갤러리 도판 (gallery) ────────────────────────────────────────
   여백이 주인공이다. 사진은 채우지 않고 **담는다**(contain) — 잘라 맞추는 순간
   이 지면의 성격이 사라진다. */
.wr-cover--gallery {
	padding: 40px 44px 34px;
	.wr-cover__gap { display: none; }
	.wr-title { font-size: calc(26px * var(--wr-tscale, 1)); }
	/* 활자 덩어리를 지면 아래쪽에 모아 두고 그 위를 전부 도판에 준다. 여백이 주인공인
	   지면이라고 사진까지 작으면 남는 것은 빈 종이뿐이다. */
	.wr-cover__low { margin-top: 0; }
}
/* 사진을 못 받은 도판 자리는 접는다. 빈 상자가 지면을 먹으면 '여백이 주인공' 이 아니라
   '무언가 빠진 지면' 으로 읽힌다. */
.wr-cover__single.is-off,
.wr-cover__band2.is-off { display: none; }
.wr-cover__single {
	position: relative;
	z-index: 2;
	flex: 1 1 auto;
	align-self: stretch;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 0;
	padding: 7% 6%;
	/* 폭·높이를 모두 주고 contain 으로 담는다. max-* 만 걸면 flex 항목이 줄어들기만 해서
	   지면의 1/6 짜리 우표가 된다(실측) — 여백이 주인공인 지면이지 사진이 작은 지면이 아니다. */
	img {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}
}

/* ── 표지 ⑦ 스위스 그리드 (grid) ─────────────────────────────────────────
   머리의 수치 행과 재단선까지 나가는 사진 띠가 격자를 눈에 보이게 만든다. */
.wr-cover__meta {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 0 16px;
	margin: 16px 0 0;
	padding-top: 10px;
	border-top: 1px solid var(--wr-ink);
	dt {
		@include dt-type-doc-label;
		font-size: 10px;
		letter-spacing: 0.06em;
		color: var(--wr-ink-soft);
	}
	dd {
		@include dt-type-doc-spec;
		margin: 2px 0 0;
		font-weight: 600;
		color: var(--wr-ink);
	}
}
.wr-cover__band2 {
	position: relative;
	z-index: 2;
	flex: 0 0 auto;
	align-self: stretch;          /* 표지가 align-items: flex-start 라 이게 없으면 내용 폭으로 줄어든다 */
	margin: 20px -24px 0;
	overflow: hidden;
	img {
		display: block;
		width: 100%;
		aspect-ratio: 16 / 8.6;
		object-fit: cover;
	}
}
.wr-cover--grid {
	.wr-cover__gap { min-height: 18px; }
	/* 머리의 라벨 행(위아래 검정 괘선)이 이미 구획을 만든다. 여기에 강조 괘선을 또 두면
	   제목 덩어리의 flex 한 줄에 40mm 짜리 항목으로 끼어들어 제목을 오른쪽으로 밀어낸다
	   (실측 2026-09-16: 제목이 66mm 지점에서 시작하고 키-값 표가 아래로 접혔다). */
	.wr-cover__rule { display: none; }
	.wr-cover__tex { display: none; }
}

/* ── 표지 물건 목록 (plate · aerial · split · grid 공용) ──────────────────
   행마다 괘선을 긋지 않는다. 다섯 줄에 밑줄을 다섯 번 그으면 인쇄 조판이 아니라
   웹 표가 된다(DESIGN.md). 구획은 번호와 여백이 만든다. */
.wr-cover__lots {
	position: relative;
	z-index: 2;
	flex: 0 0 auto;
	/* 표지가 `align-items: flex-start` 라 이게 없으면 목록이 내용 폭으로 줄어든다.
	   그러면 금액이 오른쪽 재단선에서 30mm 쯤 떨어진 자리에 서고, 번호·이름·금액이
	   지면을 가로지르는 한 줄이 아니라 가운데 떠 있는 덩어리가 된다(실측). */
	align-self: stretch;
	list-style: none;
	margin: 20px 0 0;
	padding: 0;
	li {
		display: grid;
		grid-template-columns: auto 1fr auto;
		align-items: baseline;
		column-gap: 10px;
		padding: 5px 0;
	}
}
.wr-cover__lotno {
	@include dt-type-doc-label;
	font-size: 12px;
	color: var(--wr-accent);
	font-variant-numeric: tabular-nums;
}
.wr-cover__lotname {
	@include dt-type-doc-spec;
	font-size: 14px;
	color: var(--wr-ink);
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.wr-cover__lotprice {
	font-family: SCoreDream, Pretendard, sans-serif;
	font-weight: 700;
	font-size: 13px;
	color: var(--wr-ink);
	white-space: nowrap;
}
/* 지번까지 적는 줄. 색면 분할만 쓴다(그 골격만 한 행에 두 줄을 받을 자리가 있다). */
.wr-cover__lotaddr { display: none; }
/* 동 이름만. 항공사진 전면의 캡션 행만 쓴다(다섯 칸이 한 줄에 서야 한다). */
.wr-cover__lotdong { display: none; }
/* 프리미엄 다크에서는 다섯 건이 가로로 선다(도록의 판 아래 캡션 행). */
.wr-cover--plate .wr-cover__lots {
	display: grid;
	grid-auto-flow: column;
	grid-auto-columns: minmax(0, 1fr);
	column-gap: 14px;
	border-top: 1px solid var(--wr-line);
	padding-top: 12px;
	li {
		display: block;
		padding: 0;
	}
	.wr-cover__lotname { display: block; margin-top: 4px; font-size: 12px; }
	.wr-cover__lotprice { display: block; margin-top: 3px; font-size: 12px; color: var(--wr-ink-soft); }
}
/* 스위스 그리드에서는 금액이 오른쪽 끝에 굵게 선다(수치가 세로로 정렬되는 표). */
.wr-cover--grid .wr-cover__lots {
	border-top: 1px solid var(--wr-ink);
	padding-top: 8px;
	.wr-cover__lotno { color: var(--wr-accent); }
	.wr-cover__lotprice { font-size: 15px; }
}

/* ── 표지 골격의 인쇄(A4) 치수 ───────────────────────────────────────────────
   웹은 폭이 변하지만 인쇄는 210x297mm 로 고정이다. 그래서 웹에서 px 로 잡은 자리를
   여기서 mm 로 다시 잡는다. 낱장의 `overflow: hidden` 이 재단선을 만든다 — 그 규칙에
   기대어 그림을 지면 밖으로 흘려보낸다. */
.wr-pdf {
	/* ② 도판(noir) */
	.wr-cover__plate {
		margin-top: 12mm;
		padding: 2.6mm;
	}
	.wr-cover--plate .wr-cover__gap { min-height: 0; }
	.wr-cover--plate .wr-cover__lots {
		margin-top: 9mm;
		padding-top: 4.4mm;
		column-gap: 5mm;
		.wr-cover__lotno { font-size: 11px; }
		.wr-cover__lotname { font-size: 11px; margin-top: 1.4mm; }
		.wr-cover__lotprice { font-size: 11px; margin-top: 1mm; }
	}

	/* ③ 항공사진 전면(aerial) — 번호가 조금 커야 재단 뒤에도 읽힌다.
	   **골격까지 짚어야 한다.** `.wr-cover__pin` 만 쓰면 뒤쪽 '시안에 맞춘 조정' 의
	   `.wr-cover--aerial .wr-cover__pin`(같은 특정도, 더 뒤)이 이겨 이 값이 통째로 죽는다. */
	.wr-cover--aerial .wr-cover__pin {
		width: 9mm;
		height: 9mm;
		font-size: 12px;
	}
	.wr-cover--aerial .wr-cover__lots { margin-top: 8mm; }

	/* ④ 사진 격자(mosaic) — 지면 전체가 격자다. 낱장 안쪽 여백을 쓰지 않는다. */
	.wr-cover--mosaic { padding: 0; }
	.wr-cover--mosaic .wr-cover__head .wr-mark--dark { width: 30mm; height: 4.6mm; }
	.wr-cover--mosaic .wr-cover__band { padding: 6mm; }
	.wr-cover--mosaic .wr-title { font-size: calc(34px * var(--wr-tscale, 1)); }
	.wr-cover--mosaic .wr-cover__note { font-size: 8px; max-width: none; }
	.wr-cover--mosaic .wr-legal { font-size: 7.5px; line-height: 1.45; max-width: none; margin-top: 1.4mm; }
	.wr-cover--mosaic .wr-cover__corp { font-size: 9px; }

	/* ⑤ 색면 분할(field) */
	.wr-cover--split .wr-cover__low {
		left: 18mm;
		right: 18mm;
		bottom: 46%;
	}
	.wr-cover--split .wr-cover__lots {
		left: 18mm;
		right: 18mm;
		/* 공용 인쇄 규칙의 `margin-top: 10mm` 을 여기서 되돌린다. 위아래를 모두 묶은 절대
		   위치 상자라 margin 이 상자를 통째로 밀어내, 색면과 목록 사이가 3mm 대신 13mm 가
		   되고 행에 나눠 줄 자리가 10mm 줄어든다(실측). */
		margin-top: 0;
		top: 57%;
		/* 꼬리말(상호 한 줄 + 고지 두 줄 + 괘선)이 지면 아래 18mm 를 쓰고 낱장 안쪽 여백이
		   13mm 다. 목록의 아래끝을 그보다 위에 묶어 두지 않으면 마지막 행이 고지 위에
		   겹쳐 찍힌다(실측). 민·형사 경고 두 줄(2026-10-26)이 9mm 를 더 쓴다 — 35mm 로 두면
		   마지막 행의 주소 위로 괘선이 지나갔다(실측). 여덟 건 한 줄 행(약 67mm)은 남은 83mm 에 든다. */
		bottom: 45mm;
	}
	.wr-cover--split .wr-cover__low {
		right: auto;
		width: 132mm;
	}

	/* ⑥ 갤러리 도판(gallery) — 여백을 인쇄에서 더 벌리되 도판은 지면의 절반을 넘게 쓴다. */
	.wr-cover--gallery { padding: 24mm 26mm 18mm; }
	.wr-cover--gallery .wr-title { font-size: calc(29px * var(--wr-tscale, 1)); }
	.wr-cover--grid .wr-title { font-size: calc(70px * var(--wr-tscale, 1)); }
	/* 70px 은 한 줄에 여섯 자밖에 못 담는다. 긴 제목은 `--wr-tscale`(맨 아래 '제목 길이 단계')이
	   낮춘다. 그래도 모자라면 도판이 더 양보한다 — 100자 제목에 여덟 건이 실제로 가능한 입력이다.
	   길이 단계는 표지 요소(section)에도 붙는다(도판은 제목의 형제가 아니라 사촌이다). */
	.wr-cover--grid.is-xlong .wr-cover__band2 { min-height: 18mm; }
	/* 120자 제목 + 여덟 건은 머리 괘선 아래 22px 여백까지 더하면 18mm 로도 5mm 넘친다(실측). */
	.wr-cover--grid.is-xxlong .wr-cover__band2 { min-height: 12mm; }
	.wr-cover__single { padding: 6mm 0 12mm; }

	/* ⑦ 스위스 그리드(grid) — 사진 띠가 좌우 재단선까지 나간다. */
	.wr-cover__band2 { margin: 11mm -18mm 0; }
	.wr-cover__meta {
		margin-top: 8mm;
		padding-top: 3mm;
		dt { font-size: 9px; }
		dd { font-size: 12px; }
	}
	.wr-cover--grid .wr-cover__gap { min-height: 0; }
	.wr-cover--grid .wr-cover__lots { padding-top: 3.4mm; }

	/* 공용 목록 */
	.wr-cover__lots {
		margin-top: 10mm;
		li { padding: 1.8mm 0; }
		.wr-cover__lotno { font-size: 11px; }
		.wr-cover__lotname { font-size: 13px; }
		.wr-cover__lotprice { font-size: 13px; }
	}
}


/* ══════════════════════════════════════════════════════════════════════════
   표지 골격을 시안(codex image_generation, 2026-09-16)에 맞춘 조정.
   아래 규칙은 앞의 골격 정의를 덮어쓴다 — 무엇을 왜 고쳤는지 한자리에서 읽히도록
   원래 정의를 건드리지 않고 이 구역에 모았다.
   ══════════════════════════════════════════════════════════════════════════ */

/* ── ② 프리미엄 다크(noir) — 시안 02 ────────────────────────────────────
   워드마크 아래 금 괘선이 지면 폭을 가로지르고, 도판이 거의 정사각으로 지면의 절반을
   쓴다. 하단 목록은 금색 번호와 이름만 — 금액은 속장과 색인에 있고, 표지에서까지
   반복하면 다섯 열이 숫자로 빽빽해져 도판의 인상이 흐려진다. */
.wr-cover--plate {
	.wr-cover__head {
		align-self: stretch;
		padding-bottom: 14px;
		border-bottom: 1px solid var(--wr-gold);
		.wr-caption { display: none; }        /* 시안은 상호 한 줄만 둔다 */
	}
	/* **판이 남는 지면을 먹고, 모자라면 양보한다.** 비율로 높이를 못 박으면 제목이 세 줄이
	   되는 순간(37자) 낱장이 넘쳐 잘리는 것이 지면 맨 아래 고지 문장이다(실측 15.1mm).
	   사진을 조금 낮추는 쪽이 고지를 잘라내는 쪽보다 낫다. 사진이 아예 없을 때 대체 상자의
	   높이가 사진과 달라 판이 출렁이던 것도 같이 사라진다. */
	.wr-cover__plate {
		flex: 1 1 auto;
		min-height: 40mm;
		display: flex;
	}
	.wr-cover__plate img,
	.wr-cover__plateoff {
		aspect-ratio: auto;
		height: 100%;
		min-height: 0;
	}
	.wr-cover__rule { display: none; }        /* 금 괘선이 이미 그 역할을 한다 */
	.wr-period, .wr-to { display: none; }     /* 사실은 제목 아래 한 줄로 내려갔다 */
	/* 판 아래 캡션 행은 **번호와 이름 한 줄**이다(시안 02). 지역과 금액까지 넣으면 다섯
	   칸이 세 줄짜리 블록이 되어, 먹지에서 유일한 빛인 도판의 인상을 아래에서 눌러 버린다.
	   같은 값은 바로 다음 장 수록 물건 목록이 표로 담는다. 앞 정의가 이 두 span 을
	   `display: block` 으로 켜므로 **목록까지 짚어서** 다시 꺼야 한다(선택자 특정도). */
	.wr-cover__lots .wr-cover__lotarea,
	.wr-cover__lots .wr-cover__lotprice { display: none; }
	.wr-cover__lots .wr-cover__lotname { font-size: 12.5px; }
	.wr-cover__meta1 { font-size: 13.5px; }
}
/* **제목 크기는 인쇄 치수다.** `.wr-pdf` 없이 적으면 웹에서도 먹는데, 넓은 화면의 웹 제목
   크기는 미디어 쿼리(`@media (min-width: 860px) .wr-page:not(.wr-pdf) .wr-title`)가 더 높은
   특정도로 이미 정해 두었다. 그래서 골격별 값이 **좁은 화면에서만** 이겨, 휴대폰에서 제목이
   데스크톱보다 커지는 뒤집힌 결과가 나온다(실측 2026-09-16: 색면 분할이 390px 에서 58px,
   1440px 에서 54px). 인쇄에만 건다. */
.wr-pdf .wr-cover--plate .wr-title { font-size: calc(50px * var(--wr-tscale, 1)); }

/* ── ③ 항공사진 전면(aerial) — 시안 03 ──────────────────────────────────
   활자는 **지면 맨 아래**에 모이고 위쪽은 판만 남는다. 목록은 세로로 세우지 않고
   가로 한 줄 띠로 눕힌다 — 세로로 세우면 다섯 행이 판의 오른쪽을 덮어, 번호를 실제
   위치에 얹은 이유가 사라진다(실측). */
.wr-cover--aerial {
	/* 머리를 **절대 위치로 띄우지 말 것.** 띄우면 낱장 안쪽 여백을 모르기 때문에 상호만
	   24px 자리에 서고 제목·목록·꼬리말은 여백 안(인쇄 18mm, 넓은 화면은 가운데 단)에 서서
	   왼쪽 기준선이 갈라진다(실측: 인쇄 11.6mm, 데스크톱 200px 어긋남). 자동 여백으로
	   위에 붙이면 같은 여백을 그대로 쓴다. */
	.wr-cover__gap { display: none; }
	.wr-cover__head { margin-bottom: auto; }
	.wr-cover__rule { display: none; }
	.wr-period, .wr-to { display: none; }
	/* 목록은 **한 줄에 다섯 칸**이다(시안 03). 칸마다 '번호 · 지역 · 금액' 으로, 물건
	   이름은 쓰지 않는다 — 이름은 길이가 제각각이라 다섯 칸이 두 줄로 접히고, 그러면 띠가
	   아니라 표가 되어 판 위에 검은 덩어리가 하나 더 생긴다(실측). 이름은 바로 다음 장
	   수록 물건 목록에 있다. */
	.wr-cover__lots {
		align-self: stretch;
		display: grid;
		grid-auto-flow: column;
		grid-auto-columns: minmax(0, 1fr);
		column-gap: 14px;
		margin-top: 14px;
		padding: 9px 0;
		border-top: 1px solid rgba(255, 255, 255, 0.5);
		border-bottom: 1px solid rgba(255, 255, 255, 0.5);
		li {
			display: flex;
			align-items: baseline;
			gap: 6px;
			padding: 0;
			min-width: 0;
		}
		.wr-cover__lotname,
		.wr-cover__lotarea { display: none; }
		.wr-cover__lotno { flex: 0 0 auto; font-size: 10.5px; white-space: nowrap; }
		.wr-cover__lotdong {
			display: block;
			@include dt-type-doc-note;
			flex: 0 1 auto;
			font-size: 11px;
			color: rgba(242, 246, 248, 0.88);
			min-width: 0;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		.wr-cover__lotprice { flex: 0 0 auto; font-size: 12px; }
	}
	/* 판이 어두우면 도시의 결이 사라진다. 아래쪽만 눌러 활자를 받치고 위는 밝게 둔다. */
	/* 활자가 판 위에 서려면 그 아래가 확실히 어두워야 한다. 아래 절반을 눌러 제목·목록·
	   상호를 받치고, 위쪽은 워드마크가 걸리는 만큼만 약하게 눌러 도시의 결을 남긴다.
	   가운데는 건드리지 않는다 — 거기가 번호가 앉는 자리다. */
	.wr-cover__scrim {
		background: linear-gradient(180deg, rgba(6, 11, 16, 0.48) 0%, rgba(6, 11, 16, 0.04) 22%,
			rgba(6, 11, 16, 0.04) 40%, rgba(6, 11, 16, 0.72) 72%, rgba(6, 11, 16, 0.95) 100%);
	}
	/* **표지 전면 판은 제 색 그대로다.** 두 색으로 누르거나(듀오톤) 채도를 0.26 까지 덜어낸
	   판은 도시가 회색 안개가 되어 "흑백도 좀 오바" (2026-09-17 사용자 지적). 항공사진의
	   초록·갈색을 살리고 대비만 조금 올린다 — 흰 활자는 스크림이 받친다(위·아래만 누른다). */
	img { filter: saturate(1.15) contrast(1.12) brightness(0.94); }
	/* 지도 대신 쓰는 대표 사진은 더 눌러야 한다. 항공사진은 위에서 본 도시라 결이 고르지만
	   건물 사진은 하늘·간판처럼 밝은 면이 커서, 같은 값으로 두면 흰 제목이 하늘에 묻힌다. */
	.wr-cover__aerial.is-photo img { filter: saturate(0.55) contrast(1.1) brightness(0.58); }
}
.wr-cover--aerial .wr-cover__pin {
	width: 22px;
	height: 22px;
	/* 알약을 더 불투명하게 만들지 말 것. 숫자가 읽히는지는 **찍힌 픽셀끼리** 재야 한다
	   (알약 위에 그늘막이 한 겹 더 얹히므로 CSS 로 적은 흰색은 실제로 찍히는 색이 아니다).
	   그렇게 재면 지금 값이 대비 5.3~7.3 이고, 알파를 0.86 으로 올리면 바탕이 밝아져
	   5.3~6.3 으로 오히려 떨어진다(실측 2026-09-16). 판이 어두워서 비칠수록 유리하다. */
	background: rgba(15, 107, 140, 0.62);
	border-color: rgba(255, 255, 255, 0.85);
	font-size: 10px;
}
/* 색면 분할(split)과 사진 격자(mosaic)는 표지 내용이 **전부 절대 위치**다(색면·제목·
   목록·격자). 흐름에 남는 것이 머리 한 줄뿐이라 웹에서는 표지 높이가 머리 높이로 주저앉고,
   제목과 목록이 낱장 밖으로 밀려 `overflow: hidden` 에 잘린다(실측 2026-09-16: 모바일에서
   97px·56px 짜리 표지가 나왔고 제목이 통째로 사라졌다). 인쇄에서는 낱장이 297mm 로 고정이라
   드러나지 않는 사고다. 웹에서는 지면 비율을 직접 준다. */
.wr-page:not(.wr-pdf) .wr-cover--split,
.wr-page:not(.wr-pdf) .wr-cover--mosaic {
	min-height: min(92vh, calc(100vw * 1.15));
}
/* 색면 분할의 목록은 표지 아래 43% 안에 절대 위치로 앉으므로, 화면이 낮으면(1440x650) 여섯
   행이 36px 로 눌려 주소와 다음 행 이름이 겹친다(Codex 리뷰 재현). 행 수만큼 표지의 최소
   높이를 올린다 — `--wr-lots-h` 는 화면이 행 수로 계산해 넘긴다. */
.wr-page:not(.wr-pdf) .wr-cover--split {
	min-height: max(min(92vh, calc(100vw * 1.15)), calc(var(--wr-lots-h, 0px) / 0.43 + 60px));
}

/* **웹 표지는 A4 낱장이 아니라 흐르는 띠다.** 판이 세로(0.7071)라 넓고 낮은 띠에 깔면
   `object-fit: cover` 가 판의 높이 대부분을 잘라내고, 번호가 통째로 낱장 밖으로 나간다
   (실측 2026-09-16: 1440x303 표지에서 다섯 개 중 화면에 남은 것이 하나도 없었다.
   `overflow: hidden` 이 조용히 잘라 내기 때문에 화면만 봐서는 사고가 안 보인다).
   그래서 웹에서는 표지를 판 비율만큼 세우고 판을 지면 한가운데 세로로 세운다. 판과 담는
   상자의 비율이 같아지므로 잘림이 0 이고, 번호가 건물 위에 그대로 앉는다. 판 바깥은
   먹지로 둔다 — 항공 테마의 밝은 속장 지면이 그대로 보이면 판이 사진 한 장처럼 떠 버린다. */
.wr-page:not(.wr-pdf) .wr-cover--aerial {
	min-height: min(92vh, calc(100vw * 1.32));
	background: #060b10;

	.wr-cover__aerial {
		left: 50%;
		right: auto;
		width: auto;
		aspect-ratio: 700 / 990;
		transform: translateX(-50%);
	}
	/* 대체 사진은 번호를 얹지 않으므로 판 비율을 지킬 이유가 없다. 지면을 다 덮는다. */
	.wr-cover__aerial.is-photo {
		left: 0;
		right: 0;
		width: auto;
		aspect-ratio: auto;
		transform: none;
	}
}

/* 좁은 화면 골격(위쪽 859px 미디어 규칙)의 판 폭. 판은 지도 비율 그대로 세우고, 화면 높이의 8할을
   넘으면 위아래를 자르지 않고 **폭을 줄여 가운데 둔다**(양옆은 표지의 먹지, 넓은 화면 골격과 같다).
   잘라 맞추면 두 가지를 잃었다. 판 왼쪽 아래 끝의 네이버 지도 로고(출처 표기)가 잘렸고, 인쇄(A4 폭도
   이 골격이다)에서는 번호가 화면에서 잰 자리에 남아 건물에서 29px 어긋났다(실측 2026-10-29).
   판 비율이 지도 비율과 같으면 잘림이 0 이라 번호 좌표가 판 크기와 무관하다.
   바로 위 블록(미디어 조건 없이 모든 폭에 걸린다)과 특정도가 같아서 이 블록이 그 뒤에 와야 이긴다. */
@media (max-width: 859px) {
	.wr-page:not(.wr-pdf) .wr-cover--aerial .wr-cover__aerial {
		left: 0;
		transform: none;
		align-self: center;
		width: min(calc(100% + 2 * #{$wr-pad}), calc(80vh * 700 / 990));
	}
	.wr-page:not(.wr-pdf) .wr-cover--aerial .wr-cover__aerial.is-photo {
		align-self: stretch;
		width: auto;
	}
}

/* **웹에서 표지 사진은 화면 높이의 일부만 쓴다.** 인쇄에서는 낱장이 297mm 로 고정이라
   "사진이 남는 지면을 먹는다"(`flex: 1 1 auto` + `height: 100%`)가 맞지만, 웹은 표지 높이가
   내용으로 정해지므로 같은 규칙이 **사진의 원래 비율**을 그대로 내보낸다. 휴대폰으로 찍은
   세로 사진(450x628)이 폭 980px 로 늘어나 표지 한 장이 2,000px 이 됐고 제목은 화면 두 장을
   내려야 보였다(운영 실측 2026-09-17, 사용자 지적 "표지가 제대로 하나도 안나오는데").
   dev 리포트는 가로 사진뿐이라 계측이 못 잡았다 — 세로 사진으로도 재야 한다. */
.wr-page:not(.wr-pdf) {
	.wr-cover--plate .wr-cover__plate {
		flex: 0 0 auto;
		min-height: 0;
		display: block;
	}
	.wr-cover--plate .wr-cover__plate img,
	.wr-cover--plate .wr-cover__plateoff {
		aspect-ratio: 16 / 10;
		height: auto;
		max-height: 60vh;
	}
	.wr-cover--grid .wr-cover__band2 {
		flex: 0 0 auto;
		min-height: 0;
	}
	.wr-cover--grid .wr-cover__band2 img {
		aspect-ratio: 16 / 8.6;
		height: auto;
		max-height: 56vh;
	}
	.wr-cover--gallery .wr-cover__single {
		flex: 0 0 auto;
	}
	.wr-cover--gallery .wr-cover__single img {
		height: auto;
		max-height: 64vh;
	}
}

/* ── ⑤ 색면 분할(field) — 시안 06 ───────────────────────────────────────
   색면 안의 제목이 지면에서 가장 큰 활자이고, 아래 목록은 이름 아래에 주소가 붙는
   두 줄짜리 행이다. 금액은 오른쪽 끝에 색면과 같은 초록으로 굵게 선다. */
.wr-cover--split {
	/* 색면 안은 제목 한 덩어리만 남긴다(시안 06). 기간·받는 분을 제목 위에 또 얹으면
	   제목 아래 한 줄과 같은 사실이 두 번 적히고, 색면에서 가장 큰 활자가 제목이라는
	   구성이 흐려진다. */
	.wr-cover__rule { display: none; }
	.wr-period, .wr-to { display: none; }

	/* 목록 한 행은 '번호 / 이름·주소 / 금액' 이다. **네 칸의 자리를 전부 못 박아야 한다** —
	   번호만 자동 배치로 두면 격자가 이름·금액을 먼저 1행에 채운 뒤 번호를 남은 칸,
	   곧 **오른쪽 끝**으로 보낸다(실측 2026-09-16: 번호가 금액 오른쪽에 섰다). */
	.wr-cover__lots {
		li {
			grid-template-columns: 11mm 1fr auto;
			column-gap: 0;
			align-items: baseline;
			padding: 1.8mm 0;
		}
		/* 일곱 건이 넘으면 주소 줄을 뺀다. 두 줄짜리 행 여덟 개는 색면 아래 자리보다
		   30mm 길어서, 줄이지 않으면 아래에서부터 잘려 나간다(목록은 `overflow: hidden`
		   이다 — 잘리느니 한 줄로 서는 편이 낫다). 개수로 고르는 선택자라 담당자가 물건을
		   더 담는 순간 저절로 바뀐다. */
		li:nth-child(1):nth-last-child(n+7),
		li:nth-child(1):nth-last-child(n+7) ~ li {
			padding: 1.2mm 0;
			.wr-cover__lotaddr { display: none; }
			.wr-cover__lotname { font-size: 14px; }
			.wr-cover__lotprice { font-size: 17px; }
		}
		.wr-cover__lotno {
			grid-column: 1;
			grid-row: 1;
			font-size: 13px;
			color: var(--wr-accent);
		}
		.wr-cover__lotname { grid-column: 2; grid-row: 1; font-size: 16px; font-weight: 600; }
		.wr-cover__lotarea { display: none; }
		.wr-cover__lotaddr {
			display: block;
			grid-column: 2;
			grid-row: 2;
			@include dt-type-doc-note;
			font-size: 11.5px;
			color: var(--wr-ink-soft);
		}
		.wr-cover__lotprice {
			grid-column: 3;
			grid-row: 1;
			font-size: 20px;
			color: var(--wr-accent);
		}
	}
	.wr-title { line-height: 1.14; }
}
.wr-pdf .wr-cover--split .wr-title { font-size: calc(58px * var(--wr-tscale, 1)); }


/* ── ⑥ 갤러리 도판(gallery) — 시안 07 ───────────────────────────────────
   머리는 라틴 세 단(상호 · 문서 종류 · 주차)이 지면 폭에 걸쳐 좌·중·우로 선다.
   제목은 작고, 그 아래 한 줄이 무엇을 담았는지 말한다. 바닥은 괘선 하나와
   상호·합계 두 끝. */
.wr-cover--gallery {
	/* 머리는 라틴 세 자리다. 왼쪽에 상호, 가운데에 문서 종류, 오른쪽에 주차. 괘선을 긋지
	   않는다 — 여백이 주인공인 지면에서 가로줄 하나가 머리를 따로 떼어 낸 머리띠로 만든다. */
	.wr-cover__head {
		align-self: stretch;
		display: grid;
		grid-template-columns: 1fr auto 1fr;
		align-items: center;
		.wr-mark--dark { justify-self: start; }
		.wr-caption { display: none; }
		.wr-cover__railcap {
			@include dt-type-doc-label;
			justify-self: center;
			font-size: 11px;
			letter-spacing: 0.16em;
			color: var(--wr-ink-soft);
		}
		.wr-cover__railkey {
			@include dt-type-doc-label;
			justify-self: end;
			font-size: 11px;
			letter-spacing: 0.1em;
			color: var(--wr-ink-soft);
		}
		.wr-cover__raildate { display: none; }
	}
	.wr-cover__rule { display: none; }
	.wr-period, .wr-to { display: none; }
	.wr-cover__meta1 { margin-top: 3mm; font-size: 12.5px; }
	.wr-cover__band { border-top: 1px solid var(--wr-line); }
	/* 도판은 지면 폭을 다 쓴다. 여백이 주인공인 지면이라고 사진까지 작으면 남는 것은
	   빈 종이뿐이다(시안 07 의 도판은 지면 폭의 대부분을 쓴다). */
	.wr-cover__single { padding-left: 0; padding-right: 0; }
}

/* ── ⑦ 스위스 그리드(grid) — 시안 05 ────────────────────────────────────
   머리의 라틴 라벨 행이 지면 폭을 가로지르고(위아래 검정 괘선, 세로 구분선),
   제목이 왼쪽에 크게 두 줄, 오른쪽에 키-값 표가 선다. 목록은 **행마다 괘선이 있는
   표**다 — 다른 골격에서는 금지한 형태지만, 격자를 눈에 보이게 만드는 것이 이 지면의
   성격이고 시안이 정확히 그 표를 쓴다. */
.wr-cover--grid {
	/* 머리 라벨 행. 네 칸이 세로 구분선으로 나뉘고 위아래를 검정 괘선이 잡는다. */
	.wr-cover__head {
		align-self: stretch;
		display: grid;
		grid-template-columns: auto auto auto 1fr;
		align-items: center;
		column-gap: 0;
		padding: 7px 0;
		border-top: 1px solid var(--wr-ink);
		border-bottom: 1px solid var(--wr-ink);
		.wr-caption { display: none; }
	}
	.wr-cover__railcap,
	.wr-cover__railkey,
	.wr-cover__raildate {
		@include dt-type-doc-label;
		font-size: 11px;
		letter-spacing: 0.1em;
		padding: 0 14px;
		border-left: 1px solid var(--wr-line);
		white-space: nowrap;
	}
	.wr-cover__railcap { color: var(--wr-ink); }
	.wr-cover__railkey { color: var(--wr-accent); }
	.wr-cover__raildate { color: var(--wr-ink-soft); letter-spacing: 0.04em; }
	.wr-mark--dark { margin-right: 14px; }

	/* 제목은 왼쪽에 크게 두 줄, 키-값 표가 그 오른쪽에 선다.
	   **표를 절대 위치로 띄우지 말 것.** 띄우면 이 덩어리의 높이를 제목 혼자 정하는데,
	   제목이 짧아지는 화면(웹의 한 줄 제목)에서는 표가 덩어리 밖으로 흘러나와 아래
	   도판 위에 얹힌다(실측 2026-09-16: 데스크톱에서 212x53px 겹침). 한 줄짜리 flex 로
	   세우면 덩어리 높이가 둘 중 큰 쪽을 따라가고, 좁은 화면에서는 표가 아래로 접힌다. */
	.wr-cover__low {
		align-self: stretch;
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;
		gap: 6mm 8mm;
		padding-right: 0;
		/* 머리 괘선과 제목 사이. 0 이면 제목 머리가 괘선에 붙어 한 덩어리로 읽힌다(운영 실측). */
		padding-top: 22px;
	}
	.wr-title { flex: 1 1 100mm; line-height: 1.06; margin-top: 0; }
	/* 사실은 오른쪽 키-값 표가 담는다. 제목 위·아래에 같은 값을 또 얹으면 세 번 적힌다. */
	.wr-period, .wr-to, .wr-cover__meta1, .wr-cover__sum { display: none; }
	/* 시안 05 의 도판은 **흑백**이다. 주황 하나만 색으로 남기는 지면이라 컬러 사진이
	   들어오면 그 규칙이 무너진다. 높이는 지면이 허락하는 만큼이다 — 제목·표·사진·목록이
	   모두 한 장에 들어가야 하므로 여기서 한 mm 를 더 쓰면 목록 마지막 행이 잘린다. */
	/* 도판이 **남는 지면을 먹는다**. 높이를 비율로 못 박으면 남는 자리는 빈 gap 이 가져가
	   버려서, 제목과 목록 사이가 통째로 흰 종이가 된다(실측 2026-09-16: 지면의 1/5).
	   시안 05 의 도판은 지면의 절반 가까이를 쓰는 큰 판이다. */
	.wr-cover__gap { display: none; }
	/* 판이 남는 지면을 먹되, 제목이 길어지면 그만큼 양보한다. 52mm 로 두면 제목 다섯 줄
	   (32자)에서 낱장이 넘쳐 고지 문장이 잘렸다(실측 10.3mm). */
	.wr-cover__band2 {
		flex: 1 1 auto;
		min-height: 30mm;
		margin-top: 7mm;
	}
	.wr-cover__band2 img {
		height: 100%;
		aspect-ratio: auto;
		filter: grayscale(1) contrast(1.06);
	}
	.wr-cover__lots { margin-top: 6mm; }
	.wr-cover__band { margin-top: 5mm; padding-top: 3mm; }
	.wr-cover__meta {
		flex: 0 1 56mm;
		margin: 0;
		padding-top: 0;
		border-top: 0;
		display: block;
		div {
			display: grid;
			grid-template-columns: 22mm 1fr;
			align-items: baseline;
			column-gap: 6px;
			padding: 5px 0;
			border-bottom: 1px solid var(--wr-line);
		}
		dd { margin: 0; }
	}
	.wr-period { color: var(--wr-ink-soft); }
	.wr-cover__lots {
		border-top: 2px solid var(--wr-ink);
		padding-top: 0;
		li {
			grid-template-columns: auto 1fr auto auto;
			column-gap: 14px;
			/* 행을 조인 만큼 도판이 가져간다(도판이 `flex: 1 1 auto` 라 남는 지면을 먹는다).
			   시안 05 에서 지면을 지배하는 것은 목록이 아니라 큰 흑백 도판이다. */
			padding: 2mm 0;
			border-bottom: 1px solid var(--wr-line);
		}
		.wr-cover__lotname { font-size: 22px; font-weight: 600; }
		.wr-cover__lotarea {
			@include dt-type-doc-note;
			font-size: 12px;
			color: var(--wr-ink-soft);
			white-space: nowrap;
		}
		.wr-cover__lotprice { font-size: 26px; }
	}
}

/* ── ④ 사진 격자(mosaic) — 시안 08 ──────────────────────────────────────
   상호는 **오른쪽 위** 칸, 제목은 가운데 줄 왼쪽 두 칸, 요약과 고지는 오른쪽 아래 칸.
   상호를 왼쪽 위에 두면 제목 블록과 같은 모서리에 몰려 격자의 대각선이 죽는다. */
.wr-cover--mosaic {
	.wr-cover__head {
		left: auto;
		right: 0;
		top: 0;
		width: 33.333%;
		height: 33.333%;
	}
	/* 제목 칸은 **불투명**해야 한다. 알파를 남기면 뒤 칸의 사진이 비쳐 제목 위로 건물
	   윤곽이 지나가고, 격자가 아니라 사진 위에 얹은 자막으로 읽힌다(실측). */
	.wr-cover__low {
		top: 33.333%;
		bottom: auto;
		height: 33.333%;
		width: 66.666%;
		background: var(--wr-surface);
	}
	/* 제목 칸은 제목과 사실 한 줄뿐이다(시안 08). 괘선·기간·받는 분을 위에 더 얹으면
	   한 칸 안에 작은 글 네 줄이 쌓여, 격자에서 제목 칸만 빽빽해진다. */
	.wr-cover__rule { display: none; }
	.wr-period, .wr-to { display: none; }
	.wr-cover__meta1 { color: rgba(243, 241, 236, 0.76); }
	/* 오른쪽 아래 칸은 집계 두 줄이 먼저 서고 상호·고지가 그 아래에 붙는다(시안 08). */
	.wr-cover__tally {
		display: block;
		font-family: SCoreDream, Pretendard, sans-serif;
		font-weight: 700;
		font-size: 17px;
		margin: 0;
		color: var(--wr-ink);
	}
	.wr-cover__dongs {
		display: block;
		@include dt-type-doc-note;
		margin: 3px 0 10px;
		color: var(--wr-ink-soft);
	}
}
/* 웹의 표지 목록은 바로가기다. 밑줄·색 변화 없이 커서와 초점 표시만 준다(문서의 활자를 지킨다). */
.wr-page:not(.wr-pdf) .wr-cover__lots li.is-link {
	cursor: pointer;
	transition: opacity 140ms ease-out;
	&:hover { opacity: 0.72; }
	&:focus-visible { outline: 2px solid var(--wr-gold); outline-offset: 4px; }
}

/* ── 제목 길이 단계 (모든 골격 공통) ─────────────────────────────────────
   제목은 최대 120자까지 들어온다(편집 화면). 큰 활자를 쓰는 골격일수록 긴 제목이 지면을
   넘기거나(스위스 인쇄 21자 + 여덟 건에서 11mm), 아래끝이 고정된 제목이 위로 자라 상호를
   덮거나(색면 인쇄 100자에서 지면 위 95px), 절대 위치 칸을 넘어 사진 위에 얹힌다(사진 격자
   390px). 문장은 자르지 않는다. 대신 **모든 골격의 제목 px 가 이 배율을 곱한다** —
   골격마다 단계별 px 를 따로 적으면 골격 하나 더할 때마다 빠진다(Codex 리뷰가 그렇게 빠진
   색면·항공·격자를 잡았다). 단계는 13·25·45자이고, 스위스 인쇄 70px 가 한 줄 여섯 자라는
   데서 나왔다. 좁은 화면은 바탕 활자가 이미 26px 라 덜 낮춘다. */
.wr-sheet--cover.is-long { --wr-tscale: 0.8; }
.wr-sheet--cover.is-xlong { --wr-tscale: 0.64; }
.wr-sheet--cover.is-xxlong { --wr-tscale: 0.5; }
/* 스위스의 120자·여덟 건은 제목 아홉 줄 아래 고지 띠가 A4 를 20px 넘겨, 표지 띠 안의
   민·형사 경고가 낱장 밖으로 잘렸다. 폭이 넓은 W 120자는 0.45에서도 열 줄이어서 넘친다.
   0.5 에서 경고가 A4 끝을 넘기 시작하는 길이는 W 89~90자, 한글 106~120자 사이다(실측 2026-10-29, 여덟 건).
   그래서 80자 이상에만 건다. 45~79자 제목은 원래 크기(0.5)를 지킨다(titleLenClass). */
.wr-pdf .wr-cover--grid.is-xxxlong { --wr-tscale: 0.4; }
@media (max-width: 859px) {
	.wr-page:not(.wr-pdf) .wr-sheet--cover.is-long { --wr-tscale: 0.88; }
	.wr-page:not(.wr-pdf) .wr-sheet--cover.is-xlong { --wr-tscale: 0.76; }
	.wr-page:not(.wr-pdf) .wr-sheet--cover.is-xxlong { --wr-tscale: 0.62; }
}

/* 일반 고객 URL 도 A4·여백 0 으로 인쇄한다. 하단 공간은 아래의 조각별 padding 으로
   확보한다. 고정 문장을 @page 여백으로 내리면 Chromium 에서 다음 쪽 위에 찍힌다. */
@page wr-web {
	size: A4;
	margin: 0;
}
@media print {
	.wr-print-legal {
		display: block;
		position: fixed;
		left: 14mm;
		right: 14mm;
		bottom: 3mm;
		z-index: 26;
		margin: 0;
		font-size: 7.5px;
		line-height: 1.45;
		font-weight: 600;
		letter-spacing: -0.01em;
		color: var(--wr-legal);
		background: var(--wr-paper);
		pointer-events: none;
	}
	/* 기존 경고 높이를 지켜 조판·쪽수를 보존한다. 문장만 숨겨 쪽마다 두 번 찍히지 않게 한다. */
	.wr-legal,
	.wr-foot-line__legal { visibility: hidden; }
	.wr-cover-legal { display: none; }
	/* 반복 tfoot 은 유지한다. 상호를 빈 경고 자리 위로 옮겨 고정 문장과 겹치지 않게 한다. */
	.wr-flowtab .wr-foot-line__legal { order: 1; }
	/* 낱장 padding 은 중간 조각에서 반복되지 않는다. 표의 각 조각에도 하단 여백을 남겨
	   긴 지도·사진이 고정 경고의 불투명 배경 아래까지 그려지지 않게 한다. */
	.wr-pdf table.wr-flowtab > tfoot > tr > td { padding-bottom: var(--wr-print-reserve); }
	/* A4 표지(?pdf=1)는 원래 조판을 그대로 두고 고정 경고 위를 덮는다. 이 쪽의 경고는 표지 띠 안의
	   원래 문장이 맡는다(일곱 골격 모두 지면 하단 271~291mm, 실측 2026-10-29).
	   표지 하단 12mm 를 재단하던 방식은 세 가지를 잃었다. 항공 판 왼쪽 아래의 네이버 지도 로고(출처 표기),
	   사진 격자 사진들의 아랫단, 넓은 글자 제목에서 스위스 띠의 끝줄(W 42자에서 1.3mm)이다.
	   판을 올리거나 줄여 맞추면 번호·패널이 도판과 어긋난다. 판을 12mm 올리면 번호가 머리 캡션과 겹칠 수
	   있다(지도 서버가 위쪽 130px 를 비워 두는 이유, reportMap.util.js 의 PAD_TOP).
	   워터마크는 표지보다 위에 둬야 이 쪽에도 같은 자리에 찍힌다. 가려진 고정 경고는 PDF 글자층에는
	   남는다. 고정 요소는 쪽마다 끌 수 없다. */
	.wr-pdf .wr-sheet--cover { z-index: 27; }
	.wr-pdf .wr-sheet--cover .wr-legal { visibility: visible; }
	/* 사진을 못 받아 도판 자리가 접히면(갤러리·스위스) 남는 높이를 받을 칸이 없어, 띠와 경고가 지면 위쪽
	   (갤러리 86mm, 스위스 186mm)에 선다. 고정 경고는 표지 아래에 가려지므로 그때는 1쪽 하단이 빈다.
	   그 경우에만 띠를 지면 바닥에 붙인다. 사진이 있으면 도판 칸이 남는 높이를 먼저 가져가 바뀌는 것이 없다. */
	.wr-pdf .wr-cover__single.is-off ~ .wr-cover__band,
	.wr-pdf .wr-cover__band2.is-off ~ .wr-cover__band { margin-top: auto; }
	.wr-pdf .wr-wm { z-index: 28; }
	/* 높이가 내용에 따라 달라지는 웹 표지는 활자를 자르지 않고 장식 도판만 재단한다.
	   항공 판은 자르지 않는다. 지도 로고가 판 아래 끝에 있다. 판 높이와 1쪽 맞춤은 아래 `.wr-page` 블록의
	   좁은 골격 인쇄 규칙이 맡고, 쪽 하단의 경고 자리는 그 블록의 하단 여백이 쪽마다 비워 둔다. */
	.wr-page:not(.wr-pdf) .wr-cover__tex,
	.wr-page:not(.wr-pdf) .wr-cover__mosaic { clip-path: inset(0 0 var(--wr-print-reserve)); }
	/* 뒤표지의 경고 상자는 그대로 둔다(끝 쪽에 크게, 2026-10-26 대표 요청). 하단 고정 경고도 다른 쪽과
	   같이 찍힌다. 뒤표지는 아래 안쪽 여백이 14mm 라 그 줄 아래에 가려지는 내용이 없다. */
	.wr-page:not(.wr-pdf) {
		page: wr-web;
		/* 웹의 긴 본문도 쪽마다 경고 자리를 비운다. 서버 PDF 는 낱장·tfoot 이 이미 확보한다. */
		padding-bottom: var(--wr-print-reserve);
		box-decoration-break: clone;
		-webkit-box-decoration-break: clone;
		/* 마지막 쪽에서 본문이 먼저 끝나도 종이색은 쪽 끝까지 이어진다. 본문 뒤에 그린다. */
		isolation: isolate;
		&::before {
			content: '';
			position: fixed;
			inset: 0;
			z-index: -1;
			background: var(--wr-paper);
			pointer-events: none;
		}
		.wr-wm span { font-size: 42mm; -webkit-text-stroke-width: 0.45mm; }
		.wr-bar,
		.wr-lb { display: none; }
		/* 스크롤 전 등장 대기 중인 물건도 인쇄에는 전부 실어야 한다. */
		.wr-lot { opacity: 1 !important; transform: none !important; }
		/* 물건 한 건이 쪽보다 길면 카드의 쪽 나눔 금지가 지켜지지 않는다. 그때 위치도 판이 쪽 끝에 걸쳐
		   잘리면서 고정 경고 아래로 들어갔다(실측 2026-10-29: 항공 11쪽, 경고 자리에 지도 14,476px).
		   판만은 나누지 않고 다음 쪽으로 넘긴다. */
		.wr-lot__spot { break-inside: avoid; }
		/* 표지가 한 쪽보다 길어 나뉠 때(스위스, 여덟 건에 긴 제목) Firefox 는 `overflow: hidden` 상자를 스크롤
		   상자로 다루며 나눠, 위 하단 여백 복제를 지키지 않고 목록 끝 줄을 고정 경고 아래에 찍었다(실측
		   2026-10-29: Firefox 158, 1쪽 경고 자리에 621px). `clip` 은 같은 자리를 자르되 스크롤 상자를 만들지
		   않는다. 표지는 flex 상자라 서식 문맥도 그대로다. 지원하지 않는 브라우저는 hidden 으로 남는다. */
		.wr-sheet--cover { overflow: clip; }
		/* WebKit(사파리, 아이폰의 모든 브라우저)은 고정 경고·워터마크를 문서 끝에 한 번만 찍는다(`webkitPrint`).
		   이 엔진에서는 고정 둘을 끄고 흐름 안에 싣는다. 표지 띠·브리핑·끝 마무리의 경고를 보이고, 물건 카드마다
		   끝에 경고와 워터마크를 둔다. WebKitGTK 는 쪽 폭의 1.25배로 배치해 줄여 찍었고(넓은 골격), 7테마 ×
		   기본·여덟 건을 고객·직원·지도 실패로 찍은 42경우 모두 쪽수가 경고 추가 전보다 늘지 않고 모든 쪽에 경고가
		   남았다(실측 2026-10-29). 쪽보다 긴 카드의 첫 쪽, 쪽 끝 7mm 안에서 끝나는 브리핑의 쪽은 경고가 빠질 수 있다. */
		&.wr-wk {
			/* 고정 경고를 쓰지 않으므로 그 자리(쪽 하단 12mm)도 비우지 않는다. 비워 두면 이 엔진은 그 여백을
			   문서 끝에 한 번만 붙여 빈 쪽 하나를 더 만들었다(실측 도록 기본 8쪽째). */
			--wr-print-reserve: 0mm;
			.wr-print-legal,
			.wr-wm:not(.wr-wm--card) { display: none; }
			.wr-legal { visibility: visible; }
			.wr-lot,
			.wr-foot { position: relative; }
			/* 끝 마무리의 웹 하단 여백(64px)이 문서 끝에서 넘쳐 빈 쪽 하나를 만들었다(실측 도록·먹지·항공 기본).
			   종이에서 누를 수 없는 저장 단추도 빼서, 마무리 경고의 끝줄만 다음 쪽으로 넘어가지 않게 한다(실측 필드 기본). */
			.wr-foot { padding-bottom: 0; }
			.wr-foot__actions { display: none; }
			/* 브리핑만 실린 쪽(첫 물건이 다음 쪽으로 넘어간 경우)에도 경고가 남게 브리핑 본문 아래에 싣는다.
			   브리핑 아래 여백(안쪽 18px + 바깥 26px)을 경고 높이(두 줄 6mm + 위 4px)만큼 줄여 흐름 높이가 그대로다.
			   브리핑 위 여백에 절대 위치로 얹었을 때는 브리핑이 쪽 머리에서 시작하면 경고가 앞 쪽 바닥에 찍혔다
			   (실측 도록 기본 1쪽 y=813/842). 브리핑이 표지 쪽에 붙는 지면에서는 그 쪽에 경고가 두 번 찍힌다. */
			.wr-brief {
				padding-bottom: 6px;
				margin-bottom: calc(44px - 6mm - 10px);
			}
			.wr-brief__legal {
				display: block;
				min-height: 6mm;
				margin: 4px 0 0;
				/* 쪽 끝에 걸리면 줄 단위로 갈려 다음 쪽 머리에 한 줄만 찍혔다(실측 직원 미리보기 먹지·항공·필드). */
				break-inside: avoid;
				font-size: 7.5px;
				line-height: 1.45;
			}
			/* 1쪽 경고는 웹 첫 화면용 경고를 표지 맨 위에 띠로 겹쳐 얹는다. 흐름에서 빠지므로 쪽 나눔이 바뀌지 않는다.
			   넓은 골격 표지의 위 여백(68px)보다 낮아 로고를 덮지 않는다. */
			.wr-cover-legal {
				display: block;
				position: absolute;
				top: 0;
				left: 0;
				right: 0;
				z-index: 30;
				padding: 2mm 14mm;
				font-size: 7.5px;
				line-height: 1.45;
			}
			/* 카드 끝 경고는 9mm(글 두 줄 6mm + 위 여백 3mm)를 쓰고, 위치도를 같은 만큼 낮춘다. 카드 높이가
			   그대로라 WebKit 의 쪽 나눔이 바뀌지 않는다. 그냥 붙이면 카드가 한 쪽을 넘어 갈라지고 빈 쪽이
			   생겼다(실측: 도록 기본 7쪽 → 9쪽). 위치도 판 높이는 판 폭의 비율(좁은 골격 16:10, 넓은 골격 4:3)에서
			   9mm 를 뺀다. 더 좁게 찍혀 두 줄을 넘으면 경고를 자르지 않고 카드가 늘어난다(최소 높이만 준다). */
			.wr-lot__legal {
				display: block;
				grid-column: 1 / -1;
				min-height: 6mm;
				margin: 3mm 0 0;
				font-size: 7.5px;
				line-height: 1.45;
			}
			/* 컨테이너 단위를 모르는 사파리(15 이하)는 지도를 줄이지 않는다(카드가 9mm 길어지는 쪽으로 물러난다).
			   높이만 버려지고 비율 해제가 남으면 판 높이가 0 이 되어 지도가 사라진다. */
			@supports (height: 1cqw) {
				.wr-lot__spot { container-type: inline-size; }
				.wr-lot__spotimg { aspect-ratio: auto; height: calc(62.5cqw - 9mm); }
			}
			@media (min-width: 860px) {
				/* 넓은 골격의 카드는 두 행 격자(행 간격 18px)라 경고 행이 그 간격을 한 번 더 받는다. 그만큼
				   위로 당긴다. 당기지 않았을 때 도록·갤러리 기본이 한 쪽씩 늘었다. */
				.wr-lot__legal { margin-top: calc(3mm - 18px); }
				@supports (height: 1cqw) {
					.wr-lot__spotimg { height: calc(75cqw - 9mm); }
				}
				/* 위치도를 못 받은 카드는 줄일 판이 없다. 경고를 사진 단 끝에 두고(템플릿) 사진의 최소 높이를 경고와
				   간격만큼 낮춘다. 사진 칸은 제 원본 비율 높이를 내세우지 않고(기준 크기 0) 단 높이에 맞춰 늘어난다.
				   그러면 카드 높이는 글 단 또는 사진 단의 최소 높이가 정하고, 어느 쪽이든 경고 전보다 길어지지 않는다.
				   넓은 골격의 원래 의도(카드 높이는 오른쪽 단이 정하고 사진이 맞춰 잘린다)와 같다. 위치도가 빠지면
				   오른쪽 단이 짧아져 세로 사진의 원본 높이(611x739 → 568px)가 단을 정했고, 그 위에 경고가 붙어
				   지도 실패 갤러리 여덟 건이 9쪽에서 10쪽이 됐다. 경고를 글 단 아래에 두었을 때는 스위스·모자이크에서
				   소견 끝줄에 붙었다. */
				.wr-lot__media:has(> .wr-lot__legal) > .wr-lot__photo {
					flex: 1 1 0;
					min-height: calc(300px - 12mm - 7px);
				}
			}
			.wr-lot__media > .wr-lot__legal {
				min-height: 12mm;
				margin: 0;
			}
			@media (max-width: 859px) {
				/* 좁은 골격은 사진 비율(3:2)이 높이를 정하므로 상한을 경고와 간격만큼 낮춘다. 사진 단이 종이 끝까지
				   나가는 골격이라 경고 글은 본문 여백 안으로 들인다. */
				.wr-lot__media:has(> .wr-lot__legal) > .wr-lot__photo { max-height: calc(100vw / 1.5 - 12mm - 7px); }
				.wr-lot__media > .wr-lot__legal { padding: 0 #{$wr-pad}; }
			}
			.wr-wm--card {
				display: flex;
				position: absolute;
				z-index: 25;
			}
		}
		/* 브리핑 제목이 쪽 끝에 홀로 남지 않게 본문과 붙인다. */
		.wr-brief__label { break-after: avoid; }
		/* 아래 표지 규칙은 좁은 화면 골격으로 인쇄할 때만 건다. 기본 배율의 A4(794px)가 이 골격이다.
		   인쇄 배율을 92% 아래로 내리면 넓은 골격이 되는데, 거기서는 판과 목록이 절대 위치라 이 규칙들이
		   표지 모양만 바꿨다(실측 2026-10-29 배율 80%: 도록 제목이 60px 올라가고 항공 판이 1쪽을 다 덮었다).
		   넓은 골격의 쪽수는 이 규칙 없이도 경고 추가 전과 같다.
		   직원 미리보기 띠는 한 줄(38.2px, 10.1mm)로 고정한다. 띠가 위에 있으면 10.2mm(반올림 여유 포함)를
		   더 뺀다. 지금 문구는 A4 폭에서 한 줄이지만(어드민 626px, 직원 517px), 문구나 글꼴이 바뀌어 두 줄로
		   접히면 표지가 19px 넘쳐 2쪽 머리에 표지 조각이 남는다. 그래서 줄을 접지 않고 넘치는 글자는
		   말줄임표로 끊는다(미리보기 안내라 고객 문서에는 찍히지 않는다). */
		@media (max-width: 859px) {
			.wr-devbar { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
			/* 항공 표지는 1쪽 안에 다 들어가야 한다. 판을 화면 높이의 8할로 세우면 그 아래 제목·목록이 1쪽 경고
			   자리를 넘어, 목록 줄만 2쪽 머리에 홀로 찍혔다(실측 2026-10-29: 직원 미리보기 기본 53px, 여덟 건·
			   120자 113px 넘침. 여덟 건은 HEAD 에서도 넘쳤다).
			   그래서 표지를 한 쪽에서 경고 자리를 뺀 높이로 두고, 판은 남는 높이만큼만 선다. 판 비율은 지키고 폭이
			   따라 준다(잘림 0). */
			.wr-sheet--cover.wr-cover--aerial:has(> .wr-cover__aerial:not(.is-photo)) {
				height: calc(100vh - var(--wr-print-reserve));
				> .wr-cover__aerial {
					flex: 1 1 0;
					min-height: 0;
					max-height: none;
					width: auto;
					max-width: calc(100% + 2 * #{$wr-pad});
				}
			}
			.wr-devbar ~ .wr-sheet--cover.wr-cover--aerial:has(> .wr-cover__aerial:not(.is-photo)) {
				height: calc(100vh - var(--wr-print-reserve) - 10.2mm);
			}
			/* 지도를 못 받아 대체 사진을 얹은 표지는 판 높이가 사진 비율에서 정해진다. 가로 사진은 1쪽 안에
			   들어가지만 세로 사진은 판이 화면 8할(898px)까지 자라 같은 고아 줄이 생겼다(HEAD 에서도 그랬다).
			   표지 높이에 상한만 두고, 넘칠 때만 사진 칸이 줄어든다. 사진은 `object-fit: cover` 라 위아래가
			   잘릴 뿐이고 번호를 얹지 않으므로 어긋날 것이 없다. 가로 사진 표지는 바뀌지 않는다. */
			.wr-sheet--cover.wr-cover--aerial:has(> .wr-cover__aerial.is-photo) {
				max-height: calc(100vh - var(--wr-print-reserve));
				> .wr-cover__aerial { flex: 0 1 auto; min-height: 0; }
			}
			.wr-devbar ~ .wr-sheet--cover.wr-cover--aerial:has(> .wr-cover__aerial.is-photo) {
				max-height: calc(100vh - var(--wr-print-reserve) - 10.2mm);
			}
			/* 색면 표지는 최소 높이(241.5mm)로 서고 남는 높이는 빈 칸(gap)이 먹는다. 경고 자리 12mm 만큼 1쪽이
			   줄자 그 아래 브리핑 마지막 줄이 2쪽으로 넘어갔고, 첫 물건 카드(쪽 나눔 금지)가 그 뒤에 못 들어가
			   2쪽이 거의 빈 채로 한 쪽이 늘었다(실측 2026-10-29: 고객 화면 기본 11쪽 → 12쪽). 빈 칸만 그만큼 줄인다.
			   직원 미리보기 띠가 있으면 같은 일이 띠 높이만큼 더 일어났다(기본 11쪽 → 12쪽, 여덟 건 17쪽 → 18쪽). */
			.wr-cover--split { min-height: calc(min(92vh, 100vw * 1.15) - var(--wr-print-reserve)); }
			.wr-devbar ~ .wr-cover--split { min-height: calc(min(92vh, 100vw * 1.15) - var(--wr-print-reserve) - 10.2mm); }
			/* 같은 이유로 도록 표지의 마크·제목 사이 빈 칸(104px)도 경고 자리만큼 줄인다. 여덟 건이면 목차가
			   길어 브리핑 끝이 1쪽 경고선을 42px 넘었다(실측: 고객 화면 17쪽 → 18쪽). HEAD 의 1쪽 여유와 같아진다. */
			.wr-cover--mark .wr-cover__gap { min-height: calc(104px - var(--wr-print-reserve)); }
			/* 물건 카드는 쪽 나눔 금지라, 쪽이 경고 자리만큼 줄자 예전 한 쪽(여백 0 기준 1,123px)에 들어가던 카드 가운데
			   1,078~1,123px 짜리가 한 줄 넘쳐 꼬리 쪽이 하나 생겼다(실측 2026-10-29: 위치도가 빠진 여덟 건
			   리포트에서 다섯 테마가 한 쪽씩 늘었다). 대표 사진 칸을 경고 자리만큼 낮춰 모든 카드가 정확히 그만큼
			   짧아지게 한다. 그러면 예전 한 쪽에 들어가던 카드는 지금도 들어간다. 사진은 3:2 에서 약 1.64:1 로
			   위아래가 조금 잘린다(번호를 얹지 않는 사진이라 어긋날 것이 없다). 사진이 없을 때의 자리 표시도 이 칸
			   안에 선다. */
			.wr-lot__photo { max-height: calc(100vw / 1.5 - var(--wr-print-reserve)); }
		}
	}
}
</style>
